/**
 * DOSIA Model Registry — single source of truth for model capabilities.
 *
 * Why this exists:
 *   The SDK reports display text (displayName/description) accurately, but
 *   capability metadata (context window, vision, tool-use, 1M-context
 *   suffix requirement) is Claude-family-specific. For non-Claude models
 *   routed through Anthropic-compatible providers, the SDK cannot auto-
 *   detect these fields, leading to wrong context-window displays and
 *   missing [1m] suffixes (or worse — sending [1m] to a provider that
 *   doesn't understand it).
 *
 *   This registry is DOSIA-maintained. Every time a new model is added to
 *   a built-in provider, its entry must be added here. SDK-reported data
 *   is used for displayText only; capability fields always come from here.
 *
 * Convention:
 *   - Key = DOSIA standardName (normalizeSdkValue output: "opus", "sonnet",
 *     "haiku", or the raw model ID like "deepseek-v4-pro").
 *   - priority = sort weight in the picker (higher = shown first).
 */
import { isWebSearchProviderId } from "./web-search-providers.js";
import { isAudioTranscribeProviderId } from "./audio-transcribe-providers.js";
import { isGatewayCapabilityProtocol } from "./gateway-capability-protocols.js";
import { parseBrainPriorityList } from "./brain-priority.js";
import { sortByThinkingLevel, THINKING_LEVELS } from "./types/index.js";
import { normalizeSdkValue } from "./utils/index.js";
import { toBundle } from "./bytespike-bundle-wire.js";
import { dropNoCredentialRows, isNoCredentialRow } from "./catalog-row-supply.js";
import { canonicalizeGrokMediaModelId, getGrokMediaModelContract } from "./media-generation-contract.js";
import { piNativeWireRow } from "./pi-wire.js";
export const DOSIA_MODEL_REGISTRY = {
    // ════════════════════════════════════════════════════════════════
    // Claude family — `supports1m` is the cold-start FALLBACK for the 1M
    // capability fact (SDK supported_models / manifest win at runtime). opus &
    // sonnet have a 1M variant → true; haiku does NOT (sending haiku[1m] → 400).
    // contextWindow here is just a floor for gateway chains with no catalog row; a
    // personal-subscription session never reads it (context-window-resolver shows
    // the CLI's own per-account rawMaxTokens).
    // ════════════════════════════════════════════════════════════════
    "fable": {
        contextWindow: 200_000,
        supportsVision: true,
        supportsTools: true,
        supports1m: true, // SDK 0.3.170 reports claude-fable-5[1m] → 1M variant exists
        priority: 11,
    },
    "opus": {
        contextWindow: 200_000,
        supportsVision: true,
        supportsTools: true,
        supports1m: true,
        priority: 10,
    },
    "sonnet": {
        contextWindow: 200_000,
        supportsVision: true,
        supportsTools: true,
        supports1m: true,
        priority: 8,
    },
    "haiku": {
        contextWindow: 200_000,
        supportsVision: true,
        supportsTools: true,
        supports1m: false,
        priority: 5,
    },
    // SDK-native plan/execute switching alias — Opus during plan mode, Sonnet
    // for execution. Hidden from employee picker by default; admin opens via
    // managedSettings.experimentalAliases=true. PR-2 admin policy center
    // (2026-05-29). See https://code.claude.com/docs/en/model-config#opusplan-model-setting
    "opusplan": {
        contextWindow: 200_000,
        supportsVision: true,
        supportsTools: true,
        supports1m: true,
        priority: 9,
        tagline: "Plan 用 Opus · Execute 切 Sonnet · 长会话省钱",
        experimental: true,
    },
    // ════════════════════════════════════════════════════════════════
    // DeepSeek family
    // ════════════════════════════════════════════════════════════════
    "deepseek-v4-pro": {
        contextWindow: 1_048_576,
        supportsTools: true,
        priority: 7,
        tagline: "DeepSeek V4 · 1M 上下文",
    },
    "deepseek-chat": {
        contextWindow: 1_048_576,
        supportsTools: true,
        priority: 6,
        tagline: "DeepSeek Chat · 1M 上下文",
    },
    "deepseek-r1-0528": {
        contextWindow: 1_048_576,
        supportsTools: true,
        priority: 5,
        tagline: "DeepSeek R1 · 推理 · 1M 上下文",
    },
    // ════════════════════════════════════════════════════════════════
    // Qwen family
    // ════════════════════════════════════════════════════════════════
    "qwen-plus": {
        contextWindow: 131_072,
        supportsTools: true,
    },
    "qwen-turbo": {
        contextWindow: 131_072,
        supportsTools: true,
    },
    "qwen-max": {
        contextWindow: 32_768,
        supportsTools: true,
    },
    "qwen-long": {
        contextWindow: 10_000_000,
        supportsTools: true,
        priority: 6,
        tagline: "通义千问 Long · 10M 上下文",
    },
    // ════════════════════════════════════════════════════════════════
    // MiniMax family
    // ════════════════════════════════════════════════════════════════
    "MiniMax-Text-01": {
        contextWindow: 1_048_576,
        supportsTools: true,
        priority: 6,
    },
    "abab6.5s-chat": {
        contextWindow: 245_760,
        supportsTools: true,
    },
    // ════════════════════════════════════════════════════════════════
    // Volces ARK family (火山方舟)
    // ════════════════════════════════════════════════════════════════
    "ark-code-latest": {
        contextWindow: 262_144,
        supportsVision: true,
        supportsTools: true,
        priority: 7,
        tagline: "方舟 Code · 多模态 · 256K 上下文",
    },
};
// ──────────────────────────────────────────────────────────────────────
// Family / provider classification helpers.
//
// Used by agent-session (SDK env vs options.model routing), workspace-config
// ([1m] suffix baking), and use-models (cold-cache default 1M for Claude
// family). Centralised here so the three sites can't drift apart.
// ──────────────────────────────────────────────────────────────────────
/** Bare aliases the SDK routes to Claude — hot path (per-message model checks,
 *  picker renders), so a module-level Set instead of a per-call array. */
const CLAUDE_BARE_ALIASES = new Set([
    "opus", "sonnet", "haiku", "opusplan", "default", "fable",
]);
/**
 * Claude family detection — SDK resolves these to real Claude models.
 *
 * Accepts:
 *  - "claude-*" full model IDs (claude-opus-4-7, claude-fable-5, claude-haiku-4-5-20251001, ...)
 *  - Bare aliases SDK routes to Claude (opus / sonnet / haiku / fable / opusplan / default)
 *  - Any of the above with bracket suffixes ([1m], [v2], …) — stripped before match
 *
 * Returns false for non-Claude ids (deepseek-*, kimi-*, qwen-*, doubao-*, ...).
 */
export function isClaudeFamilyModel(model) {
    if (!model)
        return false;
    const bare = model.replace(/\[[^\]]*\]/g, "");
    if (/^claude-/.test(bare))
        return true;
    return CLAUDE_BARE_ALIASES.has(bare);
}
/**
 * 「这个 id 属于 ChatGPT 个人订阅能服务的家族吗」—— `isClaudeFamilyModel` 的对称件。
 *
 * **只用于展示层判定**（订阅徽章 / 灰显），不是权威成员判定：权威的是 pi-ai 的
 * `OPENAI_CODEX_MODELS` 表（`buildCodexPiModel` 查表，不在表里当场报「不提供模型 X」）。
 * 展示层拿不到那张表（ESM-only 主进程依赖，只经 IPC 出一次列表），所以这里用家族前缀。
 *
 * 精度与 Claude 侧**完全对等**：`isClaudeFamilyModel` 同样认不出「这条 claude-* 到底走
 * 个人会员还是网关」，两边都靠「组 + 家族」两个条件把误挂压到同一水平（2026-07-02 那次
 * 误挂修的就是缺家族条件）。别在这里追求更高精度而两侧不对称。
 */
export function isChatGPTFamilyModel(model) {
    if (!model)
        return false;
    return /^gpt-/i.test(model.replace(/\[[^\]]*\]/g, ""));
}
/**
 * 「这个 id 属于 Grok 个人订阅能服务的家族吗」—— `isChatGPTFamilyModel` 的对称件。
 *
 * **只用于展示层判定**（订阅徽章 / 灰显），不是权威成员判定：权威的是本仓
 * `XAI_OAUTH_MODELS` 表（`buildXaiOAuthPiModel` 查表，不在表里当场报「不提供模型 X」）。
 * 精度与 Claude / ChatGPT 侧对等：家族前缀 + 剥 suffix tag。
 */
export function isGrokFamilyModel(model) {
    if (!model)
        return false;
    return /^grok-/i.test(model.replace(/\[[^\]]*\]/g, ""));
}
/** Bare Claude family alias → the family token that identifies its concrete
 *  catalog ids (claude-<family>-<version>). `opusplan`/`default` fold to opus. */
const CLAUDE_ALIAS_FAMILY = {
    opus: "opus",
    opusplan: "opus",
    default: "opus",
    sonnet: "sonnet",
    haiku: "haiku",
    fable: "fable",
};
/** Descending version compare on two concrete Claude ids of the SAME family
 *  (newest first): claude-sonnet-5 > claude-sonnet-4-6; claude-opus-5 >
 *  claude-opus-4-8. Extracts the numeric run after the family token; missing
 *  segments count as 0. */
function compareClaudeVersionDesc(aId, bId, family) {
    const nums = (id) => {
        const m = new RegExp(`${family}[^0-9]*([0-9]+(?:-[0-9]+)*)`).exec(id);
        return m ? m[1].split("-").map(Number) : [];
    };
    const a = nums(aId);
    const b = nums(bId);
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
        const d = (b[i] ?? 0) - (a[i] ?? 0);
        if (d !== 0)
            return d;
    }
    return 0;
}
// ──────────────────────────────────────────────────────────────────────
// Model identity guard (2026-07-18) — 防渠道偷换单一判定源。
//
// Compares the model DOSIA asked for (`expected`) against the model the request
// was actually billed to (`actual`, from OTel attrs.model / model_call
// result.modelUsage). Channel-swap has双实证 (packy/Kiro 中转吞换、§87
// availableModels 静默替换) — the "实际模型" is observable on two通道 but was never
// compared. This is that comparison, ONE pure function shared by both check
// points (OTel collector · model_call), never re-implemented.
//
// 防误报是第一设计约束（宁漏报不误报）。所以判定分三级，只有 mismatch 级才打扰：
//   • "match"    — 同一模型（含 [1m] 等 suffix 差、别名↔具体版本、legitSet 命中、
//                  同族版本前缀）。actual 为空/合成/unknown 也归 match（不判）。
//   • "variant"  — 同厂牌不同版本/档位（claude-opus-4-7 vs -4-8；opus 别名 vs
//                  claude-sonnet；deepseek-v4-pro vs deepseek-chat）。只落库、零打扰
//                  —— Auto 改写 / SDK fallback / compaction 等家族内合法换模的降噪兜底。
//   • "mismatch" — 家族/厂牌级不同（deepseek-* 请求却由 claude-* 生成）。真·疑似渠道
//                  替换，消费端发一次 amber info（非 bug 不红色，红线 #5）。
// ──────────────────────────────────────────────────────────────────────
/** actual 无从判定的哨兵值（合成消息/占位/未知）→ 一律 match，绝不误报。对齐
 *  usage-scanner isExcludedUsageModel 的 `<synthetic>` 语义 + CLI 的 unknown 回落。 */
const UNJUDGEABLE_ACTUAL_MODELS = new Set([
    "<synthetic>", "synthetic", "unknown", "<unknown>", "none",
]);
/** 归一：strip 所有 `[...]` suffix（[1m] 等）+ trim + lowercase。判定的统一底座。 */
function normModelId(m) {
    return m ? stripModelSuffixTag(m).trim().toLowerCase() : "";
}
/** 厂牌/家族前缀（vendor token）：Claude 家族（含裸别名）→ "claude"；其余取首个 `-`
 *  段（deepseek-v4-pro→"deepseek"、gpt-5.5→"gpt"、kimi-k2→"kimi"、glm-4.6→"glm"、
 *  qwen-plus→"qwen"、MiniMax-Text-01→"minimax"）。空 → ""。同 vendor → 至多 variant，
 *  故 vendor 相同永不落 mismatch（家族内换模不打扰）。 */
function modelVendorToken(bare) {
    if (!bare)
        return "";
    if (isClaudeFamilyModel(bare))
        return "claude";
    const dash = bare.indexOf("-");
    return dash > 0 ? bare.slice(0, dash) : bare;
}
/** `alias` 是 Claude 裸别名(opus/sonnet/haiku/fable/opusplan/default) 且 `concrete` 是该
 *  别名家族的具体 claude id（claude-<family>[-...]）→ true。归一后按 `-` 段边界匹配，杜绝
 *  别名误配 claude-opusplan 这类子串命中。防误报关键：主会话 expected 常是裸别名（opus），
 *  实际计费是具体 claude-<family>-<ver> id，二者是同一模型必须判 match。 */
function claudeAliasMatchesConcrete(alias, concrete) {
    const family = CLAUDE_ALIAS_FAMILY[alias];
    if (!family)
        return false;
    return concrete === `claude-${family}` || concrete.startsWith(`claude-${family}-`);
}
/**
 * 模型身份比对（防渠道偷换）。返回 "match"（含合法变体）| "variant"（同家族版本/后缀差异，
 * 只记录不打扰）| "mismatch"（家族级不同，需提示）。纯函数、确定性、无副作用。
 *
 * @param expected DOSIA 请求的模型（裸别名 / 具体 id / 带 [1m]）。空 → "match"（无期望不判）。
 * @param actual   实际计费/生成的模型（OTel attrs.model 或 model_call result.modelUsage）。
 *                 空 / "<synthetic>" / unknown → "match"（不可判，绝不误报）。
 * @param legitSet 本次合法的额外具体 id 集（roleMapping 三档值 + 会话内 SDK 合法 fallback）。
 *                 actual 命中其中任一（精确或别名解析）→ "match"，杜绝合法换档/换模误报。
 */
export function classifyModelIdentity(expected, actual, legitSet) {
    const ab = normModelId(actual);
    // 1) actual 无从判定（空/合成/unknown）→ 不判，match。宁漏报不误报。
    if (!ab || UNJUDGEABLE_ACTUAL_MODELS.has(ab))
        return "match";
    const eb = normModelId(expected);
    if (!eb)
        return "match"; // 无期望 → 不判
    // 2) 归一后全等 → match（含 [1m] 等 suffix 差）。
    if (eb === ab)
        return "match";
    // 3) legitSet 命中（精确 or 别名↔具体解析）→ match。合法档位/会话内 fallback 兜底。
    if (legitSet) {
        for (const raw of legitSet) {
            const lb = normModelId(raw);
            if (!lb)
                continue;
            if (lb === ab)
                return "match";
            if (claudeAliasMatchesConcrete(lb, ab) || claudeAliasMatchesConcrete(ab, lb))
                return "match";
        }
    }
    // 4) 别名 ↔ 具体版本（双向）：opus ↔ concrete claude-opus version。
    if (claudeAliasMatchesConcrete(eb, ab) || claudeAliasMatchesConcrete(ab, eb))
        return "match";
    // 5) 段边界前缀（版本尾差）：claude-sonnet-5 ↔ claude-sonnet-5-20260203。
    if (ab.startsWith(`${eb}-`) || eb.startsWith(`${ab}-`))
        return "match";
    // 6) 同厂牌/家族（vendor token 相同）→ variant（只落库不打扰）。
    if (modelVendorToken(eb) === modelVendorToken(ab))
        return "variant";
    // 7) 厂牌级不同 → mismatch（疑似渠道替换，消费端 amber）。
    return "mismatch";
}
/**
 * Resolve a model id-or-alias to its catalog entry for DISPLAY (picker chip).
 *
 * Direct concrete-id match wins. A bare Claude family alias (opus/sonnet/haiku/
 * fable, plus opusplan/default → opus) that isn't in the catalog as-is resolves
 * to the NEWEST concrete Claude entry of that family — so the chip shows a
 * friendly displayName + correct vendor icon even when no roleMapping maps the
 * alias (e.g. the claude-oauth group carries only `defaultModel`, no roleMapping,
 * so the picker used to render the raw lowercase alias + the neutral fallback
 * logo). Bracket suffixes ([1m], …) are stripped before matching. Non-Claude /
 * unknown ids that miss a direct match return undefined (caller falls back to id).
 *
 * Generic over the row shape so the model-profile table (N-128,
 * `findModelProfile` in model-profile-wire.ts) resolves ids and aliases with
 * this exact rule instead of a second copy of the "newest of the family" sort.
 */
export function resolveCatalogEntry(idOrAlias, catalog) {
    if (!idOrAlias)
        return undefined;
    const bare = stripModelSuffixTag(idOrAlias);
    const direct = catalog.find((e) => e.modelId === bare);
    if (direct)
        return direct;
    const family = CLAUDE_ALIAS_FAMILY[bare];
    if (!family)
        return undefined;
    const candidates = catalog.filter((e) => /^claude-/.test(e.modelId) && e.modelId.includes(family));
    if (candidates.length === 0)
        return undefined;
    return [...candidates].sort((a, b) => compareClaudeVersionDesc(a.modelId, b.modelId, family))[0];
}
// ──────────────────────────────────────────────────────────────────────
// Thinking-surface capability FACTS (2026-06-29). Source-independent facts
// about a model, used by agent-session.buildQueryOptions to pick the right
// `thinking` shape and decide whether `effort` may be sent — sending the wrong
// one is a hard 400 (verified against platform.claude.com migration guide):
//
//   • Adaptive thinking (`thinking:{type:"adaptive"}`) + budget_tokens removed:
//       Opus 4.6/4.7/4.8 + Sonnet 4.6 (and Fable 5), every later Opus/Sonnet,
//       and Haiku 5.5. On these, the legacy `{type:"enabled",budgetTokens}` is
//       deprecated (4.6) or rejected with a 400 (4.7/4.8, Haiku 5.5) — convert it
//       to adaptive. Haiku 4.5 / Sonnet 4.5 / older do NOT support adaptive and
//       keep the legacy enabled+budget passthrough.
//   • effort (`options.effort`): Opus 4.5/4.6/4.7/4.8 + Sonnet 4.6 (and Fable 5),
//       every later Opus/Sonnet, and Haiku 5.5. Errors on Sonnet 4.5 and Haiku 4.5
//       — never send it there. (Opus 4.5 supports effort but NOT adaptive — the
//       one model where the two sets differ, hence two predicates rather than one.)
//
// Haiku follows the same version rule as Sonnet (4.6+): no Haiku 4.6–5.0 exists,
// so the rule splits exactly at Haiku 4.5 (false) / Haiku 5.5 (true). Haiku 5.5
// facts: platform.claude.com models/haiku-5-5 overview + migration guide +
// build-with-claude/effort (2026-10-07 release; adaptive on by default, five
// effort levels, enabled+budget_tokens → 400); SDK 0.3.293 roster entry carries
// "effort" / "adaptive_thinking" capabilities.
//
// Matching is bare-model-name + version-token based (reusing isClaudeFamilyModel
// + the [1m]-strip convention), never fragile group-name substrings. A Claude
// bare alias with no version (opus/sonnet/haiku/opusplan/default/fable) resolves
// to a CURRENT model, which supports both — so it returns true (since SDK 0.3.293
// the `haiku` alias resolves to claude-haiku-5-5 via latest_per_family). Unknown /
// non-Claude → false, the safe default: keep the legacy enabled+budget passthrough
// older models accept and don't send effort.
// ──────────────────────────────────────────────────────────────────────
/** Parse the `major.minor` version token from a Claude model id, if present.
 *  Bare aliases and single-number ids (claude-fable-5) have none → undefined. */
function claudeVersion(bare) {
    const m = /-(\d+)-(\d+)(?:-|$)/.exec(bare);
    return m ? { major: Number(m[1]), minor: Number(m[2]) } : undefined;
}
/**
 * Does this model support adaptive thinking (`thinking:{type:"adaptive"}`)?
 * True for Opus/Sonnet 4.6+, Haiku 5.5+ (and Fable 5 / bare current aliases).
 * False for Haiku ≤4.5, Opus/Sonnet ≤4.5, unknown, and non-Claude models — those
 * keep the legacy `{type:"enabled",budgetTokens}` thinking shape.
 */
export function supportsAdaptiveThinking(model) {
    if (!model || !isClaudeFamilyModel(model))
        return false;
    const bare = model.replace(/\[[^\]]*\]/g, "").toLowerCase();
    const v = claudeVersion(bare);
    if (!v)
        return true; // bare alias / fable → current model supports adaptive
    return v.major > 4 || (v.major === 4 && v.minor >= 6); // Opus, Sonnet & Haiku: 4.6+
}
/**
 * May `options.effort` be sent for this model? True for Opus 4.5+, Sonnet 4.6+,
 * Haiku 5.5+ (and Fable 5 / bare current aliases). False for Haiku 4.5, Sonnet
 * 4.5, older, unknown, and non-Claude — effort errors there. Superset of
 * supportsAdaptiveThinking by exactly Opus 4.5 (effort-capable, adaptive-incapable).
 */
export function supportsEffort(model) {
    if (!model || !isClaudeFamilyModel(model))
        return false;
    const bare = model.replace(/\[[^\]]*\]/g, "").toLowerCase();
    const v = claudeVersion(bare);
    if (!v)
        return true; // bare alias / fable → current model supports effort
    if (/(?:^|-)opus/.test(bare))
        return v.major > 4 || (v.major === 4 && v.minor >= 5);
    return v.major > 4 || (v.major === 4 && v.minor >= 6); // Sonnet, Haiku & others: 4.6+
}
// ──────────────────────────────────────────────────────────────────────
// Per-model brain-config capability resolver (D3, 2026-06).
//
// Single source of truth for which thinking / effort controls the 主脑 settings
// panel renders. Replaces the old group-name substring hardcode (claude=全开 /
// gemini=全灰 / deepseek=开 / 其余=禁) that ignored each model's REAL capability —
// the root cause of GPT/GLM/Qwen being hidden, Gemini judged backwards, 豆包 given
// an effort it can't take, and Haiku wrongly offered effort.
//
//   Claude family     → capability is a version-intrinsic FACT (SDK/registry is the
//                       authority, not the catalog): supportsAdaptiveThinking /
//                       supportsEffort. All Claude support some thinking shape; adaptive
//                       only on the adaptive-capable set; no catalog effort levels → full set.
//   Non-Claude        → driven by gateway catalog capability fields (G1): supportsThinking
//                       → enabled/disabled ONLY (no adaptive concept — real-machine 证:
//                       Anthropic thinking:{enabled|disabled} all take effect); supportsEffort
//                       + supportedEffortLevels for the level list.
//   Unknown / no data → everything hidden (safe — never offer an unsupported control;
//                       e.g. catalog pre-G1 has supportsThinking=false → control stays hidden).
// ──────────────────────────────────────────────────────────────────────
const ALL_EFFORT_LEVELS = ["low", "medium", "high", "max"];
/** Claude 无档位描述时的回落集（N-130 E2：补上 xhigh——SDK 0.3.212 起 xhigh 是原生档，不支持的模型自动降到 high）。
 *  只用于 Claude 家族的能力回落；平台治理的保守兜底与非 Claude 回落仍是 {@link ALL_EFFORT_LEVELS}（不推断 xhigh）。 */
const CLAUDE_FALLBACK_EFFORT_LEVELS = ["low", "medium", "high", "xhigh", "max"];
/**
 * Resolve the brain-config control capability for a SINGLE concrete model id.
 *
 * STRUCTURED-FIRST (M2, 2026-06-29): when the catalog carries a structured
 * `thinkingParam` descriptor (gateway M1), it is authoritative for EVERY model —
 * Claude included (M1 fills Claude facts accurately: opus-4-8 levels incl xhigh).
 * The registry version predicates (supportsAdaptiveThinking / supportsEffort) then
 * serve only to decide the adaptive mode (a Claude version-intrinsic fact the
 * catalog can't express as a level) and as the catalog-absent SEED/fallback. When
 * `thinkingParam` is absent (pre-M1 not deployed) the resolver falls back to the
 * boolean trio + registry predicates — zero regression. Pure & deterministic.
 */
export function resolveBrainModelCapability(model, catalog) {
    const isClaude = isClaudeFamilyModel(model);
    const tp = catalog?.thinkingParam;
    // ── Structured path (M1 present) — authoritative for ALL models. ──
    if (tp) {
        const supported = tp.supported === true;
        const levels = tp.levels?.length ? [...tp.levels] : [];
        const wire = tp.wire;
        // effort control = level-based (discrete levels) OR explicit reasoning_effort wire.
        const effort = supported && (levels.length > 0 || wire === "reasoning_effort");
        // adaptive is a Claude version-intrinsic fact — the registry predicate seeds it
        // (the catalog has no "adaptive level"). Only Claude adaptive-capable models get it.
        const adaptive = supported && isClaude && supportsAdaptiveThinking(model);
        // canDisable defaults to true (most models can turn thinking off). When false
        // (e.g. Gemini 3 Flash) the "disabled" mode is dropped.
        const canDisable = tp.canDisable !== false;
        const baseModes = canDisable ? ["enabled", "disabled"] : ["enabled"];
        // 订阅行没有 catalog。它的事实是 supportsThinking:false + 档位列表 + canDisable:false
        // 且没有 wire：只渲染档位，不渲染 Anthropic thinking 模式。Claude 与其余结构化描述符
        // 仍按 tp.supported（M2），不走这条。
        const subscriptionEffortOnly = !isClaude
            && catalog?.supportsThinking === false
            && catalog?.supportsEffort === true
            && tp.canDisable === false
            && wire === undefined
            && levels.length > 0;
        const thinking = supported && !subscriptionEffortOnly;
        return {
            thinking,
            thinkingModes: thinking ? (adaptive ? ["adaptive", ...baseModes] : baseModes) : [],
            canDisable,
            effort,
            // Canonical presentation order (红线#9 单源 sortByThinkingLevel)：catalog 声明顺序
            // 可能把 xhigh 排在 max 之后，呈现前统一排 minimal<low<medium<high<xhigh<max。
            effortLevels: levels.length ? sortByThinkingLevel(levels) : (isClaude ? [...CLAUDE_FALLBACK_EFFORT_LEVELS] : []),
            budget: true,
            toolSearch: isClaude,
            effortMax: isClaude,
        };
    }
    // ── Fallback path (pre-M1) — boolean trio + registry version predicates. ──
    if (isClaude) {
        const adaptive = supportsAdaptiveThinking(model);
        return {
            thinking: true, // every Claude model supports some thinking shape
            thinkingModes: adaptive ? ["adaptive", "enabled", "disabled"] : ["enabled", "disabled"],
            canDisable: true,
            effort: supportsEffort(model),
            effortLevels: [...CLAUDE_FALLBACK_EFFORT_LEVELS],
            budget: true,
            toolSearch: true,
            effortMax: true,
        };
    }
    // Non-Claude — gateway catalog booleans are the only authority (no adaptive concept).
    // 呈现前按 canonical 序排（声明顺序无关，红线#9 单源）；ALL_EFFORT_LEVELS 兜底本就 canonical。
    const levels = catalog?.supportedEffortLevels?.length
        ? sortByThinkingLevel([...catalog.supportedEffortLevels])
        : [...ALL_EFFORT_LEVELS];
    return {
        thinking: catalog?.supportsThinking === true,
        thinkingModes: ["enabled", "disabled"],
        canDisable: true,
        effort: catalog?.supportsEffort === true,
        effortLevels: levels,
        budget: true,
        toolSearch: false,
        effortMax: false,
    };
}
// ──────────────────────────────────────────────────────────────────────
// 主脑(brain) vs 工具(tool) 分类 — 单一真值源（管理面 + 员工页 + catalog 共用）。
//
// 为何不能只看 protocol === "anthropic_messages"：ByteSpike manifest 几乎所有组
// 都挂 anthropic_messages(passthrough)，连 gemini-image/video 这种图片/视频生成
// 工具组也有 → 用 protocol 判别会把工具模型漏进「主脑」。组名约定（ByteSpike:
// *-default = 主脑组 / *-image|*-video = 工具组）只能做兼容兜底；模型已声明 image_gen
// 能力时必须走语义硬隔离，否则 gpt-image-2 映射进 openai-default 后会再次漏入主脑。
// ──────────────────────────────────────────────────────────────────────
/** 图片/视频生成工具组（按 ByteSpike 组名约定）。 */
export function isToolGroup(groupName) {
    return !!groupName && /(?:^|[-_])(image|video)(?:[-_]|$)/i.test(groupName);
}
const IMAGE_GENERATION_CAPABILITY_TAGS = new Set(["image_gen", "image_generation"]);
/** 网关 catalog 行上「该模型可走 Anthropic 驱动」的能力标签（单一来源）。 */
export const CAPABILITY_TAG_DRIVER_ANTHROPIC = "driver_anthropic";
/**
 * 该 catalog 条目是否声明「生图模型」。优先认 catalog capability tag；`gpt-image-*`
 * 是网关旧清单尚未下发 tag 时的兼容兜底。调用方若要按整模型（跨协议行）隔离，应先按
 * `stripModelSuffixTag(modelId)` 汇总 base id，不能只判断当前这一行。
 */
export function isImageGenCatalogEntry(entry) {
    if (isImageGenModelId(entry.modelId))
        return true;
    return entry.capabilityTags?.some((tag) => (typeof tag === "string" && IMAGE_GENERATION_CAPABILITY_TAGS.has(tag.trim().toLowerCase()))) === true;
}
/** 该 catalog 条目是否"主脑模型"：走 anthropic_messages 协议、非图片/视频工具组，
 *  且不是生图能力行。image_gen 是不可被平台/组织/角色白名单重新放开的硬语义边界。
 *  用于把模型分到 主脑 tab vs 工具 tab，以及管理面是否显示 role mapping 默认参数。 */
export function isBrainCatalogEntry(entry) {
    return entry.protocol === "anthropic_messages"
        && !isToolGroup(entry.chosenGroup?.name)
        && !isImageGenCatalogEntry(entry);
}
// ──────────────────────────────────────────────────────────────────────
// 主脑候选池（brain pool）—— 一个模型能不能进 Claude 引擎，只由网关给它的**原生协议**决定。
//
// 产品原则（2026-10-08 产品经理拍板，原文）：「不应该存在原生家族名单，这是历史遗留问题；应该是
// 网关下发哪些模型支持哪些原生协议，筛选后维护一个双腿协议的模型清单。」一个模型能进哪条引擎只由
// 它经网关可用的原生协议决定，与现状有无请求无关。
//
// 判据 = 该行是 `anthropic_messages` 协议行，且网关明确下发 `translated === false`（上游原生、
// 不经网关翻译）。网关对每条协议行都下发这个布尔、刻意不 omitempty（llm-gateway
// `backend/internal/service/dosia_org.go` 的 bundle 行与 `model_catalog_service.go` 的公开清单
// 协议行同一字段），所以：
//  - `translated === true`（gpt / grok / gemini 的 anthropic 行是网关翻译出来的）→ 不进池；
//  - `translated` 缺席 = 旧网关或丢字段故障，**判不了就不进池**（fail-closed）——缺席当原生，
//    会把翻译腿悄悄放进 Claude 引擎，那正是这个字段要排除的方向。
// Claude（anthropic）家族自己也走同一判据：它们的 anthropic 行是原生的，网关同样下发 false。
//
// 此前这里是一份家族名单（family_slug / id 前缀白名单），新家族上架要改代码发版，名单外的家族要
// 另开「主脑家族试点」按组织打补丁；两者都已删除（2026-10-08）。
//
// GPT 等经网关翻译的模型仍是**可指定模型**（agent 指定 / model_call / cron 程序化通路完整保留，
// 由干活模型池 `modelCallModels` 单独闸），只是不出现在「选主脑」这个面。
// ──────────────────────────────────────────────────────────────────────
/**
 * 该 catalog 行是否是「原生 Anthropic 协议腿」：`anthropic_messages` 协议、且网关下发
 * `translated === false`。`translated` 缺席（旧网关 / 丢字段）一律不算——判不了不放行。
 * 单一真相源：Claude 主脑池、平台策展、组织 / 角色圈定的主脑线分类都经这一处。
 */
export function isNativeAnthropicLeg(entry) {
    return entry.protocol === "anthropic_messages" && entry.translated === false;
}
/**
 * 该 catalog 条目是否进「Claude 主脑候选池」= 是主脑条目(isBrainCatalogEntry：anthropic 行、非
 * 图片/视频工具组、非生图) 且 是原生 Anthropic 协议腿({@link isNativeAnthropicLeg})。picker / 冷启动
 * 默认推导 / 管理策展页据此只呈现原生腿；`isBrainCatalogEntry` 保留给「结构性主脑 vs 工具分桶」等
 * 仍需含翻译腿的面。
 *
 * **本谓词只管「网关下发的行」**。用户自建端点走 {@link isUserCustomBrainEntry} 那条并列
 * 判据（REQ-39, 2026-08-19），两者是主脑候选池的两个并集分支。
 */
export function isBrainPoolEntry(entry) {
    return isBrainCatalogEntry(entry) && isNativeAnthropicLeg(entry);
}
/**
 * 该 catalog 条目是否是「用户自建端点的主脑行」= 是主脑条目(isBrainCatalogEntry) 且它的
 * `chosenGroup` 属于 source="user-custom" 的组（REQ-39, 2026-08-19 PM 定稿）。
 *
 * 为什么它必须与 {@link isBrainPoolEntry} **并列**而不是放宽那条判据：Claude 主脑池回答的是
 * 「网关说这条 anthropic 腿是上游原生的」，自建端点是另一回事：那是用户自己填的 baseUrl + key，
 * 不经网关、没有 `translated` 事实，DOSIA 既不知道也不该替他判断那背后是什么模型；
 * 挡住它只会让「我明明创建成功了却哪儿都找不到」（RC E2E 实证 v0.3.77 §3）。
 *
 * **判据是组凭据的 `source`，不是 vendor/family 字符串**：`GroupCredentialSource` 由主进程
 * 写入面产出，是自建与否的权威事实；vendor 串（"custom"/"自定义"）是展示层启发式，用户
 * 把组取名叫 "my-proxy" 就漏判。
 *
 * **不做可用性伪装**：进池只意味着「可被选中」，不代表 DOSIA 验证过它能跑。选中后路由
 * 失败按架构不变量 #11 明确报错，不静默回退——本谓词不参与任何回退决策。
 *
 * @param userCustomGroups source==="user-custom" 的组名集合（渲染层由组凭据推导）。
 */
export function isUserCustomBrainEntry(entry, userCustomGroups) {
    const group = entry.chosenGroup?.name;
    if (!group || !userCustomGroups?.has(group))
        return false;
    return isBrainCatalogEntry(entry);
}
/**
 * 产品规则单源：「没有清单行时，什么 modelId 允许 seed 主对话 / 主脑模型位」。
 *
 * = Claude 家族 id（isClaudeFamilyModel：`claude-*` 与 opus/sonnet/haiku/opusplan/default/fable
 * 裸别名及 [1m] suffix）。Claude 家族是 Agent SDK 自己的模型命名空间，个人 Claude 订阅腿根本没有
 * 网关清单行，只能按 id 认；其余任何 id 是否进 Claude 引擎只由清单行的原生协议决定
 * （{@link isBrainPoolEntry}），**没有行就不判**——缓存里的 deepseek / kimi / glm / mimo / qwen 等
 * 网关模型 id 一律不 seed，冷启动先转圈，清单水合后由 entitlement / settings.defaultModel 恢复。
 *
 * 用途：渲染层冷启动 seed（getPersistedModel 读 localStorage `dosia_defaultModel`）在把缓存值
 * 灌进 currentModelAtom 前先过此谓词——陈旧的非 Claude 引擎 concrete 缓存（gpt / doubao 等）直接被
 * 拒（返回空 → 走转圈 + useDefaultModel entitlement 推导），从 seed 源头消灭「首条消息透传一个已
 * 不在 Claude 池的模型」的竞态窗口（衔接红线 #7「先删风险源」；main 侧 migrateRetiredNonNativeBrainGroup
 * 只归一主进程 DB，够不到渲染进程 localStorage seed）。
 */
export function isBrainSeedableModel(modelId) {
    return isClaudeFamilyModel(modelId);
}
/** pi 链路的 modelId 后缀标记（单一真相源；`[1m]` 等其它 suffix 与之正交、可共存）。 */
export const PI_CHAIN_SUFFIX = "[pi]";
/** Claude 链路在 catalog 里消费的 protocol 行（anthropic 母语）。pi 链路的协议行**不是常量**——
 *  它由清单逐模型选出（{@link resolvePiWireRow}，N-121），链路→协议行的单点解析由
 *  catalogProtocolForModelChain 承载。 */
export const ANTHROPIC_MESSAGES_PROTOCOL = "anthropic_messages";
/**
 * 「这个模型 id 像不像 GPT / o 系 / grok 文本」的名字判定——**不参与 pi 选线**（pi 走哪条接口只看清单，
 * 见 {@link resolvePiWireRow}）。剩下两个消费者：Codex 编码委派按它挑 Responses 模型
 * （desktop model-config），以及 core `classifyPiProtocol` 在清单没给 `family_slug` 时的家族兜底。
 * 裸模型名与网关前缀两种写法都兜。排除 `grok-imagine-*` 媒体模型。
 */
export function isPiResponsesModel(modelId) {
    const id = stripModelSuffixTag(modelId ?? "").toLowerCase();
    if (id.startsWith("gpt") || /^o[1-9]/.test(id) || id.includes("openai"))
        return true;
    return id.startsWith("grok") && !id.includes("imagine");
}
/**
 * 「该 modelId 是生图模型吗」的**单一真相源**（红线#9）——`gpt-image-*` 起步（如
 * `gpt-image-2` / `gpt-image-2-full`），未来生图模型族命名对齐时按此扩展语义。
 *
 * 存在原因：`isPiResponsesModel` 只按 gpt/o 前缀判定「走 pi-ai openai-responses 腿」，
 * `gpt-image-2` 恰好也以 `gpt` 起步、命中该判定——凡从 catalog 按
 * `protocol === "openai_responses" && isPiResponsesModel(modelId)` 投影「委派/主脑候选
 * 模型清单」的消费点，都需再 `&& !isImageGenModelId(modelId)` 排除生图模型，否则生图
 * 模型会混入编码委派/对话主脑的模型选择器（2026-07-22 Codex 委派清单实证：catalog 修复
 * 后 gpt-image-2 带 openai_responses 行，被 isPiResponsesModel 误判为可委派编码模型）。
 */
export function isImageGenModelId(modelId) {
    const id = stripModelSuffixTag(modelId ?? "").toLowerCase();
    return /^gpt-image-/.test(id);
}
/**
 * 清单 + 模型 id → pi 链在清单里**选中的那一行**（N-121 批次 B 的唯一入口）。先剥 `[pi]` / `[1m]` 等后缀，
 * 汇总该 base id 的全部行，交 {@link piNativeWireRow}（只在上游原生行里按家族偏好挑）。没有可走的原生行
 * → undefined：该模型不进 pi，调用方按「没有 pi 腿」处置，**绝不**按名字猜一条或拿翻译行顶替。
 *
 * 凭证从哪条行取（desktop / agent-host）、route 能力从哪条行投影（{@link resolvePiRouteFromCatalog}）、
 * pi 菜单与主脑腿成员（{@link derivePiChainMenu} / {@link brainLegEntries}）都经这一处，四者同源。
 */
export function resolvePiWireRow(catalog, modelId) {
    if (!modelId)
        return undefined;
    return piNativeWireRow(catalogRowsOfBase(catalog, modelId));
}
/**
 * 链路 → 该模型在 catalog 消费的 protocol 行（凭证查找的唯一入口——agent-bridge sendMessage/setModel、
 * resolveBackgroundSessionCredentials、context pack 都调此）。claude 链恒 anthropic_messages；pi 链取
 * {@link resolvePiWireRow} 选中行的协议，**没有原生 pi 行 → undefined**（调用方不得拿它去查凭证：
 * 那会落到默认的 anthropic 行，把 pi 会话接到别的接口上）。
 */
export function catalogProtocolForModelChain(chain, modelId, catalog) {
    return chain === "pi" ? resolvePiWireRow(catalog, modelId)?.protocol : ANTHROPIC_MESSAGES_PROTOCOL;
}
// ── pi 引擎自动压缩的保留余量（N-72 批 2，单源）────────────────────────────────
//
// 业务白话：pi 引擎（deepseek / glm / kimi / gpt 等）的对话快装满时，DOSIA 自己把较早的对话
// 总结压缩。「快满」= 占用达到「窗口 − 保留余量」。余量要容下下一次请求的回复 + 一批工具结果 +
// 估算误差——这三样都随窗口变大而变大（单条工具结果预算按剩余窗口成比例给，估算器的误差按占用
// 成比例放大），所以余量按窗口**成比例**，并设上下限：128K 与 1M 的模型各按自己的窗口留余量。
//
// 取值依据（上游 pi v1.1.0 为锚，/Users/k/code/pi）：
//  · 下限 16_384 = 上游 `DEFAULT_COMPACTION_SETTINGS.reserveTokens`
//    （packages/coding-agent/src/core/compaction/compaction.ts:128、settings-manager.ts:24），上游给
//    「提示 + 一次回复」的最低余量；小窗口（32K 级）也因此有了正的阈值，能自动压缩。
//  · 比例 0.25：DOSIA 未登记窗口的 pi 模型兜底 128K（本文件 PI_ROUTE_DEFAULT_CONTEXT_WINDOW、
//    core credentials.ts DEFAULT_CONTEXT_WINDOW），此前固定余量 33K 在这个窗口上 ≈ 25.8%；取 0.25
//    让最常见的兜底窗口保持原有余量（32K，阈值 96K），不比改前晚压。
//  · 上限 131_072 = 8 × 上游下限：1M 级窗口的余量够装一次长回复 + 一批满额工具结果，但不至于
//    在 3/4 处就提前压（1M 窗口阈值 917_504；改前固定 33K 时是 1_015_576，只剩 3%）。
// 上游另有按模型精确覆盖（settings.compaction.modelOverrides["provider/modelId"]）——那是给用户
// 手调的配置面；DOSIA 由窗口推导，不另造按模型的余量表。
/** pi 自动压缩保留余量相对上下文窗口的比例。 */
export const PI_COMPACTION_RESERVE_RATIO = 0.25;
/** pi 自动压缩保留余量下限（tokens）= 上游 pi 默认 reserveTokens。 */
export const PI_COMPACTION_RESERVE_MIN_TOKENS = 16_384;
/** pi 自动压缩保留余量上限（tokens）= 8 × 上游默认 reserveTokens。 */
export const PI_COMPACTION_RESERVE_MAX_TOKENS = 131_072;
/**
 * 某个上下文窗口下 pi 自动压缩的保留余量（tokens）：`clamp(round(窗口 × 0.25), 16_384, 131_072)`。
 * 窗口不可信（非有限数 / ≤0）→ 下限；调用方用「窗口 − 余量」算阈值时自然落到 0（不触发）。
 * 窗口事实的来源仍是唯一解析链（catalog → DOSIA_MODEL_REGISTRY → 兜底），本函数只做换算。
 */
export function piCompactionReserveTokens(contextWindow) {
    if (!Number.isFinite(contextWindow) || contextWindow <= 0)
        return PI_COMPACTION_RESERVE_MIN_TOKENS;
    const proportional = Math.round(contextWindow * PI_COMPACTION_RESERVE_RATIO);
    return Math.min(PI_COMPACTION_RESERVE_MAX_TOKENS, Math.max(PI_COMPACTION_RESERVE_MIN_TOKENS, proportional));
}
// ── pi 引擎拆轮压缩的保留尾巴（B-152，单源）──────────────────────────────────────
//
// 业务白话：员工一条指令让 AI 连跑很多步工具时，这一轮本身就可能把窗口撑满。此时压缩要在这一轮
// 中间切开：较早的工具往返整理进摘要，最近一段往返原样留着让 AI 接着干。「最近一段」留多大就是
// 这里的预算——留太少 AI 丢了刚拿到的一手材料，留太多压完仍在压缩线上、等于白压。
//
// 取值依据（上游 pi origin/main 为锚，2026-10-11 读 /Users/k/code/pi）：
//  · 上限 20_000 = 上游 `DEFAULT_COMPACTION_SETTINGS.keepRecentTokens`
//    （packages/coding-agent/src/core/compaction/compaction.ts:129；docs/compaction.md「How It Works」
//    第 1 步：从最新往回累加到 keepRecentTokens 处切）。
//  · 比例 0.25（相对压缩线 = 窗口 − 保留余量）：**无外部判据，保守值**。上游是固定 20K，不随窗口
//    变；DOSIA 有 32K/64K 级小窗口模型，固定 20K 在 32K 窗口（压缩线 16_384）上比压缩线还大，压完
//    必然仍在线上。取压缩线的 1/4，保证压缩后「摘要 + 员工原话 + 保留尾巴 + 系统提示/工具」整体
//    明显回落到线下；128K 兜底窗口（压缩线 96K）× 0.25 = 24K，被上限收回上游 20K，常见窗口行为
//    与上游一致。
/** pi 拆轮压缩保留尾巴的上限（tokens）= 上游 pi 默认 keepRecentTokens。 */
export const PI_COMPACTION_KEEP_RECENT_MAX_TOKENS = 20_000;
/** pi 拆轮压缩保留尾巴相对压缩线（窗口 − 保留余量）的比例。 */
export const PI_COMPACTION_KEEP_RECENT_RATIO = 0.25;
/**
 * 某个上下文窗口下，pi 压缩在「当前这一轮内部切开」时原样保留的最近工具往返预算（tokens）：
 * `min(20_000, round((窗口 − piCompactionReserveTokens(窗口)) × 0.25))`。
 * 窗口不可信（非有限数 / ≤0）→ 上限（上游默认）；压缩线 ≤0 的极小窗口 → 0（只留最后一次往返）。
 */
export function piCompactionKeepRecentTokens(contextWindow) {
    if (!Number.isFinite(contextWindow) || contextWindow <= 0)
        return PI_COMPACTION_KEEP_RECENT_MAX_TOKENS;
    const threshold = Math.max(0, contextWindow - piCompactionReserveTokens(contextWindow));
    return Math.min(PI_COMPACTION_KEEP_RECENT_MAX_TOKENS, Math.round(threshold * PI_COMPACTION_KEEP_RECENT_RATIO));
}
/** pi route 兜底常量（catalog 缺该能力字段时的最后落点，与 credentials.ts 同值口径）。 */
const PI_ROUTE_DEFAULT_CONTEXT_WINDOW = 128_000;
const PI_ROUTE_DEFAULT_MAX_OUTPUT_TOKENS = 8_192;
/**
 * pi route 唯一构建器（红线#9）——把**选中的那一行**（协议已收窄成 pi 四条腿之一，见
 * {@link resolvePiWireRow}）投影成 PiModelRoute。纯函数、缺字段安全兜底、不抛。调用入口是下面的
 * resolvePiRouteFromCatalog（desktop 与云端 agent-host 共用），产物挂进 SessionConfig.piRoute。
 * `protocol` 直接取行上的 `entry.protocol`——不再按模型名字推（N-121）。全仓其它任何地方禁手搓此形状。
 */
export function buildPiModelRoute(entry) {
    const caps = entry.capabilities;
    const pricing = entry.pricing;
    const thinkingParam = caps?.thinkingParam ?? null;
    // reasoning：结构化描述优先（thinkingParam.supported），回落布尔 supportsThinking，再回落保守真。
    const reasoning = thinkingParam?.supported ?? caps?.supportsThinking ?? true;
    // maxOutputTokens：outputTokenParam.max（覆盖上界）优先，其次其 default，再 capabilities.maxTokens，末兜底。
    const maxOutputTokens = caps?.outputTokenParam?.max ??
        caps?.outputTokenParam?.default ??
        caps?.maxTokens ??
        PI_ROUTE_DEFAULT_MAX_OUTPUT_TOKENS;
    // contextWindow：catalog → DOSIA_MODEL_REGISTRY → 128K 兜底。中间那一档是 2026-07-30
    // 全模型窗口审查补的（R-A，kehan 事故的同构点）：catalog 行缺 contextWindow 时此处曾直落
    // 128K，于是 prod catalyst org 的 doubao-seed-2.0-pro（available/piEligible 均真、catalog
    // 无该字段、真实窗 256K）在 pi 腿按 128K 记账、95K 就提前压缩，而同一时刻显示链（一直有
    // registry 兜底）显示 256K —— 同一个事实两个答案。SDK/CLI 腿的同名兜底
    // （resolveDeclaredContextWindow：catalog → registry → 各自 floor）已有此档，这里补齐后
    // 两条腿共用同一条解析链，只是最后的 floor 各自不同（SDK 200K / pi 128K，历史口径）。
    // 归一与 registry 键消费先例同款：先 stripModelSuffixTag 取干净 base id，再 normalizeSdkValue
    // 落到 registry 键（Claude 具体 id → 裸别名）——同 context-window-resolver.ts 的查表写法。
    const contextWindow = caps?.contextWindow
        ?? DOSIA_MODEL_REGISTRY[normalizeSdkValue(stripModelSuffixTag(entry.modelId ?? ""))]?.contextWindow
        ?? PI_ROUTE_DEFAULT_CONTEXT_WINDOW;
    const effectiveContextWindow = typeof caps?.effectiveContextWindow === "number" && caps.effectiveContextWindow > 0
        ? caps.effectiveContextWindow
        : null;
    return {
        modelId: entry.modelId,
        protocol: entry.protocol,
        group: { id: entry.chosenGroup?.id ?? 0, name: entry.chosenGroup?.name ?? "" },
        familySlug: entry.familySlug?.trim() || null,
        capabilities: {
            vision: caps?.supportsVision === true,
            reasoning,
            thinkingLevels: thinkingParam,
            maxOutputTokens,
            outputTokenField: caps?.outputTokenParam?.field ?? null,
            contextWindow,
            effectiveContextWindow,
            cost: {
                input: pricing?.inputPer1m ?? 0,
                output: pricing?.outputPer1m ?? 0,
                cacheRead: pricing?.cacheReadPer1m ?? 0,
                cacheWrite: pricing?.cacheWritePer1m ?? 0,
            },
        },
        faceCompat: {
            acceptsDeveloperRole: typeof entry.acceptsDeveloperRole === "boolean" ? entry.acceptsDeveloperRole : null,
            reasoningReplay: entry.reasoningReplay ?? null,
        },
        defaults: caps?.defaults ?? null,
    };
}
/**
 * 清单 + 模型 id → 该模型 pi 选中行的 PiModelRoute（桌面 resolvePiRoute 与云端 agent-host 共用的唯一
 * 一步，B-90）。选行走 {@link resolvePiWireRow}（凭证查找读同一行，故「凭证从哪条行取」与「能力从哪条行
 * 投影」同源）。没有可走的原生行（清单为空 / 无模型 id / 只有翻译行 / 漏发 translated）→ null：调用方不挂
 * piRoute，该模型在这一刻不能走 pi。
 */
export function resolvePiRouteFromCatalog(catalog, modelId) {
    const entry = resolvePiWireRow(catalog, modelId);
    return entry ? buildPiModelRoute(entry) : null;
}
/** Single source for the Anthropic-leg provenance badge. Non-Anthropic rows do not apply. */
export function anthropicLegBadge(entry) {
    if (!entry || entry.protocol !== "anthropic_messages")
        return undefined;
    if (entry.translated === true)
        return "translated";
    if (entry.translated === false)
        return "native";
    return "unknown";
}
// ──────────────────────────────────────────────────────────────────────
// 组×模型×协议可用性契约消费（P3，模型清单 hub + 三池简化，2026-07）。
//
// `ModelCatalogEntry.supported`/`available`/`unavailableReason` 是网关侧并行 PR 新增的
// **全 optional** 字段（组详情矩阵契约同源）——本仓消费端必须缺字段安全兜底，不得假设
// 已部署。单一真相源：任何页面判「这个协议格子该显示什么」都调 `protocolAvailability`，
// 不手写 available/supported 判断（禁第二真相源，红线 #9）。
// ──────────────────────────────────────────────────────────────────────
/** `unavailableReason` 枚举 → 人话（网关侧口径，见组详情矩阵契约 pre.contract 注释）。
 *  未知/缺失原因码 → 原样透传（不臆断文案，宁可显示原始枚举值也不瞎翻译）。 */
/** Stable i18n key for user-facing unavailable reasons; unknown codes remain raw. */
export function unavailableReasonI18nKey(reason) {
    switch (reason) {
        case "no_credential":
        case "quota_exhausted":
        case "rate_limited":
        case "maintenance":
        case "probe_failed":
        case "adapter_pending":
            return `model.unavailableReason.${reason}`;
        default:
            return undefined;
    }
}
export function describeUnavailableReason(reason) {
    switch (reason) {
        case "no_credential": return "无凭据";
        case "quota_exhausted": return "配额耗尽";
        case "rate_limited": return "上游冷却中";
        case "maintenance": return "维护中";
        case "probe_failed": return "探测失败";
        case "adapter_pending": return "适配待接入";
        default: return reason || "无存活上游";
    }
}
/**
 * 从单条 catalog 行（一个 (model, protocol) 组合）判定协议可用性档位。**缺字段安全兜底**：
 * `available`/`supported` 均未部署时返回 `level:"unknown"`——调用方据此决定是否回落到「entry
 * 存在即视为支持」的既有启发式（零回归），不得把 unknown 误判成 unsupported。
 */
export function protocolAvailability(entry) {
    if (!entry || (entry.supported === undefined && entry.available === undefined)) {
        return { level: "unknown", label: "" };
    }
    if (entry.supported === false)
        return { level: "unsupported", label: "不支持" };
    if (entry.available === false)
        return { level: "degraded", label: `上游暂不可用 · ${describeUnavailableReason(entry.unavailableReason)}` };
    return { level: "available", label: "可用" };
}
/**
 * Return the catalog rows that are actually consumed as the selected brain leg for a chain.
 * This is the single source of truth for Claude/Pi brain-leg membership.
 *
 * 两条腿同一原则：一个模型能进哪条引擎只由它经网关的**原生**协议决定，只认 `translated === false`
 * 且 `supported !== false` 的行（N-121 批次 D，收紧 N-119 §7 的过渡口径）。
 *
 * claude：Claude 主脑池成员（{@link isBrainPoolEntry}：anthropic 行、非工具组非生图、上游原生）。
 * GPT / grok / gemini 的 anthropic 面是网关翻译的，作「可指定模型」时不进 Claude 腿，由 pi 腿承接。
 *
 * pi：每个 base 模型按 {@link resolvePiWireRow} 选出一种原生接口，成员 = 该模型在**那种接口**上的原生行
 * （多个分组各出一行时全算，可用性按 OR 聚合）。旁路协议（同模型的其它原生面、翻译面）不算——pi 运行时
 * 根本不会打那些面。`entries` 可以混着多个模型，按 base id 各自选。
 */
export function brainLegEntries(entries, chain) {
    if (chain === "claude")
        return entries.filter((entry) => isClaudeLegRow(entry));
    const wireByBase = new Map();
    const chosenWire = (modelId) => {
        const base = stripModelSuffixTag(modelId);
        if (!wireByBase.has(base))
            wireByBase.set(base, resolvePiWireRow(entries, base)?.protocol);
        return wireByBase.get(base);
    };
    return entries.filter((entry) => entry.translated === false
        && entry.supported !== false
        && entry.protocol === chosenWire(entry.modelId));
}
/**
 * 「这一行能不能当 Claude 引擎腿」的唯一行判据（N-121 批次 D）：网关下发的行只认上游原生的 anthropic 行
 * （{@link isBrainPoolEntry}，且 `supported !== false`，与 pi 腿同一原则）；员工自建接入点的主脑行
 * （{@link isUserCustomBrainEntry}，REQ-39）没有 `translated` 事实，按组凭据来源并入——不传
 * `userCustomGroups` 时只判网关行（{@link brainLegEntries} 的 claude 腿就是这一半）。
 *
 * 选主脑面、Agent「指定模型」面、create_agent、桌面 Claude 链会话的凭证门都经这一处。
 */
export function isClaudeLegRow(entry, userCustomGroups) {
    return (isBrainPoolEntry(entry) && entry.supported !== false) || isUserCustomBrainEntry(entry, userCustomGroups);
}
/**
 * 该模型在清单里**有行**、却一条 Claude 引擎腿都没有（GPT 等只有网关翻译的 anthropic 行）——Claude 引擎跑不了，
 * 调用方要明确失败（换模型 / 改走 pi），不能当成冷启动缺口或退回个人会员。清单里一行都没有时返回 false：
 * 那是「供给缺失 / 已下线」，归 no_catalog_row 与下线判官处置。
 */
export function lacksClaudeLeg(catalog, modelId, userCustomGroups) {
    if (!modelId)
        return false;
    const base = stripModelSuffixTag(parseModelChain(modelId).withoutChain).trim();
    if (!base)
        return false;
    const rows = catalog.filter((entry) => stripModelSuffixTag(entry.modelId) === base);
    return rows.length > 0 && !rows.some((entry) => isClaudeLegRow(entry, userCustomGroups));
}
/**
 * 指定了模型、没写引擎的完整 AI 任务该跑哪个引擎（N-121 批次 D，2026-10-11 产品经理拍板「引擎按模型自动选」）：
 * 该模型有 Claude 引擎腿 → `"claude"`；否则有 pi 腿 → `"pi"`；两条都没有 → `undefined`（调用方明确报错，
 * 不猜）。判据就是两条腿的单源 {@link brainLegEntries}。
 */
export function resolveAgentFrameworkForModel(catalog, modelId) {
    const legs = engineLegsForModel(catalog, modelId);
    if (legs.claude)
        return "claude";
    if (legs.pi)
        return "pi";
    return undefined;
}
/**
 * Agent 锁定的模型该跑哪个引擎（N-182 P1 R3.3）：模型 id 显式写了 `[pi]` 且该模型有 pi 腿 → `"pi"`；
 * 否则同 {@link resolveAgentFrameworkForModel}（它去掉后缀、优先 Claude，所以显式 `[pi]` 要先判）。
 * 两条腿都没有 → `undefined`（调用方本次不跑，不猜）。agent-host 开跑与对话里建任务补引擎都调它。
 */
export function resolveLockedModelFramework(catalog, modelId) {
    if (parseModelChain(modelId).chain === "pi" && engineLegsForModel(catalog, modelId).pi)
        return "pi";
    return resolveAgentFrameworkForModel(catalog, modelId);
}
/** 该模型在两条引擎上各有没有腿（单源 {@link brainLegEntries}）。值班表写闸、改模型时要不要换引擎都按它判。 */
export function engineLegsForModel(catalog, modelId) {
    const base = stripModelSuffixTag(parseModelChain(modelId).withoutChain).trim();
    if (!base)
        return { claude: false, pi: false };
    const rows = catalog.filter((entry) => stripModelSuffixTag(entry.modelId) === base);
    return {
        claude: brainLegEntries(rows, "claude").length > 0,
        pi: brainLegEntries(rows, "pi").length > 0,
    };
}
/** Aggregate availability for the rows that make up one brain chain leg. */
export function brainLegAvailabilitySummary(entries, chain) {
    const eligible = brainLegEntries(entries, chain);
    if (eligible.length === 0)
        return { blocked: false, level: "unknown", label: null };
    const statuses = eligible.map((entry) => protocolAvailability(entry));
    const available = statuses.find((status) => status.level === "available");
    if (available)
        return { blocked: false, level: "available", label: available.label };
    const unknown = statuses.find((status) => status.level === "unknown");
    if (unknown)
        return { blocked: false, level: "unknown", label: null };
    const degraded = statuses.find((status) => status.level === "degraded");
    if (degraded && statuses.every((status) => status.level === "degraded")) {
        return { blocked: true, level: "degraded", label: degraded.label };
    }
    // Unsupported-only rows retain the old visible label without treating them as upstream-blocked.
    const unsupported = statuses.find((status) => status.level === "unsupported");
    return { blocked: false, level: "unknown", label: unsupported?.label ?? null };
}
/**
 * 平台治理页「Claude 主脑 / Pi 主脑」勾选可用性单一判定入口（红线 #9——治理页禁自己手写
 * available===false / count===0 判断）。对某 model 的全部 catalog 行按 role 过滤出
 * role-eligible 协议子集，逐条走 `protocolAvailability` 聚合：
 *  - brain：{@link brainLegEntries} 的 Claude 腿（上游原生的 anthropic_messages 行，网关翻译行不算）。
 *  - pi：**只**取清单选中的那种原生接口的行（{@link brainLegEntries}：GPT / grok → responses；
 *    deepseek/glm/kimi → chat；Gemini → gemini；Claude → anthropic）。孪生的旁路协议（grok Chat
 *    Completions 维护中、GPT chat 行配额耗尽）不参与 Pi 勾选判定——Pi 运行时根本不会打那条腿。
 *  - role-eligible 协议为空（该 model 压根没有这个 role 的协议行）→ `{blocked:false, note:null}`
 *    （不适用，非不可用）。
 *  - **全部** role-eligible 协议都是 `level:"degraded"` → `blocked:true`（无任一 available 兜底）。
 *  - 任一协议 `level:"available"` 或 `level:"unknown"`（契约未部署，缺字段安全兜底，见
 *    `protocolAvailability` 同款兜底）→ `blocked:false`。
 *  - `note` 仅在汇总档位为 degraded 时返回首个 degraded **role-eligible** 协议的 label；
 *    有 available/unknown 兜底时不再报 amber。
 *    （`unsupported` 协议不产出 note——「不支持」是静态契约事实，不是「上游无可调度账号」，
 *    不应被误报成可用性原因）。
 */
export function modelRoleAvailability(entries, role) {
    const summary = brainLegAvailabilitySummary(entries, role === "brain" ? "claude" : "pi");
    return {
        blocked: summary.blocked,
        note: summary.level === "degraded" ? summary.label : null,
    };
}
/**
 * 解析 modelId 的链路身份。返回 `{ chain, withoutChain }`——`withoutChain` **只剥链路标记，
 * 保留其它 suffix**（`[1m]` 等），故它仍可能带 tag；要拿干净 base id 用 `stripModelSuffixTag`。
 *
 * 顺序不敏感（`[pi][1m]` 与 `[1m][pi]` 同判），非字符串 / 空 → `claude`（默认链路，绝不抛）。
 *
 *   parseModelChain("kimi-k3")                  → { chain: "claude", withoutChain: "kimi-k3" }
 *   parseModelChain("kimi-k3[pi]")              → { chain: "pi",     withoutChain: "kimi-k3" }
 *   parseModelChain("deepseek-v4-pro[1m][pi]")  → { chain: "pi",     withoutChain: "deepseek-v4-pro[1m]" }
 */
export function parseModelChain(modelId) {
    if (typeof modelId !== "string" || !modelId)
        return { chain: "claude", withoutChain: "" };
    const withoutChain = modelId.split(PI_CHAIN_SUFFIX).join("");
    return { chain: withoutChain === modelId ? "claude" : "pi", withoutChain };
}
/**
 * 产出某链路的变体 id（唯一构建器，红线 #9——禁在消费点手拼 `` `${id}[pi]` ``）。
 *
 * **幂等**：先经 `parseModelChain` 剥掉已有链路标记再挂，故对已带 `[pi]` 的 id 重复调用不会
 * 产生 `[pi][pi]`（对照 desktop `model-config.ts` 的 `[1m][1m]` 双后缀历史教训）。反向也成立：
 * `buildChainedModelId("kimi-k3[pi]", "claude")` → `"kimi-k3"`，两向同一实现。
 * 其它 suffix（`[1m]`）原样保留。
 */
export function buildChainedModelId(baseOrFullId, chain) {
    const { withoutChain } = parseModelChain(baseOrFullId);
    return chain === "pi" ? `${withoutChain}${PI_CHAIN_SUFFIX}` : withoutChain;
}
/**
 * 从 catalog 依据 pi 策展白名单派生「pi 链路可选项」= eligible（清单能为它选出一条原生 pi 接口，见
 * {@link resolvePiWireRow}）∩ on（base id 在策展白名单内）。纯函数、按选中行在清单里的位置保序、按
 * base id 去重（一个模型可能有多条组行/多协议行，菜单只出一项）。**未策展（白名单空/缺省）→ 空数组**
 * （pi 内测，不默认开放）。
 *
 * 返回**变体 id**（`base[pi]`）供选择态/UI 消费；同时带回 `baseModelId`（策展/治理/档位映射
 * 比对用，那些面全走 base id）与**选中的那一行** `entry`（凭证/组信息与 route 同一行）。
 */
export function derivePiChainMenu(catalog, piSelection) {
    const on = new Set((piSelection ?? []).filter((v) => typeof v === "string").map(stripModelSuffixTag));
    if (on.size === 0)
        return [];
    const chosenByBase = new Map();
    const out = [];
    for (const entry of catalog) {
        if (!entry?.modelId)
            continue;
        const baseModelId = stripModelSuffixTag(entry.modelId);
        if (!baseModelId || !on.has(baseModelId))
            continue;
        if (!chosenByBase.has(baseModelId))
            chosenByBase.set(baseModelId, resolvePiWireRow(catalog, baseModelId));
        if (chosenByBase.get(baseModelId) !== entry)
            continue;
        out.push({ modelId: buildChainedModelId(baseModelId, "pi"), baseModelId, entry });
    }
    return out;
}
/**
 * Derive the stable pi brain-line base ids from catalog eligibility and the platform pi curation.
 * This deliberately does not inspect the mutable brain-priority list: changing priority must not
 * change whether a model is classified as a brain-line candidate by model selection.
 */
export function piBrainLineBases(catalog, piSelection) {
    return new Set(derivePiChainMenu(catalog, piSelection).map(({ baseModelId }) => baseModelId));
}
/**
 * 从 catalog 派生 Pi 可用的 base modelId 集（N-121 批次 B）。
 *
 * 能不能进 pi 只看清单：该模型能选出一条上游原生、且 pi 有腿的接口（{@link resolvePiWireRow}）。不再按名字
 * 排除 Gemini，也不再写死「只认两条 OpenAI 接口」——Gemini / Claude 有原生面就可进 pi。
 * 在选中行上保留三道与协议无关的门：
 *  - 名字含 `image` / `auto`：生图 SKU 与 Auto 路由不是 pi 主脑运行时；
 *  - 选中行的组被显式策展成 `backendProtocol === "anthropic"`；
 *  - 清单明说不支持工具调用（`supportsToolUse === false`）。
 * 这是候选池判断，真实可用性仍需分组/账号测试确认。
 */
export function derivePiEligibleModels(catalog) {
    const eligible = new Set();
    const decided = new Set();
    for (const row of catalog) {
        const base = row?.modelId ? stripModelSuffixTag(row.modelId).trim() : "";
        if (!base || decided.has(base))
            continue;
        decided.add(base);
        const entry = resolvePiWireRow(catalog, base);
        const modelId = entry?.modelId?.trim();
        if (!entry || !modelId)
            continue;
        const normalized = modelId.toLowerCase();
        if (normalized.includes("image") || normalized.includes("auto"))
            continue;
        if (entry.chosenGroup?.backendProtocol === "anthropic")
            continue;
        if (entry.capabilities?.supportsToolUse === false)
            continue;
        eligible.add(modelId);
    }
    return eligible;
}
/**
 * 单个 base 模型是否 Pi（母语引擎）可路由（模型参数范围治理 PR-2，2026-07-23）——**单一判定源**。
 *
 * 红线 #9：**不另立第二判据**，委托 `derivePiEligibleModels`（同一清单选线 + image/auto 工具面排除 +
 * backendProtocol/toolUse 门），只是把「派生全集」收成「单模型 boolean」。base id 两侧
 * 均经 `stripModelSuffixTag` 归一（org bundle 的 modelId 可能带 `[1m]` 等 suffix，catalog 全集里通常
 * 是裸 base，故逐条 strip 后比对，避免 suffix 错配漏判）。
 *
 * 消费点：`getBundleRaw` join 贴 `EnterpriseAdminBundleRawModel.piEligible`（bundle-actions.ts，仿
 * `aggregateModelAvailability`）→ 管理端 ChainParamRangeEditor 据此 gate Pi 段可见性。
 */
export function derivePiEligibility(catalog, modelId) {
    const target = stripModelSuffixTag(modelId);
    if (!target)
        return false;
    for (const id of derivePiEligibleModels(catalog)) {
        if (stripModelSuffixTag(id) === target)
            return true;
    }
    return false;
}
// resolvePiBackendProtocol（org_pi_selection × eligible 的路由 overlay）已于 PR2 退役：
// 链路（Claude vs pi）不再由 org 布尔策展决定，而由**员工选中的变体 id**（`base` vs
// `base[pi]`，见 parseModelChain）在会话构造 choke point 单点派生。org_pi_selection_v1
// 仍作为「哪些模型可产出 pi 变体」的候选来源（derivePiChainMenu 消费），但不再参与
// 运行时路由决策——单一真相源收敛到变体 id（红线 #9）。
/**
 * pi 策展 PR2 —— **唯一构建器**（红线 #9）：把原始「pi-on 白名单」输入（admin 勾选 / 存储读回 /
 * bundle 下发）归一为规范 base modelId 字符串数组。逐项 string 校验、`stripModelSuffixTag` 剥
 * `[1m]` 等 suffix、trim、去空、去重（复用 `sanitizeModelIdList` 单点，与 buildModelSelection
 * 同一归一语义）。空 / 非数组 / 全脏 → 空数组（= 未策展，与 PR1 `resolveModelCredentials` overlay
 * 的 orgPiSelection=[] 零影响语义对齐）。org PUT 路由归一、bundle wire 下发、desktop 落库读回都必
 * 接此构建器，不手搓 object literal（禁第二真相源）。
 */
export function buildPiModelSelection(raw) {
    return sanitizeModelIdList(raw) ?? [];
}
/**
 * pi 策展 PR2 —— **反序列化边界 guard**（红线 #9）：`JSON.parse` 读盘 / 网络 / bundle wire 后
 * 索引前先过此单点。语义与 `buildPiModelSelection` 完全一致（pi 白名单无 version 包装，构建即解析），
 * 委托同一实现保证读写单源。畸形输入（字符串 / 含非 string 元素 / null）→ 空数组降级，绝不裸抛。
 */
export function parsePiModelSelection(raw) {
    return buildPiModelSelection(raw);
}
/**
 * pi 策展平台级（tenant 级全平台单份）—— **唯一构建器**（红线 #9）：从原始「pi-on 白名单」输入
 * （超管勾选 / 存储读回）构造规范 `PlatformPiCuration`。逐项 sanitize（复用 `sanitizeModelIdList`
 * 单点：strip `[1m]` 等 suffix、trim、去空、去重 → base id 集），空 / 非数组 / 全脏 →
 * `models` 省略（undefined，= 未配置 = 未策展；与 buildPlatformBrainCuration 空即省略同 footgun
 * 防护姿态）。盖上 version=2 + editedAt(now) + editedBy；v1 存量由 parsePlatformPiCuration 迁移。
 * admin-portal PUT 路由与任何服务端产出点
 * 都必须接此构建器，不复制字段（禁第二真相源）。镜像 buildPlatformBrainCuration，字段 brain→pi。
 */
/** Pi 链 effort 六档。成员与顺序的单源是 THINKING_LEVELS，这里只做 membership。 */
const PI_REASONING_EFFORTS = new Set(THINKING_LEVELS);
/** SDK 链 effort 5 档 membership 集（EffortLevel，SDK 0.3.212，含 xhigh；不含 minimal）。
 *  与 PI_REASONING_EFFORTS 分开：minimal 不是 EffortLevel，不能进 SDK 清洗。 */
const SDK_EFFORT_LEVELS = new Set(["low", "medium", "high", "xhigh", "max"]);
const PI_MAX_COMPLETION_TOKENS_MIN = 1;
const PI_MAX_COMPLETION_TOKENS_MAX = 1_048_576;
/**
 * pi reasoning-effort 白名单类型守卫（单一真相源，红线#7 类级去重）——`packages/core`
 * 两处消费点（pi-backend.ts / loop.ts）此前各自逐字重复 `"low"|"high"|"max"` 判断，
 * 统一改调本函数。校验失败即窄化为 `false`（调用侧据此落 `undefined`）。
 */
export function isPiReasoningEffort(v) {
    return typeof v === "string" && PI_REASONING_EFFORTS.has(v);
}
function sanitizePlatformPiModelParams(raw, allowed) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return undefined;
    const allowedSet = new Set(allowed);
    const out = {};
    for (const [rawId, value] of Object.entries(raw)) {
        const modelId = stripModelSuffixTag(rawId.trim());
        if (!modelId || !allowedSet.has(modelId) || !value || typeof value !== "object" || Array.isArray(value))
            continue;
        const v = value;
        const params = {};
        if (typeof v.reasoningEffort === "string" && PI_REASONING_EFFORTS.has(v.reasoningEffort)) {
            params.reasoningEffort = v.reasoningEffort;
        }
        const max = v.maxCompletionTokens;
        if (typeof max === "number" && Number.isInteger(max) && max >= PI_MAX_COMPLETION_TOKENS_MIN && max <= PI_MAX_COMPLETION_TOKENS_MAX) {
            params.maxCompletionTokens = max;
        }
        if (Object.keys(params).length > 0)
            out[modelId] = params;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
const PLATFORM_THINKING_MODES = new Set(["auto", "adaptive", "enabled", "disabled"]);
const PLATFORM_TEMPERATURE_MIN = 0;
const PLATFORM_TEMPERATURE_MAX = 2;
/**
 * `PlatformModelDefaultParams` 唯一 sanitizer（红线 #9）——Claude 主脑池
 * (buildPlatformBrainCuration) 与工具/干活模型池 (buildPlatformToolCuration) 共用，避免各写
 * 一份校验逻辑漂移。镜像 sanitizePlatformPiModelParams 的姿态：key 必须在白名单内才保留，
 * 每字段独立校验，全空的模型项整体丢弃，输出全空则返回 undefined（= 未配置默认参数）。
 *
 * `allowed` 省略（undefined）= **不做白名单过滤**，保留全部形状合法的模型项——供
 * `buildModelSelection`（企业模型治理 P0）复用：ModelSelection 的参数覆盖与可见性 whitelist
 * **解耦**（可见性继承但参数已覆盖是合法态），故不能像平台 builder 那样要求 key ∈ 白名单。
 */
function sanitizePlatformModelDefaultParams(raw, allowed) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return undefined;
    const allowedSet = allowed ? new Set(allowed) : undefined;
    const out = {};
    for (const [rawId, value] of Object.entries(raw)) {
        const modelId = stripModelSuffixTag(rawId.trim());
        if (!modelId || (allowedSet && !allowedSet.has(modelId)) || !value || typeof value !== "object" || Array.isArray(value))
            continue;
        const v = value;
        const params = {};
        if (typeof v.temperature === "number" && Number.isFinite(v.temperature) && v.temperature >= PLATFORM_TEMPERATURE_MIN && v.temperature <= PLATFORM_TEMPERATURE_MAX) {
            params.temperature = v.temperature;
        }
        const max = v.maxTokens;
        if (typeof max === "number" && Number.isInteger(max) && max >= PI_MAX_COMPLETION_TOKENS_MIN && max <= PI_MAX_COMPLETION_TOKENS_MAX) {
            params.maxTokens = max;
        }
        if (typeof v.thinking === "string" && PLATFORM_THINKING_MODES.has(v.thinking)) {
            params.thinking = v.thinking;
        }
        // SDK 主脑池 effort 五档（含 medium/xhigh，不含 minimal）。
        if (typeof v.reasoningEffort === "string" && SDK_EFFORT_LEVELS.has(v.reasoningEffort)) {
            params.reasoningEffort = v.reasoningEffort;
        }
        if (Object.keys(params).length > 0)
            out[modelId] = params;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
export function buildPlatformPiCuration(rawAllowed, editedBy, rawModelParams, rawReviewed) {
    const models = sanitizeModelIdList(rawAllowed);
    const curation = {
        version: 2,
        editedAt: new Date().toISOString(),
        editedBy,
    };
    if (models && models.length > 0) {
        curation.models = models;
        const modelParams = sanitizePlatformPiModelParams(rawModelParams, models);
        if (modelParams)
            curation.modelParams = modelParams;
    }
    // 超管保存时见过的候选集（N-75）：Pi 池一个都不勾时也照记——空池本身不会被并入（不开放不翻成开放），
    // 但超管之后再勾上几个时，基线仍是他当初见过的那一份。
    const reviewedModels = sanitizeModelIdList(rawReviewed);
    if (reviewedModels)
        curation.reviewedModels = reviewedModels;
    return curation;
}
/** Parse persisted/wire platform Pi curation. v1 is migrated in-memory to v2. */
export function parsePlatformPiCuration(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return undefined;
    const value = raw;
    const models = sanitizeModelIdList(value.models);
    const reviewedModels = sanitizeModelIdList(value.reviewedModels);
    if (!models?.length)
        return {
            version: 2,
            editedAt: typeof value.editedAt === "string" ? value.editedAt : new Date(0).toISOString(),
            editedBy: typeof value.editedBy === "string" ? value.editedBy : "system",
            ...(reviewedModels ? { reviewedModels } : {}),
        };
    const parsed = buildPlatformPiCuration(models, typeof value.editedBy === "string" ? value.editedBy : "system", value.modelParams, reviewedModels);
    if (typeof value.editedAt === "string" && value.editedAt)
        parsed.editedAt = value.editedAt;
    return parsed;
}
/**
 * 公开治理快照工具策展子集的唯一构建器（红线 #9）。
 * 只登记零凭据字段；`group` / `webSearchProviders` / `xSearch` 未登记 → 静默丢弃。
 * 生图/生视频按 modelId 去重（公开面没有 group，同 id 多行只保留首见）。
 */
export function buildPlatformModelGovernanceToolCuration(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return undefined;
    const input = raw;
    const out = {};
    const modelCallModels = sanitizeModelIdList(input.modelCallModels);
    if (modelCallModels)
        out.modelCallModels = modelCallModels;
    const visionModels = sanitizeScenarioModelRows(input.visionModels);
    if (visionModels.length > 0)
        out.visionModels = visionModels;
    const documentModels = sanitizeScenarioModelRows(input.documentModels);
    if (documentModels.length > 0)
        out.documentModels = documentModels;
    const videoModels = sanitizeScenarioModelRows(input.videoModels);
    if (videoModels.length > 0)
        out.videoModels = videoModels;
    const imageFallbackChain = [];
    const seenFallbackIds = new Set();
    if (Array.isArray(input.imageFallbackChain)) {
        for (const v of input.imageFallbackChain) {
            const id = cleanId(v);
            if (!id || seenFallbackIds.has(id))
                continue;
            seenFallbackIds.add(id);
            imageFallbackChain.push(id);
        }
    }
    if (imageFallbackChain.length > 0)
        out.imageFallbackChain = imageFallbackChain;
    const imageGenModels = sanitizePublicMediaRows(input.imageGenModels, "image");
    if (imageGenModels.length > 0)
        out.imageGenModels = imageGenModels;
    const videoGenModels = sanitizePublicMediaRows(input.videoGenModels, "video");
    if (videoGenModels.length > 0)
        out.videoGenModels = videoGenModels;
    return Object.keys(out).length > 0 ? out : undefined;
}
function sanitizePublicMediaRows(raw, kind) {
    const out = [];
    const seen = new Set();
    if (!Array.isArray(raw))
        return out;
    for (const it of raw) {
        const row = it;
        const rawModelId = cleanId(row.modelId);
        const modelId = rawModelId ? (canonicalizeGrokMediaModelId(rawModelId) ?? rawModelId) : undefined;
        const mediaContract = modelId ? getGrokMediaModelContract(modelId) : undefined;
        if (!modelId)
            continue;
        if (kind === "image" && mediaContract?.kind === "video")
            continue;
        if (kind === "video" && mediaContract?.kind !== "video")
            continue;
        if (seen.has(modelId))
            continue;
        seen.add(modelId);
        const entry = {
            modelId,
            scenario: cleanId(row.scenario) ?? "",
        };
        const unsupportedParams = sanitizeStringList(row.unsupportedParams);
        if (unsupportedParams)
            entry.unsupportedParams = unsupportedParams;
        const paramSchema = resolveCuratedMediaParamSchema(modelId, row.paramSchema);
        if (paramSchema)
            entry.paramSchema = paramSchema;
        const paramDefaults = sanitizeParamDefaultsForSchema(row.paramDefaults, paramSchema);
        if (paramDefaults)
            entry.paramDefaults = paramDefaults;
        out.push(entry);
    }
    return out;
}
/** 公开治理快照里的工具线政策子集反序列化 guard（红线 #9）：走唯一构建器。 */
function parseGovernanceToolCuration(raw) {
    return buildPlatformModelGovernanceToolCuration(raw);
}
/** Parse a public policy snapshot at the network boundary. */
export function parsePlatformModelGovernanceSnapshot(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return undefined;
    const value = raw;
    if (value.version !== 1)
        return undefined;
    const brainCuration = parsePlatformBrainCuration(value.brainCuration);
    const brainPriority = parseBrainPriorityList(value.brainPriority);
    const piCuration = parsePlatformPiCuration(value.piCuration);
    const toolCuration = parseGovernanceToolCuration(value.toolCuration);
    if (!brainCuration && !brainPriority && !piCuration && !toolCuration)
        return { version: 1 };
    return {
        version: 1,
        ...(brainCuration ? { brainCuration } : {}),
        ...(brainPriority ? { brainPriority } : {}),
        ...(piCuration ? { piCuration } : {}),
        ...(toolCuration ? { toolCuration } : {}),
    };
}
/**
 * 从一组模型推导 role mapping（opus/sonnet/haiku/default → modelId）。
 * 按 id 关键字启发式匹配（opus/pro → opus；sonnet/mid/-chat → sonnet；
 * haiku/flash/lite/mini → haiku）。未命中的角色回落到 default（首个模型）。
 * 主脑组的「推荐默认参数」与员工面 derive 共用此单一实现（move from AIModelTab 2026-06-01）。
 */
export function deriveRoleMapping(entries) {
    const map = {};
    for (const e of entries) {
        const id = e.modelId.toLowerCase();
        if (!map.opus && (id.includes("opus") || id.includes("pro") || id.includes("4-pro")))
            map.opus = e.modelId;
        if (!map.sonnet && (id.includes("sonnet") || id.includes("mid") || id.endsWith("-chat")))
            map.sonnet = e.modelId;
        if (!map.haiku && (id.includes("haiku") || id.includes("flash") || id.includes("lite") || id.includes("mini")))
            map.haiku = e.modelId;
    }
    map.default = entries[0]?.modelId;
    map.opus = map.opus ?? map.default;
    map.sonnet = map.sonnet ?? map.default;
    map.haiku = map.haiku ?? map.default;
    return map;
}
/**
 * 主脑（anthropic）组的「推荐默认参数」（2026-06-01）：admin 打开未配置的主脑组时
 * 直接预填，而非「不预设」。defaultModel = opus 角色映射到的最强模型；effort = high。
 */
export function recommendedBrainDefaults(entries) {
    const roleMapping = deriveRoleMapping(entries);
    const defaultModel = roleMapping.opus ?? roleMapping.default ?? entries[0]?.modelId;
    return { roleMapping, defaultModel, effort: "high" };
}
/**
 * Full role mapping for a user-picked MAIN model within a brain group — the
 * single builder for the "pick ONE model → invisible same-endpoint tiers" flow
 * (2026-06 model-module simplify). The chat picker no longer exposes the 4-role
 * grid; selecting a model derives the whole mapping here and stores it.
 *
 * `default` is forced to the picked main model; the opus/sonnet/haiku tiers come
 * from the group's own catalog via `deriveRoleMapping` (same endpoint by
 * construction — never a hardcoded cross-provider id that could drift or 404).
 * Every role falls back to the main model itself so no role is ever left
 * undefined: an undefined role would let the CLI fall back to its built-in alias
 * (e.g. claude-haiku) and fire a background call to a non-Claude endpoint → 404.
 * Worst case (single-model custom endpoint with no cheaper variant) every role
 * resolves to the main model — valid on that one endpoint.
 *
 * `[1m]` baking stays in workspace-config (capability fact): opus/sonnet get it,
 * haiku never does (haiku has no 1M variant — sdk-learnings §42).
 */
export function roleMappingForMainModel(groupEntries, mainModel) {
    const base = mainModel.replace(/\[[^\]]*\]/g, "");
    const d = deriveRoleMapping(groupEntries);
    return {
        default: base,
        opus: d.opus ?? base,
        sonnet: d.sonnet ?? base,
        haiku: d.haiku ?? base,
    };
}
/** Role aliases the renderer / cold-start defaults emit instead of a concrete id.
 *  `opusplan` is excluded — it's an SDK-native alias that must pass through. */
export const BRAIN_ROLE_ALIASES = new Set(["opus", "sonnet", "haiku", "default"]);
/** 名字关键字排名（价格并列时的 tie-break）：haiku 端=0（便宜）· 中位=1 · opus 端=2（贵）。 */
function tierNameRank(modelId) {
    const s = modelId.toLowerCase();
    if (s.includes("haiku") || s.includes("flash") || s.includes("lite") || s.includes("mini"))
        return 0;
    if (s.includes("opus") || s.includes("pro"))
        return 2;
    return 1; // sonnet / mid / -chat / 未知 → 中位
}
/**
 * 从一组主脑条目按**能力 + 价格**推导 opus/sonnet/haiku 三档（替换 deriveRoleMapping
 * 纯名字启发式，bug② 根治）。
 *
 * 语义：
 *  - 无 pricing 数据（全组都没有 `pricing.inputPer1m`）→ 回落 `deriveRoleMapping` 名字关键字
 *    （零回归，纯名字组照旧）。
 *  - 有 pricing 数据 → 只对**带价条目**按 `inputPer1m` 升序排（价格并列用 `tierNameRank`
 *    tie-break，让同价里名字更像 haiku 的排前、更像 opus 的排后）：
 *      · opus 档 = 最贵条目；
 *      · haiku 档 = 最便宜且 `capabilities.supportsToolUse !== false` 的条目（不支持工具的最便宜
 *        条目不当小档，避免小活丢工具能力；全组都不支持则退回最便宜）；
 *      · sonnet 档 = 中位条目（`floor((n-1)/2)`）；
 *      · default = 最便宜条目（消费端 resolveTierMapping 会用主模型覆盖 default）。
 *    这样 doubao-pro / deepseek-pro 若价格便宜就不会再因名字带 "pro" 被误判进 opus 档。
 *
 * 纯函数、确定性。入参应为**已过 L2 治理**（disabled 模型不进档位）的组内主脑条目。
 * 零账号行（`isNoCredentialRow`，llm-gateway#354 起才出现）不是候选——档位落上去必 503；
 * 维护中 / 限流等暂时不可用的行照旧参与，避免一次限流让三档来回跳。
 */
export function deriveTierFromCatalog(entries) {
    const served = dropNoCredentialRows(entries);
    if (served.length === 0)
        return {};
    const priced = served.filter((e) => Number.isFinite(e.pricing?.inputPer1m));
    if (priced.length === 0)
        return deriveRoleMapping(served); // 无价 → 名字启发式（旧行为）
    const sorted = [...priced].sort((a, b) => {
        const pa = a.pricing.inputPer1m;
        const pb = b.pricing.inputPer1m;
        if (pa !== pb)
            return pa - pb; // 便宜 → 贵
        return tierNameRank(a.modelId) - tierNameRank(b.modelId);
    });
    const cheapest = sorted[0];
    const opus = sorted[sorted.length - 1].modelId; // 最贵
    const sonnet = sorted[Math.floor((sorted.length - 1) / 2)].modelId; // 中位
    const haikuEntry = sorted.find((e) => e.capabilities?.supportsToolUse !== false) ?? cheapest;
    return { opus, sonnet, haiku: haikuEntry.modelId, default: cheapest.modelId };
}
const stripAllTags = (m) => m.replace(/\[[^\]]*\]/g, "");
/** concrete id 判定：非空且（strip 全部 [...] 后）不是裸角色别名。 */
function isConcreteTierId(v) {
    return !!v && !BRAIN_ROLE_ALIASES.has(stripAllTags(v));
}
/**
 * claude 链路可路由的 base id 集合 —— `resolveTierMapping` `routableIds` 入参的**纯 catalog
 * 版底线**（拿不到 store 的调用方用）。
 *
 * 判据 = 「catalog 里有该 base id 的 `anthropic_messages` 主脑行」。这不是启发式：claude 链的
 * 凭证查找键就是 (base id, `anthropic_messages`)（desktop `resolveModelCredentials` Step 2
 * `catalog.find(e => e.modelId === base && e.protocol === protocol)`），查不到那一行 = 结构上
 * 拿不到凭证，会话必然走 `no_catalog_row`。
 *
 * **刻意不看 `available`**：可用性是运行时瞬时态（不变量 #9），凭证查找本身不消费它；在这里
 * 多筛一层会把「产品其实跑得起来、只是网关此刻标了维护」的档位误降级。
 * **唯一例外是零账号行**（`isNoCredentialRow`）：那不是瞬时态，是「这一格背后已没有账号」——
 * llm-gateway#354 之前这种行根本不下发，这里把它当不存在，与改动前逐字节等价。
 *
 * 凭证级的更严版本在 desktop（`listRoutableBrainModelIds`：再验组已开通 + key 可解密）。
 * 能拿到 store 的调用方传那个；本函数只保证「不发一个 catalog 里根本没有 claude 链路由的 id」。
 */
export function claudeChainRoutableBaseIds(catalog) {
    // 成员判据直接用 `isBrainCatalogEntry`（与 `resolveTierMapping` 自己筛 groupEntries 同一处），
    // 不另写一份 protocol 比较；`brainLegEntries` 是它在 `ModelCatalogEntry` 容器上的包装，
    // 入参类型对不上，这里不为复用它而放宽那个导出签名。
    //
    // `!entry` / `typeof modelId !== "string"` **不是** TS 类型上的死分支：这份 catalog 的来源是
    // `readJson<ModelCatalogEntry[]>(store, "model_catalog_v1", [])`——持久化 JSON 上的 `as` 断言，
    // 运行时并没有 guard（不变量 #7：不用 `as` 代替校验）。姐妹函数 `collectRoutableBrainModels`
    // （desktop `model-config.ts`）就是同样四道判空，本函数与它对齐。
    // 少了这两道时，`group === undefined`（企业冷启动，正是本修复的触发面）+ 畸形行会让
    // `resolveBrainSelection` 直接抛 TypeError，而它在 agent-bridge 里**不在**那个把失败收成
    // `enterpriseCredentialGap` 的 try 内——异常会穿出 sendMessage。
    const ids = new Set();
    for (const entry of catalog) {
        if (!entry || typeof entry !== "object" || typeof entry.modelId !== "string")
            continue;
        if (!isBrainCatalogEntry(entry) || isNoCredentialRow(entry))
            continue;
        const base = stripAllTags(entry.modelId);
        if (base)
            ids.add(base);
    }
    return ids;
}
/**
 * 找出档位表里「目标不在主脑策展白名单」的条目（REQ-293 源头闸，纯函数）。
 *
 * 用在 admin-portal 的档位表 PUT 上：超管把某一档指向一个**没勾进主脑清单**的模型时，
 * 那个模型的主脑腿会在下发链上被剪掉，而档位还指着它 → 员工端登录后第一条消息被拒发，
 * 弹「企业模型配置正在同步，请稍候重试。」且永不恢复。这里把违规条目挑出来让路由 400 顶回去。
 *
 * **白名单为空 / 未配置 = 全放行**（未策展的 footgun 防护，与 `applyBrainCuration`、
 * `applyModelSelection` 同一口径——别在这里发明「空 = 全禁」）。
 * 比较一律走 base id（`stripModelSuffixTag`），与档位表构建器和消费端同键。
 */
export function collectUncuratedTierTargets(rawTiers, allowedBrainModels) {
    return fallbackUncuratedTierTargets(rawTiers, allowedBrainModels).fallbacks;
}
/**
 * 把档位表里「目标不在主脑策展白名单」的那几档**回落为自动**（从载荷里删掉），其余原样保留
 * （B-54 / leave1206/dosia#3500，纯函数）。
 *
 * 为什么是回落而不是整次拒绝：REQ-293 起档位 PUT 对这种条目 400 顶回去，而目标没勾上的原因
 * 常常是超管改不了的——上游维护中的模型勾选框 disabled、界面上根本没有下拉框的非主脑行。
 * 于是只要网关上有一个模型维护/下线，整张档位表（连同同一次保存的其余映射）就永远存不进去。
 * 回落与同页「默认主脑优先级」的「已下线，保存时移除」同口径，且与运行时本来的行为一致：
 * 缺档对消费端的含义就是「自动」（`resolveTierMapping` 落回派生）。
 *
 * 语义：
 *  - 白名单为空 / 未配置 = 全放行（与 `collectUncuratedTierTargets` 同一 footgun 防护），`tiers` 原样返回；
 *  - 非对象 / 数组形状原样返回（脏值由唯一构建器 `buildModelTierTable` 另行剔除，这里不越权）；
 *  - 只动被回落的那一档：同一行的其余档、别的键（含非字符串脏值）一律不碰，有效映射语义不变；
 *  - 比较走 base id（`stripModelSuffixTag`），与构建器和消费端同键。
 */
export function fallbackUncuratedTierTargets(rawTiers, allowedBrainModels) {
    const allowed = new Set((allowedBrainModels ?? []).map((id) => stripModelSuffixTag(String(id))));
    if (allowed.size === 0)
        return { tiers: rawTiers, fallbacks: [] };
    if (!rawTiers || typeof rawTiers !== "object" || Array.isArray(rawTiers))
        return { tiers: rawTiers, fallbacks: [] };
    const fallbacks = [];
    const tiers = {};
    for (const [mainModel, row] of Object.entries(rawTiers)) {
        if (!row || typeof row !== "object" || Array.isArray(row)) {
            tiers[mainModel] = row;
            continue;
        }
        const nextRow = { ...row };
        for (const tier of ["opus", "sonnet", "haiku"]) {
            const target = nextRow[tier];
            if (typeof target !== "string" || !target.trim())
                continue;
            if (!allowed.has(stripModelSuffixTag(target.trim()))) {
                fallbacks.push({ mainModel: mainModel.trim(), tier, target: target.trim() });
                delete nextRow[tier];
            }
        }
        tiers[mainModel] = nextRow;
    }
    return { tiers, fallbacks };
}
/**
 * 圈定之后把「点名了已被剪掉的模型」的档位三元组清干净（REQ-293 上游修，纯函数）。
 *
 * 为什么需要它（2026-09-10 实证的完整因果链）：
 *  1. 网关按 org bundle 里**真实的 anthropic 原生腿**派生档位三元组，并盖在主模型那一行上
 *     （`StampBundleTierDefaults`，只拿 `anthropic_messages && Available` 的行当候选）——这一步没错，
 *     公开清单实拉可见 `deepseek-v4-pro` 确有 `anthropic_messages` 腿。
 *  2. DOSIA 服务端按超管/企业的圈定，把没被勾进主脑清单的模型**剪掉主脑腿**
 *     （`applyBrainCuration` / `applyModelSelection`）——`deepseek-v4-pro` 的 anthropic 行就是这么没的
 *     （它的 openai 行走工具线、`toolModels` 未配 → 放行留下，所以模型本身还在清单里）。
 *  3. **剪裁没有回头动已经盖上的三元组** → 员工清单里出现「档位指着它、主脑腿却已经没了」的模型
 *     → 客户端凭证按 (base id, `anthropic_messages`) 查不到行 → 会话被拒发。
 *
 * 所以这不是网关的锅，也不是「客户端该更宽容」——是**圈定与随行三元组两步没有互相看**。
 * 客户端那道 `routableIds` guard 仍然保留（纵深防御：清单可能来自旧版服务端），但源头在这里收：
 * 剪完模型行之后，同一 seam 里立刻让三元组与剩下的行重新自洽。
 *
 * 语义：逐 (行 × 组 × 档) 检查目标是否仍有 claude 链路由；没有就**删掉那一档**（不重新派生——
 * 服务端再派生一次就是第二真相源，红线 #9；缺档对客户端的含义已经是「网关没有答案」，
 * 它会落回本地价格派生，那正是我们要的）。三档全被删 → 整个 `tierDefaultsByGroup` 去掉
 * （空对象与缺失同义，见 `parseGatewayTierDefaults`）。无三元组的行原样返回同一个对象引用。
 */
export function stripUnroutableTierDefaults(entries) {
    const routable = claudeChainRoutableBaseIds(entries);
    return entries.map((entry) => {
        const byGroup = entry?.tierDefaultsByGroup;
        if (!byGroup || typeof byGroup !== "object")
            return entry;
        const nextByGroup = {};
        for (const [group, tiers] of Object.entries(byGroup)) {
            if (!tiers || typeof tiers !== "object")
                continue;
            const kept = {};
            for (const tier of ["opus", "sonnet", "haiku"]) {
                const target = tiers[tier];
                if (typeof target !== "string" || !target.trim())
                    continue;
                if (routable.has(stripAllTags(target.trim())))
                    kept[tier] = target;
            }
            if (Object.keys(kept).length > 0)
                nextByGroup[group] = kept;
        }
        if (Object.keys(nextByGroup).length === 0) {
            const { tierDefaultsByGroup: _dropped, ...rest } = entry;
            return rest;
        }
        return { ...entry, tierDefaultsByGroup: nextByGroup };
    });
}
/**
 * **唯一档位解析器**（PR-C）—— 全消费路径（对话 env 注入 / --model 别名 / 后台定时）
 * 经此单点解析 opus/sonnet/haiku 三档。纯函数。
 *
 * 每档优先级链（`default` 恒 = 主模型 base）：
 *   1. `lockRoleMapping ? ∅ : employeeOverride[t]` —— 员工显式覆盖（admin 锁定时跳过）；
 *   2. `?? tierTable[mainModel][t]` —— admin 档位表（`routableIds` 给出时做凭证可路由 guard，
 *      不可路由则跳过）；
 *   2.5 `?? catalog[mainModel].tierDefaultsByGroup[group][t]` —— **网关下发的档位默认**
 *      （`tier_defaults`，2026-08）。网关按 org 真实账号逐 (组 × 模型) 派生。
 *      排在 admin 表**之下**（人写的意图赢过自动派生）、本地派生**之上**（网关看得到价格与账号，
 *      客户端 catalog 连 pricing 都没有）。同样过 `routableIds` guard。
 *      ⚠️ 接缝 §5.7 写的「bundle 腿点名的档位对这把 key **天然可路由**」在 2026-09-09
 *      admin-kehan 真机被证伪（`deepseek-default` 组 opus 档 = `deepseek-v4-pro`，而该模型
 *      在同一份 bundle 里根本没有 `anthropic_messages` 行）。所以 guard 不是冗余：
 *      **调用方必须给 `routableIds`**（拿得到 store 的给凭证级集合，拿不到的至少给
 *      `claudeChainRoutableBaseIds(catalog)`），否则这一级会发出一个必然 `no_catalog_row` 的 id。
 *   3. `?? deriveTierFromCatalog(groupEntries)[t]` —— 能力/价格感知派生（候选仅**同组**——
 *      跨组是 admin 显式意图，只经档位表，不自动发生）；
 *   4. `?? mainModel` —— 强制回填 concrete 主模型（haiku 档 404 铁律：空档/裸别名结构性不可能）。
 *
 * **第 4 级是铁律不是配置**：即使 2.5 上线也绝不能让它回落成空。空档位 → CLI 落回内建的
 * haiku 家族 id → 非 Claude 组 404（deepseek-default 组近 30 天 12 次的成因）。同理，
 * `tier_defaults` 缺失与空对象都只表示「网关没有答案」，绝不表示「清空档位」。
 *
 * candidate 池 = L2 输出：`catalog` 入参应由调用方先经 `resolveAvailableModels` 过滤
 * （disabled 模型不进档位）。返回 `collapsed=true` 当三档全塌成主模型（该组无更便宜/更强档位
 * → 小任务按主模型计费，消费端发 amber info，非 bug 不红色）。
 */
export function resolveTierMapping(input) {
    const base = stripAllTags(input.mainModel);
    // 零账号行在档位解析里一律视为不存在（llm-gateway#354 之前它们不下发）：组内派生候选、
    // 员工覆盖的组成员自愈、主模型行上的网关三元组，三处都从同一份「有供给的行」读。
    const catalog = dropNoCredentialRows(input.catalog);
    const groupEntries = input.group
        ? catalog.filter((e) => isBrainCatalogEntry(e)
            && (e.chosenGroup?.name === input.group
                || e.availableGroups?.some((g) => g?.name === input.group)))
        : [];
    const derived = deriveTierFromCatalog(groupEntries);
    const tableTiers = input.tierTable?.tiers?.[base];
    // 网关下发的档位默认（`tier_defaults`）—— 按 (组, 主模型) 取，两个键都必须对上：
    //   · 组：三元组是**按组派生**的（网关按 chosen_group 分桶），拿 A 组的三元组喂 B 组的会话
    //     就是跨组漂移。`input.group` 缺失时不猜（个人版 / 冷启动无组）→ 无答案。
    //   · 主模型：网关按行做了「主脑即上限」特化（选 sonnet 的人，opus 档也是 sonnet，不静默升贵），
    //     所以必须取**主模型自己那一行**的三元组，不能随便挑组里任一行。
    // 取不到 → undefined = 「网关没有答案」，落回下面的派生/主模型回填，绝不当作「清空档位」。
    const gatewayTiers = input.group
        ? catalog.find((e) => isBrainCatalogEntry(e) && stripAllTags(e.modelId) === base && e.tierDefaultsByGroup?.[input.group])?.tierDefaultsByGroup?.[input.group]
        : undefined;
    // 组成员自愈校验的候选集（红线 #7 类级根治）—— 当前组 catalog 里所有模型 base id。
    // 用整组集合（不限主脑）从宽判定「是否属于本组」：只拦跨组漂移（历史粗分组时代把
    // 别品牌 id 固化进本组，如 glm 组 haiku=doubao-seed-2.0-lite），不误伤组内合法候选。
    const groupModelIds = new Set((input.group
        ? catalog.filter((e) => e.chosenGroup?.name === input.group
            || e.availableGroups?.some((g) => g?.name === input.group))
        : []).map((e) => stripAllTags(e.modelId)));
    // 仅当有组 + 组内有已知模型时才做成员校验；无 catalog 数据无从判定 → 信任员工覆盖
    // （退化但安全，与个人版/无企业下发的既有语义一致）。
    const canValidateGroup = !!input.group && groupModelIds.size > 0;
    const healed = [];
    const pick = (t) => {
        // 1. 员工显式覆盖（admin 锁定时跳过）—— 采信前先做组成员自愈校验：持久化的档位候选若
        //    不属于当前组的模型集（catalog 演进后从未复核的僵尸映射），视为失效 → 丢弃并当场
        //    重派生（步骤 3）/ 回落主模型（步骤 4），杜绝幽灵模型 404 拖死整 turn。
        if (!input.lockRoleMapping) {
            const emp = input.employeeOverride?.[t];
            if (isConcreteTierId(emp)) {
                if (!canValidateGroup || groupModelIds.has(stripAllTags(emp)))
                    return emp;
                healed.push({ tier: t, stale: emp });
            }
        }
        // 2. admin 档位表（凭证可路由 guard）
        const tv = tableTiers?.[t];
        const dv = derived[t];
        const gv = gatewayTiers?.[t];
        if (isConcreteTierId(tv)) {
            if (!input.routableIds || input.routableIds.has(stripAllTags(tv)))
                return tv;
            console.warn(`[TierTableUnroutable] group=${input.group ?? "?"} tier=${t} target=${tv} → fallback=${isConcreteTierId(gv) ? gv : isConcreteTierId(dv) ? dv : base || "(none)"}`);
        }
        // 2.5 网关下发的档位默认（`tier_defaults`）—— 排在 admin 档位表**之下**、本地派生**之上**。
        //     这是 404 的正解：Agent SDK 干隐藏小活时发写死的 claude-* id，网关对原生 anthropic 分发器
        //     的组（deepseek/glm/kimi）不做转译 → 直接 404。网关按 org 真实账号派生这个三元组，
        //     DOSIA 照它发 concrete id 就不再撞。admin 显式表仍然赢：那是人写的意图，网关是自动派生。
        if (isConcreteTierId(gv)) {
            if (!input.routableIds || input.routableIds.has(stripAllTags(gv)))
                return gv;
            // bundle 派生的档位在网关侧「构造上可路由」，但 org 圈定/策展可能在下发后又剔掉了那个模型。
            // 与 admin 表同一姿态：不可路由就跳过，落回派生/主模型，绝不发一个明知路由不到的 id。
            console.warn(`[GatewayTierDefaultUnroutable] group=${input.group ?? "?"} tier=${t} target=${gv} → fallback=${isConcreteTierId(dv) ? dv : base || "(none)"}`);
        }
        // 3. 能力/价格派生
        if (isConcreteTierId(dv))
            return dv;
        // 4. 强制回填主模型（404 铁律）；主模型为空时留 undefined（退化场景，消费端各自兜）
        return base || undefined;
    };
    const opus = pick("opus");
    const sonnet = pick("sonnet");
    const haiku = pick("haiku");
    const mapping = { opus, sonnet, haiku, default: base || undefined };
    // 自愈信号（稳定 tag，可 grep）—— 日志面保留；结构化事件经 onSelfHeal 送企业飞书告警。
    for (const h of healed) {
        console.warn(`[RoleMappingSelfHeal] group=${input.group ?? "?"} tier=${h.tier} stale=${h.stale} → ${mapping[h.tier] ?? "(none)"}`);
    }
    if (healed.length > 0 && input.onSelfHeal) {
        try {
            input.onSelfHeal(healed.map((h) => ({
                group: input.group,
                tier: h.tier,
                staleModel: h.stale,
                healedModel: mapping[h.tier],
            })));
        }
        catch {
            // 通知回调失败绝不影响档位解析结果
        }
    }
    const collapsed = !!base && opus === base && sonnet === base && haiku === base;
    return { mapping, collapsed };
}
/**
 * Claude 引擎此刻能拿来当**主脑**的模型（base id 集）——档位锚点（{@link resolveClaudeTierAnchor}）与
 * 容量降级候选（桌面 `collectRoutableBrainModels` 产出、core `deriveFallbackModel` 消费）共用的唯一判据，纯函数。
 *
 * = Claude 链可路由（凭证级 `routableIds` 非空时用它，否则清单级 {@link claudeChainRoutableBaseIds}）
 *   ∩ 清单里有 Claude 引擎腿的模型（{@link isClaudeLegRow}：网关下发的上游原生 anthropic 行，或员工自建接入点的主脑行）。
 *
 * 网关翻译出来的 anthropic 行（gpt / gemini / grok，`translated === true`）本地路由能转发，所以仍留在档位 guard 的
 * 可路由集里（admin 档位表可以把小活指过去）；但它们不是 Claude 引擎的主脑（2026-10-08 产品经理拍板：一个模型能不能进
 * Claude 引擎，只由网关给它的原生协议决定）——主脑锚点与临时顶替主脑的降级都不落上去（B-327）。`translated` 缺席的行
 * 判不了，不算（与主脑池同一 fail-closed 口径）。
 */
export function claudeBrainRoutableBaseIds(input) {
    const routable = input.routableIds && input.routableIds.size > 0
        ? input.routableIds
        : claudeChainRoutableBaseIds(input.catalog);
    const brain = new Set();
    if (routable.size === 0)
        return brain;
    for (const entry of input.catalog) {
        // 落盘 JSON 来源，运行时没 guard（同 claudeChainRoutableBaseIds 头注）。
        if (!entry || typeof entry !== "object" || typeof entry.modelId !== "string")
            continue;
        if (isNoCredentialRow(entry) || !isClaudeLegRow(entry, input.userCustomGroups))
            continue;
        const base = stripAllTags(entry.modelId);
        if (base && routable.has(base))
            brain.add(base);
    }
    return brain;
}
/**
 * Claude 引擎会话派生三档用的**锚点**（主模型 + 组）——唯一判定点，交互会话（core
 * `resolveWorkspaceConfig`）与后台 / 定时会话（desktop `resolveBrainRoleMapping`）共用，纯函数。
 *
 * 平时锚点就是激活组的主脑（组内档位表不随会话模型漂移）。但激活组是**全局**的：员工在 pi 引擎上
 * 选了只有 pi 接口的模型（grok-4.7 等），激活组跟着切走，而钉着自己模型的 Claude 引擎对话还在跑。
 * 拿一个 Claude 链走不了的主脑去派生 Claude 引擎的三档，`resolveTierMapping` 末级回填就把三档全填成它：
 * 隐藏小活与 `model: sonnet` 的分叉技能、以及由三档派生的容量降级链，一起落到本地路由的 404
 * （周审 2026-10-31 B3：zhenglisha / sunmengfei / yeyiting 降级到 grok-4.7 整轮作废，
 * wangkai / caiziyu 的 xlsx、docx、飞书表格技能同一句 404）。
 *
 * 判据只有一条：激活组主脑**可证**不是 Claude 引擎此刻能当主脑的模型、而会话自己的模型**可证**是 → 锚到会话
 * 模型与它在清单里的组（`reanchored: true`，调用方据此丢掉激活组的员工档位覆盖——那是另一个组的档位）。
 * 「能当主脑」的集合单源 {@link claudeBrainRoutableBaseIds}（与容量降级候选同一份）；为空 = 判不了 → 原样返回。
 * 激活组主脑本身能当 Claude 引擎主脑时（同组换模型、跨组钉别的 Claude 链模型）一律不动。
 */
export function resolveClaudeTierAnchor(input) {
    const unchanged = { mainModel: input.mainModel, group: input.group, reanchored: false };
    const mainBase = stripAllTags(input.mainModel);
    const sessionBase = input.sessionModel ? stripAllTags(input.sessionModel) : "";
    if (!mainBase || !sessionBase || sessionBase === mainBase)
        return unchanged;
    const brain = claudeBrainRoutableBaseIds(input);
    if (brain.size === 0 || brain.has(mainBase) || !brain.has(sessionBase))
        return unchanged;
    const sessionRow = input.catalog.find((entry) => !!entry
        && typeof entry.modelId === "string"
        && isBrainCatalogEntry(entry)
        && !isNoCredentialRow(entry)
        && stripAllTags(entry.modelId) === sessionBase);
    return { mainModel: sessionBase, group: sessionRow?.chosenGroup?.name, reanchored: true };
}
/**
 * **云端/机器人档位收编入口**（PR-E, 消费点 #5/#6）—— 从网关 bundle 的原始 wire `models[]`
 * 为主模型解析 SDK 档位 `roleMapping`，喂 SessionConfig.modelRoleMapping → buildProviderEnv
 * → `ANTHROPIC_DEFAULT_{OPUS,SONNET,HAIKU}_MODEL`。收编前云端/bot 会话零档位 env → CLI 小活
 * （标题/摘要/网页抽取）回落内置 `claude-haiku` 别名，对非 Claude 网关 404 或按主模型计费
 * （成本 gap）。收编后小活走同组更便宜档。agent-host / feishu-bot 共用（apps 不互相 import，
 * 共享逻辑经 shared，红线 #2）。
 *
 * 单一真相源（红线 #9）：wire → catalog 转换走唯一解析器 `toBundle`（bundle 端点既有的
 * camelCase 映射，含 `pricing`/`capabilities`/`chosenGroup`）——派生用真价格/能力
 * （`deriveTierFromCatalog`），无价条目才回落名字启发式；候选限主模型所在组
 * （`resolveTierMapping` 既有 group 过滤）。跨组档位只经 admin tierTable（guard = bundle 内
 * 可路由；消费端须有 model-router 按模型换 key，否则不要传 tierTable）。
 *
 * **失败隔离**：本函数**绝不 throw**——wire 形状异常（如缺 `chosen_group`）→ 返回空 mapping
 * （= 零档位 env，收编前现状），凭证解析路径不受档位收编影响。
 */
export function resolveBundleTierMapping(input) {
    try {
        // 零账号行（llm-gateway#354）当不存在：主模型定组、组内派生、可路由 guard 同一份行。
        const catalog = dropNoCredentialRows(toBundle({ models: input.models }).models);
        const base = stripAllTags(input.mainModel);
        const main = catalog.find((m) => m.modelId === base) ?? catalog.find((m) => m.modelId.includes(base));
        return resolveTierMapping({
            mainModel: base,
            group: main?.chosenGroup?.name,
            catalog,
            tierTable: input.tierTable,
            // guard：bundle 里**有 Anthropic 原生腿**的 id 才算可路由。
            //
            // REQ-293 同类点（2026-09-10 补）：这里原来是 `catalog.map(m => stripAllTags(m.modelId))`
            // ——协议盲的，「id 在 bundle 里出现过」就算数。可这三档最终落进
            // `ANTHROPIC_DEFAULT_{OPUS,SONNET,HAIKU}_MODEL`（agent-host / feishu-bot /
            // apps/web server-model-call-context 三条腿都是 `provider="claude-custom"`），
            // 只有 openai 行的模型在那个面上根本不接。于是 desktop 那条路修好之后，云端定时任务、
            // 飞书机器人、服务端 modelCall 的隐藏小活（起标题/摘要/网页抽取）还会被派去
            // `deepseek-v4-pro` 这种打不通的 id ——**同一个根因的另一条腿**。
            // 判据换成与 desktop 侧同源的 `claudeChainRoutableBaseIds`（`isBrainCatalogEntry`，
            // 即 `anthropic_messages` 且非工具组非生图）。
            routableIds: claudeChainRoutableBaseIds(catalog),
        });
    }
    catch {
        // 反序列化边界防御（红线 #9）：档位派生失败降级为「无档位 env」（现状），不裸抛。
        return { mapping: {}, collapsed: false };
    }
}
// ── ModelTierTable：单一构建器 + 反序列化 guard（PR-D，红线 #9）───────────
// admin-portal PUT / bootstrap 产出经 buildModelTierTable 唯一构建；下发链每个读盘/
// 读网络的消费端（desktop 落库、session-manager 读、后台定时读）经 parseModelTierTable
// 单点 guard（version/tiers 形状校验），杜绝手搓 object literal 漂移 + 反序列化裸崩。
const isTierValue = (v) => typeof v === "string" && v.trim().length > 0;
/**
 * **唯一构建器**（红线 #9）—— 从原始 tiers 输入构造规范 `ModelTierTable`：
 *  - key/value trim；空 key、三档全空的条目一并剔除（集合语义，空条目无意义）；
 *  - 三档仅收 concrete 非空 string；
 *  - 盖上 version=1 + editedAt(now) + editedBy。
 * admin-portal PUT 路由与任何服务端产出点都必须接此构建器，不复制字段。
 */
export function buildModelTierTable(rawTiers, editedBy) {
    const tiers = {};
    for (const [k, v] of Object.entries(rawTiers ?? {})) {
        const key = String(k).trim();
        if (!key || !v || typeof v !== "object")
            continue;
        const row = {};
        for (const t of ["opus", "sonnet", "haiku"]) {
            const val = v[t];
            if (isTierValue(val))
                row[t] = val.trim();
        }
        if (row.opus || row.sonnet || row.haiku)
            tiers[key] = row;
    }
    return { version: 1, tiers, editedAt: new Date().toISOString(), editedBy };
}
/**
 * **反序列化边界 guard**（红线 #9）—— `JSON.parse` 读盘/网络/bundle 后索引前先过此单点。
 * 非对象 / version≠1 / tiers 缺失或非对象 → `undefined`（消费端各自回落派生，绝不裸崩）。
 * 逐条 sanitize tiers（同 buildModelTierTable 的三档 concrete 语义），剔除脏条目。
 */
export function parseModelTierTable(raw) {
    if (!raw || typeof raw !== "object")
        return undefined;
    const obj = raw;
    if (obj.version !== 1 || !obj.tiers || typeof obj.tiers !== "object")
        return undefined;
    const built = buildModelTierTable(obj.tiers, typeof obj.editedBy === "string" ? obj.editedBy : "");
    // 保留原始 editedAt（构建器会盖 now）—— guard 不是重新编辑，只是清洗。
    return { ...built, editedAt: typeof obj.editedAt === "string" ? obj.editedAt : built.editedAt };
}
/**
 * **档位表 → 组供给「可路由校验」**（服务端 seam 唯一实现，红线 #9）。
 *
 * 档位表管**小任务计费路由**（哪个更便宜档跑标题/摘要/网页抽取），主脑策展/圈定管**员工可
 * 选主模型可见面**——前者不受后者过滤。可路由性的真相 = org 网关的**原始供给**（策展前的
 * bundle 模型集），只有服务端看得到；故校验搬到 bundleToPushEvent 单 seam（旧客户端 routableIds
 * guard 只看到策展/圈定后的子集，会把 admin 配的合法跨档目标误杀——2026-07-20 kimi-k3 员工端
 * 「无更便宜档位」banner 根因）。
 *
 * 语义：对每个 `tiers[base]` 的 opus/sonnet/haiku，目标 id `stripModelSuffixTag` 后不在
 * `supplyBaseIds` → 删该档；三档全空的条目删 key。**base key 本身不校验**（主模型不在供给只是
 * 没人会选它，条目留着无害）。结果空表返回**带空 tiers 的表**（保留 version/editedAt/editedBy，
 * 不返回 undefined——空表即「无档位」的清空信号，须原样传播到 desktop 删 key，见
 * enterprise-bundle-runtime）。入参非对象（防御）→ undefined。
 */
/**
 * {@link filterTierTableToSupply} 的供给集：行集合的 base id，**去掉零账号行**（`isNoCredentialRow`）。
 *
 * 判据刻意协议盲、可用性盲（2026-07-20 kimi-k3 事故背书：被策展剔出但仍可路由的跨档目标必须保留），
 * 维护中 / 限流的行照旧算供给。唯一剔除的是零账号行——llm-gateway#354 前它们根本不在 bundle 里，
 * 档位指过去必 503。同 base 只要还有一行非零账号即算供给。
 */
export function servedSupplyBaseIds(rows) {
    const ids = new Set();
    for (const row of rows) {
        if (!row || typeof row !== "object" || typeof row.modelId !== "string")
            continue;
        if (isNoCredentialRow(row))
            continue;
        const base = stripModelSuffixTag(row.modelId);
        if (base)
            ids.add(base);
    }
    return ids;
}
export function filterTierTableToSupply(tierTable, supplyBaseIds) {
    if (!tierTable || typeof tierTable !== "object")
        return undefined;
    const tiers = {};
    for (const [base, row] of Object.entries(tierTable.tiers ?? {})) {
        const filtered = {};
        for (const t of ["opus", "sonnet", "haiku"]) {
            const target = row?.[t];
            if (isTierValue(target) && supplyBaseIds.has(stripModelSuffixTag(target))) {
                filtered[t] = target;
            }
        }
        if (filtered.opus || filtered.sonnet || filtered.haiku)
            tiers[base] = filtered;
    }
    return { ...tierTable, tiers };
}
/**
 * 档位/组参数的**唯一存储优先级读取器**：员工自配 `brainGroupParams[group]` ??
 * admin 下发 `managedSettings.brainGroupDefaults[group]`。整对象级 ??（与 workspace-config
 * 现行为一致，不做字段级 merge）—— 员工只要为该组存过任意参数，就整体接管，admin 默认
 * 不再参与。三条消费路径（workspace-config env 注入 / resolveBrainModelName --model 解析 /
 * resolveBrainRoleMapping 后台定时）全部经此单点，避免「只读员工自配、漏 admin 下发」的
 * 档位分叉（同一会话两套档位）。
 */
export function resolveEffectiveBrainParams(brainGroupParams, brainGroupDefaults, group) {
    if (!group)
        return undefined;
    return brainGroupParams?.[group] ?? brainGroupDefaults?.[group];
}
// ──────────────────────────────────────────────────────────────────────
// 钱包身份（brain wallet）—— 「这次会话花谁的钱」的单一真相源
// ──────────────────────────────────────────────────────────────────────
/**
 * Claude 个人订阅虚拟组名。**全仓这条字面量只准出现在这一处**，判断走 `brainWalletForGroup`。
 * （desktop / ui 侧此前各写死一份，2026-07-31 接第二个钱包时收敛到本常量。）
 */
export const CLAUDE_OAUTH_GROUP = "claude-oauth";
/** ChatGPT Plus/Pro 个人订阅虚拟组名。同上：字面量只在这里，判断走 `brainWalletForGroup`。 */
export const CHATGPT_OAUTH_GROUP = "chatgpt-oauth";
/** Grok 个人订阅（SuperGrok / X Premium）虚拟组名。同上：字面量只在这里，判断走 `brainWalletForGroup`。 */
export const XAI_OAUTH_GROUP = "xai-oauth";
/**
 * brain group 名 → 钱包身份。**唯一判定入口**（红线#7 类级根治）。
 *
 * 语义与收敛前的 `activeBrainGroup === "claude-oauth"` 逐字等价：`undefined` / `null` /
 * 任何别的组名 → `"gateway"`（原写法对 undefined 同样得 false）。调用点各自保留自己的
 * 「未设置组」分支语义——「未设置」不等于「是网关组」，两者不可合并进本函数。
 */
export function brainWalletForGroup(group) {
    if (group === CLAUDE_OAUTH_GROUP)
        return CLAUDE_OAUTH_GROUP;
    if (group === CHATGPT_OAUTH_GROUP)
        return CHATGPT_OAUTH_GROUP;
    if (group === XAI_OAUTH_GROUP)
        return XAI_OAUTH_GROUP;
    return "gateway";
}
/**
 * Persistence boundary for a model route returned by `resolveBrainModelName`.
 *
 * Gateway and personal-pi (ChatGPT / Grok) routes must be concrete. Claude OAuth
 * is the one backend where a bare opus/sonnet/haiku alias is intentionally
 * executable: the Claude CLI resolves it natively and keeps the user's selected
 * tier. Tagged aliases remain invalid because `sonnet[1m]` is not a bare native
 * CLI route.
 */
export function canPersistResolvedBrainModelRoute(input) {
    const modelId = input.modelId?.trim();
    if (!modelId)
        return false;
    const base = stripAllTags(modelId);
    if (!BRAIN_ROLE_ALIASES.has(base))
        return true;
    return input.brainWallet === CLAUDE_OAUTH_GROUP
        && modelId === base
        && base !== "default";
}
/** 免计费的个人订阅钱包（员工自己的会员）——与「公司网关」二分。 */
export function isPersonalWallet(wallet) {
    return wallet !== "gateway";
}
/**
 * 走 pi 腿的个人订阅钱包（ChatGPT Plus/Pro、Grok SuperGrok / X Premium）。
 *
 * 与 `isPersonalWallet` 的分工：后者是「非网关」二分（含 Claude 个人会员）；本函数是
 * 「走 pi 腿的个人订阅钱包」。Claude 个人钱包走 SDK 腿（`claude-local`），不在此列。
 * 消费点：无档位体系的裸别名塌回、以及「绝不借道网关凭证」的计费红线。
 */
export function isPersonalPiWallet(wallet) {
    return wallet === CHATGPT_OAUTH_GROUP || wallet === XAI_OAUTH_GROUP;
}
/** 换付费方时按这个顺序找「能提供它的钱包」。个人 Claude 会员不在其中：它能否提供取决于本机 CLI 登录态，
 *  这里判不了，且 Claude 裸 id 与网关有歧义——只有它本身就是当前主脑时才用它。 */
const CHAT_MODEL_WALLET_FALLBACK_ORDER = ["gateway", CHATGPT_OAUTH_GROUP, XAI_OAUTH_GROUP];
function walletServesChatModel(wallet, modelId, facts) {
    const { chain, withoutChain } = parseModelChain(modelId);
    const base = stripAllTags(withoutChain).trim();
    if (!base)
        return false;
    switch (wallet) {
        case "gateway":
            return chain === "pi"
                ? resolvePiWireRow(facts.catalog, modelId) !== undefined
                : facts.catalog.some((entry) => stripAllTags(entry.modelId) === base && isClaudeLegRow(entry, facts.userCustomGroups));
        // 个人订阅的选择器行恒为裸 id（带 `[pi]` 的一定是网关变体，见 buildChatgptOauthItems / buildXaiOauthItems）。
        case CHATGPT_OAUTH_GROUP:
            return chain === "claude" && facts.chatgpt.modelIds.includes(base);
        case XAI_OAUTH_GROUP:
            return chain === "claude" && facts.xai.modelIds.includes(base);
        case CLAUDE_OAUTH_GROUP:
            return chain === "claude" && isClaudeFamilyModel(base);
    }
}
/**
 * 已有对话 / 任务的**具体模型**由哪个钱包提供（周审 2026-10-15 B4②；维护者 2026-10-16 拍板：全局主脑只管新对话，
 * 已有对话按它自己记着的模型走它自己的通道；同一框架内企业模型 ↔ 个人订阅可以互换，跨框架由选择器拒绝）。
 *
 * 钱包是全局一份（`activeBrainGroup`），模型是每个对话各记一份；员工在别的对话里选了另一钱包的模型、或在会员卡上
 * 「设为主脑」后回到旧对话，发送会按「旧对话的模型 + 新主脑的钱包」发出——wangkai 10-14（v0.4.8 起）对话记着企业
 * GLM、主脑是 ChatGPT 个人订阅，每次都报「ChatGPT 个人订阅不提供模型 glm-5.3-flash」，重发多少次都一样。
 *
 *  - 别名 / 没指定模型（= 新对话的默认）→ 当前主脑钱包，原逻辑不动。
 *  - 当前主脑钱包能提供它 → 当前钱包（两边都能提供时尊重员工此刻的选择；主脑与对话一致时零变化）。
 *  - 否则按 {@link CHAT_MODEL_WALLET_FALLBACK_ORDER} 取第一个**已授权**且能提供它的钱包。
 *  - 谁都提供不了 → 原样交回当前钱包，由原有出口报因（不按名字猜一个，红线 #11）。
 *  - Claude 裸 id 在个人部署下与个人 Claude 会员有歧义（同一个 id 两边都可能是员工选的那一行），判不清就不换付费方。
 */
export function resolveChatModelWallet(args) {
    const { modelId, activeWallet, facts } = args;
    if (!modelId || BRAIN_ROLE_ALIASES.has(stripAllTags(parseModelChain(modelId).withoutChain).trim()))
        return activeWallet;
    if (walletServesChatModel(activeWallet, modelId, facts))
        return activeWallet;
    const { chain, withoutChain } = parseModelChain(modelId);
    if (chain === "claude" && isClaudeFamilyModel(stripAllTags(withoutChain)) && facts.deployMode !== "enterprise") {
        return activeWallet;
    }
    const authorized = (wallet) => wallet === CHATGPT_OAUTH_GROUP ? facts.chatgpt.authorized
        : wallet === XAI_OAUTH_GROUP ? facts.xai.authorized
            : true;
    return CHAT_MODEL_WALLET_FALLBACK_ORDER.find((wallet) => wallet !== activeWallet && authorized(wallet) && walletServesChatModel(wallet, modelId, facts)) ?? activeWallet;
}
/**
 * **仅** activeBrainGroup 真正缺失（fresh enterprise cold-start）时调用：从**企业主脑
 * catalog** 为裸 Claude 家族别名找**唯一** concrete target。
 *
 * 仅当主脑条目（`isBrainCatalogEntry`）里该家族 concrete id 集合 size===1 才返回；
 * 0 个或多个不同 concrete → undefined（不发明、不静默跨组选模）。同 id 跨多组/重复行
 * 仍算唯一。工具组（image/video）被 isBrainCatalogEntry 排除。
 *
 * 调用方必须先门控 `activeBrainGroup === undefined`：组已存在但 half-sync / 未知组 /
 * 组内无法解析时，禁止走本路径从全局 catalog 跨组偷选（strict no-fallback）。
 *
 * 与 `resolveCatalogEntry`（展示层「取最新」）刻意不同：发消息路径禁止在多候选时挑 newest，
 * 那会静默改写用户/组目标模型（严格无回退红线）。
 */
function resolveUniqueClaudeAliasFromBrainCatalog(alias, catalog) {
    // default/opusplan 经 CLAUDE_ALIAS_FAMILY 折叠到 opus 族；非 Claude 裸别名直接无匹配。
    if (!CLAUDE_ALIAS_FAMILY[alias])
        return undefined;
    const ids = new Set();
    for (const entry of catalog) {
        // 零账号行不是候选（llm-gateway#354 前它不下发）：既不能被选中，也不能破坏「唯一」。
        if (!isBrainCatalogEntry(entry) || isNoCredentialRow(entry))
            continue;
        const bare = stripAllTags(entry.modelId);
        if (!claudeAliasMatchesConcrete(alias, bare))
            continue;
        ids.add(bare);
        if (ids.size > 1)
            return undefined; // 多不同 concrete → 立刻放弃，不发明
    }
    if (ids.size !== 1)
        return undefined;
    return ids.values().next().value;
}
/**
 * Resolve a chat's model selection against the user's CURRENT brain (2026-06-03).
 *
 * Pure decision shared by the desktop send path (resolveBrainSelection) and tests.
 * Root cause it fixes: the renderer keeps model identity as a BARE ALIAS ("opus" —
 * see use-models.ts normalizeSdkValue invariant) and the plugin-setup flow sends no
 * model. Credential resolution matches the catalog by FULL id, so an alias/undefined
 * never matched → silent claude-local OAuth fallback → `[1m]` on a no-1M-credits
 * account → error / 200K floor.
 *
 * Maps an alias (or missing model) to the entitled FULL id for the active brain via
 * the唯一档位解析器 `resolveTierMapping`（员工显式覆盖 → 能力/价格派生 → 主模型回填）。
 * Concrete ids and `opusplan` pass through untouched. `brainWallet` lets the caller
 * honour an employee configured for a personal subscription (never a gateway).
 *
 * 2026-07-07 PR-C：派生源从 `deriveRoleMapping`（纯名字）升级为 `resolveTierMapping`
 * → `deriveTierFromCatalog`（能力/价格感知），根治 --model 别名路径的 bug②
 * （deepseek-pro 等便宜模型不再因名字进 opus 档）。
 *
 * 2026-08-07 P0 cold-start：`activeBrainGroup` 真正缺失时 resolveTierMapping(group=undefined)
 * 候选池为空会把裸 alias 原样透传 → 企业凭证 full-id 匹配失败。档位链未给出 concrete 时，
 * **仅**在 `activeBrainGroup === undefined` 再走 `resolveUniqueClaudeAliasFromBrainCatalog`
 * （唯一 concrete 才解析）。组已选定（含未知组 / half-sync）保持组解析与显式失败语义，
 * 禁止全局 catalog 跨组静默偷选。Claude OAuth / ChatGPT 早退路径不变。
 */
export function resolveBrainModelName(args) {
    const { modelId, activeBrainGroup, brainGroupParams, brainGroupDefaults, catalog } = args;
    const brainWallet = brainWalletForGroup(activeBrainGroup);
    const effective = resolveEffectiveBrainParams(brainGroupParams, brainGroupDefaults, activeBrainGroup);
    const roleMapping = effective?.roleMapping;
    // Role we're resolving. Missing model = the brain's default role.
    const base = (modelId ?? "default").replace(/\[[^\]]*\]/g, "");
    if (modelId && !BRAIN_ROLE_ALIASES.has(base)) {
        return { modelId, brainWallet }; // concrete id / opusplan → pass through
    }
    // 走 pi 腿的个人订阅脑（ChatGPT / Grok）：**没有档位体系可解**。它们的模型来自各自
    // 的权威表（pi-ai codex 表 / 本仓 XAI_OAUTH_MODELS），既不在 catalog 里、也没有
    // opus/sonnet/haiku 三档语义 —— 走下面的 resolveTierMapping 只会拿一个空 catalog
    // 派生出空 mapping，再把裸别名原样吐回去（对 Claude OAuth 那是对的：CLI 认识 "sonnet"；
    // 对 pi 个人钱包是错的：表里没有叫 "sonnet" 的模型，组装函数会当场抛「不提供模型
    // sonnet」）。故一律塌回组主模型 concrete id —— 员工在选择器里选的就是那一个具体
    // 模型，「主脑/背景小活」都用它。
    if (isPersonalPiWallet(brainWallet)) {
        return { modelId: effective?.defaultModel ?? roleMapping?.default ?? modelId, brainWallet };
    }
    // OAuth 脑（claude-local）：裸 Claude 别名（opus/sonnet/haiku）透传给 CLI 原生「跟随最新」
    // 解析，绝不经 resolveTierMapping 强制回填组主模型 —— catalog 无 "claude-oauth" 组 → 派生空
    // → 404 铁律 step-4 会把 sonnet 塌成组主模型（钉 Sonnet 的 chat 静默跑 Opus）。员工显式
    // concrete 覆盖优先。与交互会话 workspace-config OAuth 不变式（“绝不强制回填主模型”）同源。
    // base==="default"（缺 model）例外：仍走下方 resolveTierMapping 取组主模型 concrete
    // （plugin-setup / 后台定时需要具体主模型，不能留裸 alias）。
    if (brainWallet === "claude-oauth" && base !== "default") {
        const emp = roleMapping?.[base];
        return { modelId: isConcreteTierId(emp) ? emp : modelId, brainWallet };
    }
    // 单一真相源：主模型 = 员工映射的 default ?? 组默认模型；档位经 resolveTierMapping 解析。
    const mainModel = roleMapping?.default ?? effective?.defaultModel ?? "";
    const { mapping } = resolveTierMapping({
        mainModel,
        group: activeBrainGroup,
        employeeOverride: roleMapping,
        catalog,
        // REQ-293：本函数拿到的是**全量 entitled catalog**（desktop `resolveBrainSelection` 直读
        // `model_catalog_v1`，未经策展），所以这里够条件自己现推可路由集（判据与理由见
        // `claudeChainRoutableBaseIds` 的 JSDoc，不在此复述）。
        //
        // 这一层独有的信息是**触发面**：漏传 guard 时受害的是「不带 concrete id」的入口——
        // 企业登录后的第一条消息（App.tsx `setCurrentModel(roleConfig.model)` 塞的是裸角色别名）、
        // 技能卡片首条、派活/评审 agent、后台定时。员工侧表现为「企业模型配置正在同步，请稍候重试。」
        // 且永不恢复（agent-bridge `enterpriseCredentialGap`，pre-persist gate，连 user 行都不落）。
        // 输入框里正常打字那条路带着 concrete id、走上面的早返回，不受影响——所以它长得像时好时坏。
        routableIds: claudeChainRoutableBaseIds(catalog),
    });
    // REQ-265：链路身份（`[pi]`）随它自己的模型走，别在这一跳掉。
    //
    // 档位解析的键必须是**干净 base id**（`resolveTierMapping` 入口就 `stripAllTags(mainModel)`，
    // catalog 行本身也无后缀），于是「按组默认反解主模型」这条路返回的一律是裸 id —— 员工在
    // 选择器里选的 `deepseek-v4-flash[pi]` 到这里被抹成 `deepseek-v4-flash`。`[pi]` 是链路身份的
    // **唯一早期载体**（`parseModelChain` 是它的单一解析器，`backendProtocol` 要到凭证解析之后才
    // 落定），后缀一没，下游 agent-bridge 就判成 claude 链 → `backendProtocol=undefined` →
    // ClaudeBackend；而那条 `modelChain === "pi"` 的 fail-loud 护栏**结构上不可能触发**（链路身份
    // 在它之前就丢了）→ 静默改写用户目标模型（红线 #11）。REQ-263 三轮活体探针（正式后端
    // admin-kehan）观测到的正是这一格：`chats.model=deepseek-v4-flash[pi]` 而 jsonl
    // `entrypoint=sdk-ts`、`dosia-pi-backend` 零命中。
    //
    // 触发面是「不带模型参数的入口」：技能卡片首条消息、派活/评审 agent 启动、快捷派发
    // （`platform.sendMessage(chatId, msg, undefined, undefined, …)`）以及裸角色别名。员工在输入框
    // 里正常打字那条路带着 `currentModel` 变体 id，走上面的 concrete 早返回，不受影响。
    //
    // 只在**落回主模型自己**时还原，落到组内另一个模型（真正的档位下探）不动：那个模型有没有
    // pi 腿资格是 catalog 的事实，这里不发明。
    const restorePiChain = (resolved) => {
        if (!resolved || parseModelChain(mainModel).chain !== "pi")
            return resolved;
        if (stripAllTags(resolved) !== stripAllTags(mainModel))
            return resolved;
        // 变体 id 的拼装只有 buildChainedModelId 一处（幂等、保住 `[1m]` 这类权益 tag），别在这
        // 手拼字符串。
        return buildChainedModelId(resolved, "pi");
    };
    const full = mapping[base] ?? mapping.default;
    if (isConcreteTierId(full)) {
        return { modelId: restorePiChain(full), brainWallet };
    }
    // 档位链未钉住 concrete（典型：fresh state activeBrainGroup 缺失 → groupEntries=[]）：
    // **仅**真正缺失组时扫全局 catalog 找唯一 Claude concrete；组已存在则保持组语义 /
    // 显式失败（透传裸 alias），绝不 half-sync 时跨组静默偷选。
    // 多候选 / 空 catalog → 不发明，回落原 alias 让上层 enterpriseCredentialGap / CLI 接管。
    if (activeBrainGroup === undefined) {
        const unique = resolveUniqueClaudeAliasFromBrainCatalog(base, catalog);
        if (unique) {
            return { modelId: unique, brainWallet };
        }
    }
    // `full || modelId` —— resolveTierMapping 在主模型为空时可能给出空串 default，`||` 让其
    // 落回原 alias（OAuth 无 catalog 组时保留裸别名给 CLI 原生解析）。
    return { modelId: full || modelId, brainWallet };
}
/**
 * 求员工最终可见的模型组 = 角色可见组 ∩ 部门可见组（3-page 重构 2026-05-31）。
 *
 * 语义：任一维度为 空数组/undefined = 该维度"不限"。两者皆不限 → 返回 allGroups 原样
 * （避免 admin 没配可见性时员工看不到任何组的回归）。否则返回同时被两个非空维度允许的组，
 * 保持 allGroups 的顺序。
 *
 * @param allGroups 当前 entitled 的全部组名（保序）
 * @param roleVisible 角色可见组（企业策略页配）
 * @param deptVisible 部门可见组（部门管理页配）
 */
export function resolveVisibleGroups(allGroups, roleVisible, deptVisible) {
    const roleSet = roleVisible && roleVisible.length > 0 ? new Set(roleVisible) : null; // null = 不限
    const deptSet = deptVisible && deptVisible.length > 0 ? new Set(deptVisible) : null;
    if (!roleSet && !deptSet)
        return allGroups;
    return allGroups.filter((g) => (!roleSet || roleSet.has(g)) && (!deptSet || deptSet.has(g)));
}
// ─────────────────────────────────────────────────────────────────────────
// L2/L3 模型治理 — 单一真相源纯函数。
//
// 2026-07-17 enforcement 换血：旧 disabledModels 黑名单机制（org∪role 合并、
// mergeDisabledModels / isModelDisabled）**全面退役**，改为三层模型权限白名单漏斗
// （ModelSelection.brainModels/toolModels，见 applyModelSelection）。resolveAvailableModels
// 保留为**客户端唯一 enforcement 出口**，但输入从 `disabledModels: string[]`（黑名单）换血
// 为 `roleSelection: ModelSelection`（白名单，org 层的圈定已在服务端 bundleToPushEvent
// 单 seam 过滤——见 dosia-org-sync.ts applyModelSelection，client 只需再叠 role 一层）。
//
// 作用域：仅企业网关模型受治理；**员工自己的个人订阅**（claude-oauth → claude-local /
// chatgpt-oauth → ChatGPT Plus·Pro / xai-oauth → Grok SuperGrok·X Premium）不纳入
// （2026-07-07 拍板，enforcement 换血后依然保留——见 isGovernanceExemptEntry）。
// 个人版无 modelSelection → 全量放行 → 零行为变化。
// ─────────────────────────────────────────────────────────────────────────
/** 个人订阅通道组名 —— 治理（黑名单时代）/ 圈定（白名单漏斗时代）均不作用于此
 *  （拍板 2026-07-07，enforcement 换血后不变式延续）。
 *
 *  **2026-07-31 收第二个成员 `chatgpt-oauth`；2026-08-22 收第三个 `xai-oauth`**：该条豁免的
 *  语义写在上面那段注释里，是 **「员工花自己的钱，公司不管他用哪个模型」**（钱包级），
 *  不是「Claude 专属」——所以 ChatGPT / Grok 个人订阅落在同一语义下，widening 是兑现原意
 *  而非扩大解释。对比 PR-1 的 `migrateFlippedOAuthBrainGroup`：那一处的探针只认 Claude
 *  登录态，widening 会造成事故，故显式收窄。**判据是「这条判断的语义锚点是钱包还是 Claude」，
 *  不是「名字里有没有 oauth」。** */
export const GOVERNANCE_EXEMPT_GROUPS = [CLAUDE_OAUTH_GROUP, CHATGPT_OAUTH_GROUP, XAI_OAUTH_GROUP];
/** base id 归一（剥离 `[1m]` 等 `[...]` suffix tag）——三层模型漏斗全链（applyBrainCuration /
 *  applyModelSelection / isBrainModelSelected / resolveAvailableModels 等）base id 比对的唯一
 *  归一实现（红线 #9）。导出供 runtime 「成员判定」门禁复用（agent-bridge.ts / model-config.ts
 *  的 resolveAvailableModels 输出 base id 比对），禁在消费点各写一份同款正则（此前是模块内
 *  private const，只服务本文件内部；2026-07-18 谓词对齐批次导出）。 */
export const stripModelSuffixTag = (m) => m.replace(/\[[^\]]*\]/g, "");
/**
 * 网关改名后旧 id 仍应命中的别名（不含自身）。Google 先以 `-preview` 发布 Gemini，转正后去掉后缀
 * （gemini-3-pro-image-preview → gemini-3-pro-image），网关按自己的节奏改名；会话里存的旧 id 可能早于
 * 改名。与网关 `modelLookupCandidates` 的回落同义。**单源**：凭证解析（desktop
 * `resolveModelCredentialsDetailed`）与「是否已下线」判定（`isRetiredFromLiveCatalog`）都读它——
 * 两边候选集不一致时，判官会把凭证仍能解析的模型判成下线并把员工换走。
 */
export function catalogRenameAliases(baseModelId) {
    if (!baseModelId.startsWith("gemini"))
        return [];
    return [baseModelId.endsWith("-preview") ? baseModelId.slice(0, -"-preview".length) : `${baseModelId}-preview`];
}
/**
 * 从角色漏斗后的 catalog 中派生「可指定给 Agent / model_call / cron」的 concrete model ids。
 * `resolveAvailableModels` 的输出是主脑线与工具供给线的并集，不能直接 `.map(modelId)` 当作
 * 可执行模型池：生图模型为了 `generate_image` 必须保留供给行，但永远不能成为对话主脑或
 * 文本工作模型。按 base id 汇总后整模型剔除，避免只删 anthropic 行却从 openai 孪生行回流。
 *
 * 其余模型保持既有语义（含纯 openai 文本模型），并按 base id 保序去重，避免同一模型的
 * 多协议行在 entitlement 中重复出现。
 */
export function deriveAssignableModelIds(entries) {
    const imageGenBases = new Set(entries
        .filter(isImageGenCatalogEntry)
        .map((entry) => stripModelSuffixTag(entry.modelId)));
    const seen = new Set();
    const out = [];
    for (const entry of entries) {
        const base = stripModelSuffixTag(entry.modelId);
        if (!base || imageGenBases.has(base) || seen.has(base))
            continue;
        seen.add(base);
        out.push(entry.modelId);
    }
    return out;
}
/**
 * 「员工自建接入点的组名集合」唯一推导（N-21 定义、B-29 收敛成单源）——喂给
 * {@link isGovernanceExemptEntry} / {@link resolveAvailableModels} 的 `userCustomGroups`
 * 与渲染侧置灰判据，三个进程面共用这一份（红线 #9：判据只留一个权威源）。
 *
 * **判据是组凭据的 `source === "user-custom"`**（`GroupCredentialSource`，主进程
 * `setGroupCredentials` 写入面产出的权威事实），**不是** vendor / 组名字符串启发式——
 * 员工把组取名叫 "my-proxy" 时启发式会漏判，自建行又被圈定收窄掉一次（B-29 现象）。
 *
 * 入参是**外部输入面**（主进程 `groups_config_v1` 的 JSON.parse 结果 / 渲染侧
 * `getGroupCredentials` 的 IPC 回值），故此处逐层运行时 guard，不用 `as` 代替校验。
 * 形状不对 / 读不到 → 空集 =「没有自建组」= 既有治理行为（收紧项读不到按无豁免处理，
 * 不误放行）。
 *
 * 消费方（B-32 起五处，各留一层「从哪读」的包装，判据本体只有本函数一份）：
 * `packages/core/src/runtime.ts`（create_agent 腿）、
 * `packages/core/src/agent/workspace-resolver.ts`（会话装配腿——读出来随 chatOverrides 传给
 * 纯函数 `resolveWorkspaceConfig`，它自己不读盘）、
 * `apps/desktop/src/main/ipc-handlers/model-config.ts` `readUserCustomGroups`（主进程读
 * settings 的那一层包装）、`packages/ui/src/hooks/use-brain-models.ts`（渲染侧 picker）、
 * `packages/ui/src/components/settings/AIModelTab.brain-list.ts` `resolveGovernedCatalog`
 * （设置页两个 tab 的上游池；组凭据在那里是必填参数）。
 * 新增消费方时改这一行——它与 `docs/system/design/model-supply.md` 的同一句是两份副本，
 * 只改一边就是下一次漏接的起点（B-32 实证：N-21 / B-29 两轮各漏一处）。
 */
export function deriveUserCustomGroupNames(groupCredentials) {
    if (!groupCredentials || typeof groupCredentials !== "object" || Array.isArray(groupCredentials)) {
        return new Set();
    }
    return new Set(Object.entries(groupCredentials)
        .filter(([, group]) => !!group && typeof group === "object"
        && group.source === "user-custom")
        .map(([name]) => name));
}
/** 该 catalog 条目是否属于治理豁免组 = **固定名单 ∪ 当前员工的 user-custom 组集合**——
 *  个人订阅通道与**员工自建接入点**都不受企业圈定/治理约束。`resolveAvailableModels`、
 *  两处单模型 runtime 门禁（agent-bridge.ts / model-config.ts 后台会话）、picker 置灰面
 *  （ModelSelector.tsx `isPickerModelDisabledByAdmin`）与冷启动默认主脑推导
 *  （`packages/ui/src/lib/derive-default-model.ts`，N-62：平台清单为空时的 entitlement seed）
 *  共用此判定，单一豁免定义（红线 #9），不各自重复写 chosenGroup.name 比对。
 *
 *  **为什么自建组进的是「集合」那一半而不是常量**（N-21，2026-09-19 PM 拍板 §6）：豁免的语义
 *  是**钱包级**的「员工花自己的钱 / 带自己的东西进来，公司配的范围管不着」——员工自己填的
 *  baseUrl+key 与他自己的个人订阅同性质。但个人订阅是三个**固定虚拟组名**，自建组的名字由
 *  员工自取（"my-proxy" 也可以），常量里根本写不出来，所以判据扩成「名单 ∪ 集合」。
 *
 *  @param userCustomGroups 组凭据 `source === "user-custom"` 的组名集合。唯一推导 =
 *    {@link deriveUserCustomGroupNames}（B-29 收敛：主进程 `readUserCustomGroups`、渲染侧
 *    `use-brain-models`、core `runtime.ts` create_agent 腿都调它，各面只保留「从哪读」的
 *    包装，不再各写一份判据）。来源必须是**凭据 source**（`GroupCredentialSource`，主进程
 *    写入面产出的权威事实）。
 *    **禁用 vendor / family 串启发式**（"custom"/"自定义"）——员工把组取名叫 "my-proxy"
 *    就漏判，自建行又会被置灰一次。未传 / 空集 = 既有行为（自建行照旧受圈定收窄）。 */
export function isGovernanceExemptEntry(entry, exemptGroups = GOVERNANCE_EXEMPT_GROUPS, userCustomGroups) {
    const group = entry.chosenGroup?.name;
    if (!group)
        return false;
    return exemptGroups.includes(group) || !!userCustomGroups?.has(group);
}
/**
 * 个人钱包组：企业思考档上限管不着。
 *
 * 口径与 {@link isGovernanceExemptEntry} 相同——`GOVERNANCE_EXEMPT_GROUPS`
 * （claude-oauth / chatgpt-oauth / xai-oauth）∪ 员工自建组。自建组纳入，
 * 因为豁免的语义是钱包级「员工自己的钱 / 自己带进来的接入点，公司配的范围
 * 管不着」：模型能不能选已经按这个并集豁免，思考档上限是同一条治理，不能只
 * 放行模型、仍用公司档位改员工自己钱包发出去的档。不认组名启发式。
 * 未传 `userCustomGroups` = 只认三个固定虚拟组（与 isGovernanceExemptEntry 一致）。
 */
export function isPersonalWalletGroup(groupName, userCustomGroups) {
    if (typeof groupName !== "string" || groupName.length === 0)
        return false;
    return isGovernanceExemptEntry({ chosenGroup: { name: groupName } }, GOVERNANCE_EXEMPT_GROUPS, userCustomGroups);
}
/**
 * 可用池单源（enforcement 换血后的唯一出口）= OAuth 豁免行恒留 + 其余行经
 * `applyModelSelection(nonExempt, roleSelection)` 收窄。保序、不改入参。
 *
 * `roleSelection` = 该员工 role 层的圈定（`workspace.modelSelection`）。org 层圈定已在
 * 服务端 `bundleToPushEvent` 单 seam（`applyModelSelection(curated, policy.orgModelSelection)`）
 * 过滤过——client 落库的 catalog 已是 org 圈定后的子集，本函数只需再叠 role 一层，两层共用
 * 同一份 `applyModelSelection`（红线 #9 同源，禁写第二份分类逻辑）。
 *
 * **为何不能把 OAuth 行也喂给 applyModelSelection**：`applyModelSelection` 按 base id 把
 * 「同一模型的全部协议/组行」绑定判定（跨协议整模型剔除，见其 docstring）——若把 OAuth 行也
 * 混进候选池，一个被 role 圈掉的网关模型会连累同 base id 的 OAuth 豁免行一起被剔除，破坏
 * 「个人订阅不受企业圈定约束」的不变式（2026-07-07 拍板）。故本函数把豁免行**移出**候选池，
 * 单独判定后再并回结果——`isGovernanceExemptEntry` 与 `applyModelSelection` 各司其职。
 *
 * 唯一 enforcement 源：picker、档位候选预过滤、runtime 校验全部消费它，收敛
 * 「picker≠门禁」的双源分叉（bug⑨ 类级根治，enforcement 换血后延续）。
 *
 * `opts.userCustomGroups`（N-21，2026-09-19）= 员工自建接入点的组名集合，与 OAuth 豁免行同档
 * 恒留；语义与来源见 {@link isGovernanceExemptEntry}。未传 = 既有行为。
 */
export function resolveAvailableModels(catalogEntries, roleSelection, opts) {
    const exemptGroups = opts?.exemptGroups ?? GOVERNANCE_EXEMPT_GROUPS;
    const exempt = (e) => isGovernanceExemptEntry(e, exemptGroups, opts?.userCustomGroups);
    const allowedNonExempt = new Set(applyModelSelection(catalogEntries.filter((e) => !exempt(e)), roleSelection, opts));
    return catalogEntries.filter((e) => exempt(e) || allowedNonExempt.has(e));
}
/** 清单里与 `modelId` 同 base id 的全部行（`[1m]` / `[pi]` 等标签剥掉再比）。 */
function catalogRowsOfBase(catalog, modelId) {
    const base = stripModelSuffixTag(modelId);
    return catalog.filter((entry) => stripModelSuffixTag(entry.modelId) === base);
}
/**
 * `applyModelSelection` 的单模型便利包装（红线 #9 同源，取代已退役的 `isModelDisabled`）——
 * 用于「这个具体模型是否仍在 org / 角色圈定内」的单点判定。消费：主脑 picker（ModelSelector.tsx /
 * derive-default-model.ts）、平台默认主脑优先级逐条判定（brain-priority.ts）、云端任务值班表写入闸、
 * agent-host ⑦ 前置门。
 *
 * **走哪条线由该模型的清单行决定**（2026-10-08 起）：`catalogRows` 里该 base id 的行原样喂给
 * `applyModelSelection`，后者按 {@link isBrainPoolEntry}（原生 Anthropic 腿）分类——有原生腿 → 主脑线
 * （`brainModels` 判），没有（GPT 等翻译腿、纯 openai 行）→ 工具线（`toolModels` 判）；`opts.brainLineBases`
 * 里的 base（pi 变体）同样归主脑线。**清单里一行都没有该模型时不判它进 Claude 池**：以一条不带
 * `translated` 的合成 anthropic 行落工具线，`brainModels` 对它不生效。`catalogRows` 是必填参数——只拿
 * 模型 id 已经分不出它属于哪条线（此前靠家族名单按 id 前缀猜，已删除）。
 *
 * `modelId` 缺失 → false（无模型可判）。调用方各自在外层保留既有 OAuth/claude-local
 * 豁免形态（本函数不含豁免逻辑，因为调用点在进入本判定前已用 `group !== OAUTH_GROUP` /
 * `provider !== "claude-local"` 排除了豁免场景）。
 */
export function isBrainModelSelected(modelId, catalogRows, roleSelection, opts) {
    if (!modelId)
        return false;
    const rows = catalogRowsOfBase(catalogRows, modelId);
    const probe = rows.length > 0 ? rows : [{ modelId, protocol: ANTHROPIC_MESSAGES_PROTOCOL }];
    return applyModelSelection(probe, roleSelection, opts).length > 0;
}
/**
 * `applyBrainCuration` 的单模型便利包装（红线 #9 同源，与 {@link isBrainModelSelected} 对称）——
 * 判「这个模型的 Claude 引擎腿（anthropic_messages 行）是否仍在平台层放行范围内」。三层模型漏斗的
 * **平台层**成员判定（org/role 层各调一次 isBrainModelSelected）。语义（与 applyBrainCuration 一致）：
 *  - `curation` 未配置 / 无 allowedBrainModels → 非 image_gen 模型 true（未策展=全放行）；
 *  - image_gen（任一行声明 image_gen，或 gpt-image-* 兼容识别）→ 恒 false，不受平台/干活池配置解锁；
 *  - `modelId` 缺失 → false（无模型可判）；
 *  - 有原生 Anthropic 腿（{@link isBrainPoolEntry}）的模型：按 `curation.allowedBrainModels` 白名单判；
 *  - 其余（翻译腿如 "gpt-6-sol"、没有 anthropic 行的模型、清单里一行都没有的 id）：**不受
 *    `allowedBrainModels` 约束**，改由第三参 `platformWorkModels` 判——省略/undefined → false（工具线闸
 *    未配置=该线维持不可用）；传入且命中 → true。
 * 分类用 `catalogRows` 里该 base id 的真实行（必填，理由同 isBrainModelSelected）；该模型没有 anthropic
 * 行时补一条不带 `translated` 的合成 anthropic 行代表「它的 Claude 引擎腿」，按非原生判。
 *
 * **2026-07-18 桌面/云端语义对齐**：agent-host ⑦ 前置门（唯一消费点）判的不再是纯"能否当
 * 主脑"，而是"这个 id 对当前 (org,user,role) 是否放行"（承接桌面 agent-bridge.ts /
 * model-config.ts 的 union 门禁同一语义收敛——"GPT 出主脑 picker"由 isBrainPoolEntry 另管，
 * 不由这条 runtime 门决定，否则同一张已允许 GPT 当本地会话主模型的 Agent 卡会在云端定时执行
 * 时被拦，造成桌面/云端语义分裂，#1574 修过的同类分裂）。调用方按需传入
 * `policy.toolCuration?.modelCallModels` 喂第三参，让已被超管圈入干活模型池的翻译腿模型
 * 同样通过本判定。
 */
export function isBrainModelCurated(modelId, catalogRows, curation, platformWorkModels) {
    if (!modelId)
        return false;
    const rows = catalogRowsOfBase(catalogRows, modelId);
    const probe = rows.some((entry) => entry.protocol === ANTHROPIC_MESSAGES_PROTOCOL)
        ? rows
        : [...rows, { modelId, protocol: ANTHROPIC_MESSAGES_PROTOCOL }];
    return applyBrainCuration(probe, curation, platformWorkModels)
        .some((entry) => entry.protocol === ANTHROPIC_MESSAGES_PROTOCOL);
}
/**
 * L2 退役：从 managedSettings 剥离整字段 `availableModels`（不再向 SDK 下发白名单）。
 * 纯函数，返回新对象（不改入参）。红线 #8 迁移例外：server 停发的同时，客户端对
 * 旧 bundle 缓存里已落库的 availableModels 在透传 SDK 前也剥离，杜绝 CLI 侧静默替换。
 */
export function stripRetiredManagedSettings(ms) {
    if (!ms || typeof ms !== "object")
        return ms;
    if (!("availableModels" in ms))
        return ms;
    const { availableModels: _retired, ...rest } = ms;
    return rest;
}
/**
 * 组织记忆开关的「收严」合并（red line #9 合并原语，与 mergeDailyBudget 同姿态）。
 * org 默认 ∪ role，任一显式 false → false（记忆关，role 只能更严不能放宽）；两者皆非
 * false → undefined（= 缺省开，不落库 true，缺省语义单源避免漂移）。只有 false 是
 * 「限制值」，需要显式下发；on 用缺省表达。
 */
export function mergeOrgMemoryEnabled(...values) {
    return values.some((v) => v === false) ? false : undefined;
}
/**
 * 云端任务日预算的「收严」合并（red line #9 合并原语，与
 * mergeOrgMemoryEnabled 同姿态）。org 默认 与 role 取**更小的正数**（role 只能收严，
 * 不能放宽 org 的预算）。语义：正数 = 该角色/组织员工每个北京日的云端任务模型花费
 * 上限（USD）；`undefined`（缺省，或非正数被忽略）= 无限（存量不破坏）。
 *   - 两者皆 undefined/非正 → undefined（无限）；
 *   - 只有一个是正数 → 取该正数；
 *   - 两个都是正数 → 取更小者（收严）。
 */
export function mergeDailyBudget(...values) {
    let min;
    for (const v of values) {
        if (typeof v !== "number" || !Number.isFinite(v) || v <= 0)
            continue;
        min = min === undefined ? v : Math.min(min, v);
    }
    return min;
}
/**
 * L2 唯一构建器（red line #9）——server 构建下发给员工的 managedSettings 时调用一次：
 *  1. 剥离退役字段 availableModels（server 停发白名单）；
 *  2. 组织记忆开关 orgMemoryEnabled = org 默认 ∪ role「收严」（任一 false → false，
 *     mergeOrgMemoryEnabled）。只在关闭时下发 false，开启用缺省（不写 true）；
 *  3. 云端任务日预算 cloudTaskDailyBudgetUsd = org 默认 与 role「收严取小」
 *     （mergeDailyBudget）。只在有正数上限时下发，缺省=无限（不写字段）。
 * 推送给员工的 managedSettings 已是合并结果，客户端只见一份（禁多处手搓）。
 *
 * **2026-07-17 enforcement 换血**：disabledModels 黑名单合并（org∪role，mergeDisabledModels）
 * 已整段退役——模型治理改走三层白名单漏斗（ModelSelection.brainModels/toolModels），走独立的
 * org/role modelSelection 下发链（bundleToPushEvent / roleWorkspaceMap[role].modelSelection），
 * 不再经本构建器。managedSettings.disabledModels 字段停发（老客户端在员工升级前 role 层短暂
 * 放宽——org 层已在服务端 applyModelSelection 过滤兜住，过渡期语义可接受，PR body 注明）。
 *
 * 返回：role 有 managedSettings / 记忆被关 / 有预算上限 → 合并后的对象；
 * 全空 → undefined（个人版/无治理，管线同一条不分叉）。入参不改。
 */
export function buildEmployeeManagedSettings(roleManagedSettings, orgMemoryEnabled, orgCloudTaskDailyBudgetUsd) {
    const effectiveMemory = mergeOrgMemoryEnabled(orgMemoryEnabled, roleManagedSettings?.orgMemoryEnabled);
    const effectiveBudget = mergeDailyBudget(orgCloudTaskDailyBudgetUsd, roleManagedSettings?.cloudTaskDailyBudgetUsd);
    const hasBase = !!roleManagedSettings && Object.keys(roleManagedSettings).length > 0;
    if (!hasBase && effectiveMemory === undefined && effectiveBudget === undefined) {
        return undefined;
    }
    const out = stripRetiredManagedSettings({ ...(roleManagedSettings ?? {}) });
    // disabledModels 整字段停发（enforcement 换血，见函数头注释）——旧字段若残留在
    // roleManagedSettings 里（存量数据）一律剥离，绝不透传给员工端。
    delete out.disabledModels;
    if (effectiveMemory === false)
        out.orgMemoryEnabled = false;
    else
        delete out.orgMemoryEnabled;
    if (effectiveBudget !== undefined)
        out.cloudTaskDailyBudgetUsd = effectiveBudget;
    else
        delete out.cloudTaskDailyBudgetUsd;
    return out;
}
// ── L1.5 平台策展-主脑清单（PR-G）— 唯一构建器 + 反序列化 guard + 单点过滤纯函数 ──
//
// tenant 级（DOSIA 超管配置，全平台单份）「可作主脑的模型」白名单。动因：非 Claude 组被
// ByteSpike 标 anthropic_messages passthrough 但网关常 403（客户端只能靠模型家族猜原生性）。
// 超管按协议原生性显式圈定 → 供给 ∩ 策展。语义：allowedBrainModels 缺省 = 未配置 = 除
// image_gen 主脑驱动行外全放行（平台没配不锁人，但图片模型不能靠缺省态成为主脑）。
// 与 disabledModels 同姿态（base id 归一、单点纯函数消费）。
// ─────────────────────────────────────────────────────────────────────────
/**
 * **唯一构建器**（红线 #9）—— 从原始白名单输入构造规范 `PlatformBrainCuration`：
 *  - strip [1m] 等 suffix、trim、去空、去重 → base id 集合；
 *  - **空白名单 → allowedBrainModels 省略（undefined）**：策展是「显式圈定」，空 = 未配置
 *    = 全放行（避免超管误存空清单把全平台锁死的 footgun，与 disabledModels 空即省略同姿态）；
 *  - 盖上 version=1 + editedAt(now) + editedBy。
 * admin-portal PUT 路由与任何服务端产出点都必须接此构建器，不复制字段。
 */
export function buildPlatformBrainCuration(rawAllowed, editedBy, rawModelParams, rawReviewed) {
    const set = new Set();
    if (Array.isArray(rawAllowed)) {
        for (const v of rawAllowed) {
            if (typeof v !== "string")
                continue;
            const base = stripModelSuffixTag(v).trim();
            if (base)
                set.add(base);
        }
    }
    const curation = {
        version: 1,
        editedAt: new Date().toISOString(),
        editedBy,
    };
    if (set.size > 0) {
        curation.allowedBrainModels = [...set];
    }
    // 参数覆盖与可见性白名单**解耦**（企业模型治理统一面 P1，2026-07-22）——「配了平台默认参数
    // 但未收窄可见性（allowedBrainModels 空）」是合法态：该默认仍要作为 org/角色继承的底座下发，
    // 否则又是「配了不生效」。故不传第二参 allowed（不按白名单过滤 key），且移出 `set.size>0`
    // 分支——镜像 P0 buildModelSelection 的解耦（sanitizePlatformModelDefaultParams 省 allowed）。
    const modelParams = sanitizePlatformModelDefaultParams(rawModelParams);
    if (modelParams)
        curation.modelParams = modelParams;
    // 超管保存时见过的候选集（N-75）：与白名单解耦——白名单为空（全放行）时也照记。
    const reviewedModels = sanitizeModelIdList(rawReviewed);
    if (reviewedModels)
        curation.reviewedModels = reviewedModels;
    return curation;
}
/**
 * **反序列化边界 guard**（红线 #9）—— `JSON.parse` 读盘/网络/bundle 后索引前先过此单点。
 * 非对象 / version≠1 → `undefined`（消费端回落到「未配置=全放行」，绝不裸崩）。
 * allowedBrainModels 逐项 sanitize（同 buildPlatformBrainCuration 的 base id 语义）；
 * 缺失或清洗后为空 → allowedBrainModels 省略（全放行）。保留原始 editedAt/editedBy。
 * 存量里的 `pilotBrainFamilies`（主脑家族试点准入，2026-10-08 随家族名单一并删除）不读、不回传：
 * 构建器只认现行字段，旧字段读入即丢，下次保存自然从存储里消失（无需数据迁移）。
 */
export function parsePlatformBrainCuration(raw) {
    if (!raw || typeof raw !== "object")
        return undefined;
    const obj = raw;
    if (obj.version !== 1)
        return undefined;
    const built = buildPlatformBrainCuration(obj.allowedBrainModels, typeof obj.editedBy === "string" ? obj.editedBy : "", obj.modelParams, obj.reviewedModels);
    return { ...built, editedAt: typeof obj.editedAt === "string" ? obj.editedAt : built.editedAt };
}
function stripDriverAnthropicTagIfProtocolDropped(original, kept) {
    const survivingAnthropicBases = new Set();
    for (const entry of kept) {
        if (entry.protocol === ANTHROPIC_MESSAGES_PROTOCOL) {
            survivingAnthropicBases.add(stripModelSuffixTag(entry.modelId));
        }
    }
    const droppedAnthropicBases = new Set();
    for (const entry of original) {
        if (entry.protocol !== ANTHROPIC_MESSAGES_PROTOCOL)
            continue;
        const base = stripModelSuffixTag(entry.modelId);
        if (!survivingAnthropicBases.has(base))
            droppedAnthropicBases.add(base);
    }
    if (droppedAnthropicBases.size === 0)
        return kept;
    return kept.map((entry) => {
        if (!droppedAnthropicBases.has(stripModelSuffixTag(entry.modelId)))
            return entry;
        const tags = entry.capabilityTags;
        if (!tags?.includes(CAPABILITY_TAG_DRIVER_ANTHROPIC))
            return entry;
        return {
            ...entry,
            capabilityTags: tags.filter((tag) => tag !== CAPABILITY_TAG_DRIVER_ANTHROPIC),
        };
    });
}
/**
 * L1.5 单点过滤（红线 #9 唯一消费函数）—— 从 catalog 剔除「未被平台圈定为可作主脑」的 Claude 引擎腿
 * （2026-07-17 产品拍板按 base modelId 联动；2026-07-18 谓词对齐批次：工具线单独收编；2026-10-08 起
 * 主脑候选按**原生协议**判，不再按家族名单）。保序。语义：
 *  - `curation` 未配置（无 allowedBrainModels）→ 原样返回（全放行，向后兼容；本情形下第三参
 *    `platformWorkModels` 无效——没有策展就没有可豁免的剔除）；
 *  - **纯工具组**（image/video 组，`isToolGroup`）永不受策展影响（工具清单是 PR-T 辖区）；
 *  - **`anthropic_messages` 行逐行判**：
 *      · 原生 Anthropic 腿（{@link isNativeAnthropicLeg}：网关下发 `translated === false`）→ 未圈定进
 *        `allowedBrainModels` 即剔除、圈定即保留；
 *      · 其余 anthropic 行（网关翻译 `translated === true`，或 `translated` 缺席判不了）→ 不受
 *        `allowedBrainModels` 约束，只认第三参 `platformWorkModels`（干活模型池，超管平台策展
 *        `PlatformToolCuration.modelCallModels`）——`undefined` = **维持不可用**（延续 07-17「彻底剔除」
 *        默认）；配置了 = 清单内 base id 保留该行、清单外剔除。
 *  - **有原生 Anthropic 腿的模型的非 anthropic 行**（`openai_chat_completions` 孪生行）是 **pi 母语引擎
 *    链路**的供给，归母语引擎策展管，**不受本闸约束**（2026-07-19 产品拍板：主脑策展 / 母语引擎策展
 *    两面平级、app 内合并展示；此前的「跨协议整模型剔除」让 pi 从属于主脑策展，是 2026-07-18 那条
 *    「有 ID 无 eligible 行」漂移的根源）。
 *  - **只有翻译 / 判不了的 anthropic 行的模型**（GPT/grok/gemini 等）的**非 anthropic 行**（行级拆闸，
 *    F1 2026-07-22）→ 受**并集** `platformWorkModels ∪ supplementalSupplyIds`（第四参 = pi 白名单 base
 *    ids ∪ 工具面模型 id）闸——命中任一策展面即存活，为空 → 剔除（保持现状默认，不无条件放行，否则
 *    gemini/minimax 会回流指派面）。此拆闸把「pi 面/工具面」从「干活模型池清单」解耦（2026-07-19 拍板：
 *    各策展面各管各的供给），根治 modelCallModels 收窄成单模型后 gpt-image-2 / pi 白名单 / 视觉桶被整
 *    模型剔出客户端目录（生图/pi picker/视觉桶空）。
 *  - **无 anthropic 行的纯 openai 模型**（不在任何候选空间内）→ **不受本函数任何一层闸约束**，照旧放行
 *    （策展/干活池只管"有 anthropic 行的模型"，纯 openai 模型从未被剔过，零回归）。
 *
 * 企业链（getBundleRaw 的 org-admin 视图 + login/sync-self 的员工 catalog）与个人链共用此单点。
 *
 * **image_gen 硬边界（2026-08-10）**：任一协议行以 capabilityTags 声明 image_gen（或命中
 * gpt-image-* 旧清单兜底），该 base 的 anthropic_messages 行恒剔除；非 anthropic 行仅作为
 * generate_image 等工具供给保留。此边界先于「curation 未配置=全放行」，且
 * platformWorkModels 不能把它重新解锁成干活/主脑模型。
 *
 * **capabilityTags 同步（driver_anthropic）**：本函数剔掉某 base 的 anthropic_messages 行后，
 * 从该 base **存活行**摘掉 {@link CAPABILITY_TAG_DRIVER_ANTHROPIC}。网关本就没下发
 * anthropic 行时不动标签（上游语义）。不改入参。
 */
export function applyBrainCuration(catalogEntries, curation, platformWorkModels, supplementalSupplyIds) {
    // 生图是跨协议整模型的硬语义：任一行声明 image_gen（或命中旧 gpt-image-* 兜底），
    // 该 base 的 anthropic 驱动行无条件剔除。此规则先于「未配置=全放行」，避免平台漏斗未
    // 配置时图片模型再次成为主脑；非 anthropic 行继续留给 generate_image 的供给面。
    const imageGenBases = new Set(catalogEntries
        .filter(isImageGenCatalogEntry)
        .map((entry) => stripModelSuffixTag(entry.modelId)));
    const eligibleEntries = catalogEntries.filter((entry) => (!(imageGenBases.has(stripModelSuffixTag(entry.modelId))
        && entry.protocol === ANTHROPIC_MESSAGES_PROTOCOL)));
    const allowed = curation?.allowedBrainModels;
    if (!allowed || allowed.length === 0) {
        return stripDriverAnthropicTagIfProtocolDropped(catalogEntries, eligibleEntries.slice());
    }
    const allowSet = new Set(allowed.map(stripModelSuffixTag));
    // 工具线平台闸集合：undefined/空 = 未配置 → 该线维持现状不可用（不是"无豁免"式全放行）。
    const workModelSet = platformWorkModels && platformWorkModels.length > 0
        ? new Set(platformWorkModels.map(stripModelSuffixTag))
        : undefined;
    // 翻译腿模型「非 anthropic 行」的补充供给面（pi 白名单 ∪ 生图/视觉/视频工具面 base ids，F1
    // 2026-07-22）：与 workModelSet 取**并集**闸——干活池 anthropic 驱动行归 workModelSet，同 base 的
    // openai/gemini 行只要在任一策展面（干活池 or 供给面）内即存活。undefined/空 = 无补充供给。
    const supplySet = supplementalSupplyIds && supplementalSupplyIds.length > 0
        ? new Set(supplementalSupplyIds.map(stripModelSuffixTag))
        : undefined;
    // 有原生 Anthropic 腿的 base（Claude 主脑候选，与超管页候选空间 brainPoolCandidates 同一判据）。
    const nativeBrainBases = new Set(eligibleEntries.filter(isBrainPoolEntry).map((e) => stripModelSuffixTag(e.modelId)));
    // 带 anthropic 行、但这条行不是原生腿的 base（翻译腿 / translated 缺席）——独立工具线闸。
    const translatedAnthropicBases = new Set(eligibleEntries
        .filter((e) => isBrainCatalogEntry(e) && !isNativeAnthropicLeg(e))
        .map((e) => stripModelSuffixTag(e.modelId)));
    const kept = eligibleEntries.filter((e) => {
        const base = stripModelSuffixTag(e.modelId);
        // image_gen 非 anthropic 供给行只认工具供给面；误填 platformWorkModels 不能把图片模型
        // 重新解锁成 model_call。纯 *-image/*-video 工具组延续既有「策展不裁供给」语义。
        if (imageGenBases.has(base)) {
            return isToolGroup(e.chosenGroup?.name) || (!!supplySet && supplySet.has(base));
        }
        if (isToolGroup(e.chosenGroup?.name))
            return true; // 纯工具组永不受策展影响
        if (e.protocol === ANTHROPIC_MESSAGES_PROTOCOL) {
            // Claude 引擎腿逐行判：原生腿归 Claude 主脑策展；翻译 / 判不了的腿只认干活池（客户端对
            // modelCallModels 无第二消费点，是该行唯一 enforcement）。
            return isNativeAnthropicLeg(e) ? allowSet.has(base) : (!!workModelSet && workModelSet.has(base));
        }
        // 有原生腿的模型：主脑策展**只管 Claude 链路**。同 base 的 `openai_chat_completions` 孪生行是
        // **pi 母语引擎链路的供给**，归母语引擎策展（platform_pi_curation）管，不受本闸约束（此前做
        // 跨协议整模型剔除 → 桌面端有 ID 无 eligible 行 → 漂移；解耦即删掉这个漂移源，红线 #7）。
        if (nativeBrainBases.has(base))
            return true;
        if (translatedAnthropicBases.has(base)) {
            // 行级拆闸（F1 2026-07-22）：非 anthropic 行（pi 母语引擎 + 非 image_gen 工具面的供给）由
            // **并集** workModelSet ∪ supplySet 闸——命中任一策展面即存活；并集为空 → 剔除（不无条件放行，
            // 否则 gemini/minimax/doubao 会回流指派面，违反 07-17「非原生退出干活面」拍板）。
            return (!!workModelSet && workModelSet.has(base)) || (!!supplySet && supplySet.has(base));
        }
        return true; // 纯 openai 行（从无 anthropic 行）→ 不受本闸约束，照旧放行
    });
    return stripDriverAnthropicTagIfProtocolDropped(catalogEntries, kept);
}
/**
 * 「这一行属于这个组吗」的**单一谓词**（红线 #9）—— `chosenGroup` **∪ `availableGroups`**。
 *
 * 为什么必须只有一份：catalog 把同一模型去重到**单行**（`chosenGroup` = 最小 group id），
 * 而同一个模型往往同时属于多个组——随包快照 v55 实读，`glm-5.3-flash` 的 openai 行
 * `chosenGroup=glm-default`，`availableGroups` 还含 `zhipu-01` / `glm-text`。超管把腿指向
 * `glm-text` 时，只看 `chosenGroup` 就判不出它在组里。而这条判据同时被
 * **两个面**消费：{@link collectHostedSearchSupplyModelIds}（哪些行能活下来）与 desktop
 * `resolveHostedSearchEndpoint`（哪些行能拿来解 endpoint）。两份副本一旦漂移，症状恰好就是
 * 「策展配了、客户端说未配置」——正是 2026-08-29 门 D 那个形状。
 */
export function isCatalogEntryInGroup(entry, groupName) {
    if (!groupName)
        return false;
    if (entry.chosenGroup?.name === groupName)
        return true;
    return Array.isArray(entry.availableGroups)
        && entry.availableGroups.some((g) => g?.name === groupName);
}
/**
 * 「被点名的这个网关组，这台电脑上有没有它」的**单一判据**（红线 #9，B-299 从 desktop 收进 shared）。
 * 入参是本机组表：主进程是 `groups_config_v1` 解析结果，渲染侧是 `getGroupCredentials()` 的回值——
 * 后者由主进程 `GROUP_CONFIG_GET` 对前者逐键映射，两边键集相同，所以同一判据两边给出同一答案。
 *
 * 消费方：凭证解析器 `resolveModelCredentialsDetailed`（指名组不在本机 → `group_not_provisioned`，
 * 不回落该行的 `chosenGroup`）、网关能力腿 / 托管搜索腿（`apps/desktop/src/main/ipc-handlers/model-config.ts`）、
 * 「问管理员」里的组状态 `probeGroupCredHealth`、设置页工具模型的置灰（`packages/ui/src/lib/tool-model-groups.ts`）。
 * 只认自有键（原型链上的 `constructor` 之类不算），值为空也不算。
 */
export function isNamedGroupProvisioned(groups, group) {
    return Object.hasOwn(groups, group) && Boolean(groups[group]);
}
/**
 * 指名组不在这台电脑上时，员工与 AI 看到的**同一句话**，按版本取句（B-299）：
 *  - `enterprise`：维护者 2026-10-26 拍板原句「两种都说」——可能还没同步，也可能还没开通（找管理员）。
 *  - `personal`：个人版没有管理员这个角色（设置页文案原则 ⑤），只留「重新登录试试」。
 * AI 侧由 core「问管理员」条目按会话的部署形态原样带上；设置页置灰副文走 locale
 * `settings.aiModel.toolGroup.notOnThisMachine` / `settings.aiModel.toolGroup.notOnThisMachinePersonal`，两者 zh-Hans 值
 * 与这里逐字相同（`model-registry-named-group.test.ts` 钉住）。
 */
export const GROUP_NOT_ON_THIS_MACHINE_NOTICE = {
    enterprise: "这个模型在你的电脑上还用不了：可能还没同步好，重新登录试试；仍不行请找管理员开通",
    personal: "这个模型在你的电脑上还用不了：可能还没同步好，重新登录试试",
};
/**
 * **唯一构建器**（红线 #9）—— 收集「hosted-search 借用腿供给面」的 base id：被超管
 * `PlatformToolCuration` 点名做 web_search / x_search 腿的**组**里，那些**非主脑行**所属的
 * base id。产出喂 {@link applyBrainCuration} 第四参 `supplementalSupplyIds`（与生图/视觉/pi
 * 供给面同一个并集闸，不新开一条参数）。
 *
 * **为什么借用腿需要自己的供给面**（2026-08-29 v0.3.87 RC 门 D 定因）：hosted 腿的 endpoint 由
 * desktop `resolveHostedSearchEndpoint` 从「该组 × 该 face 的 catalog 行」解出，而那些行同时也
 * 是模型漏斗的裁剪对象——**腿的凭证供给此前从属于主脑/干活池策展**。prod 实证：超管
 * `allowedBrainModels` 没圈 glm → glm 的 `anthropic_messages` 行在下沉时被剔 → glm_web 这条
 * Anthropic 面借用腿在客户端**静默变成「未配置」，零报错**。deepseek_web 当时没坏纯属
 * `deepseek-v4-flash` 恰好在白名单里（巧合，不是设计）；grok/gpt 侧同构——它们带网关翻译的 anthropic
 * 行因而落进 `translatedAnthropicBases` 分支，非 anthropic 行只在 `modelCallModels ∪ 供给面` 里
 * 才存活，超管一收窄干活池，`grok_web`/`openai_web`/`x_search` 同样静默失联。
 *
 * **刻意跳过 `anthropic_messages` 行**：那一行就是「主脑可见性」本身（{@link isBrainCatalogEntry}
 * 的定义即此），把它纳入供给面等于用借用腿把未策展的模型送回主脑 picker。供给只骑在**非主脑行**
 * 上，Anthropic 面能不能用这些行由 desktop 侧解析决定（组凭证与协议行无关：baseUrl/apiKey 来自
 * `groups_config_v1` 的组，face 路径段由 hosted-search 客户端自己拼）。这就是「主脑不可见、腿仍
 * 可用」成立的机制——两件事各自有各自的判据，不再共用一行的存亡。
 *
 * **image_gen base 整体排除**：媒体行的 id 永远当不了 hosted-search 的 `model`
 * （`resolveHostedSearchModel` 直接跳过），把它放进供给面只会顺带解锁生图供给——那是 PR-T 辖区，
 * 不该由「某个组被点名做搜索腿」来解锁。
 *
 * 组成员判定走 {@link isCatalogEntryInGroup}（`chosenGroup` ∪ `availableGroups`），与 desktop 侧
 * `resolveHostedSearchEndpoint` 的 `inGroup` **同一个谓词**——两份副本一漂移，症状就退回本函数
 * 要修的那一类。
 *
 * REQ-268 起 `tavily` 也是网关能力组腿（伪模型行 `tavily_search`，按组解凭证），与其它腿同样
 * 收组名——不再有「本地 key 天然跳过」的例外。curation 未配置 / 无 group → `[]`
 * （= 无补充供给，与既有并集闸「空即维持现状」同姿态）。去重。
 *
 * **已知残口（如实记，别当它不存在）**：若某个被点名的组里，某原生家族模型**只有** anthropic 行
 * （它的 openai 孪生行既不以该组为 chosenGroup、也没在 availableGroups 里列它），本函数收不到
 * 任何 base、desktop 侧也找不到可用行 → 那条腿仍会是「未配置」。今日网关不是这个形状
 * （随包快照 v55 实读：glm/deepseek/kimi/grok 的每个 base 在自己的 `*-default` 组里都同时有
 * anthropic 行与 openai 行），但这是**上游下发形状**、不是本仓能保证的不变式。
 * 真出现时的判据在 `docs/model-catalog-missing-models-runbook.md`，届时的修法是给主脑行加「供给态」
 * 标记（让它存活但不进 {@link isBrainCatalogEntry}），不是把它整行放进供给面。
 */
export function collectHostedSearchSupplyModelIds(catalogEntries, curation) {
    const groups = new Set();
    // 反序列化边界（红线 #7）：curation 来自存量 bootstrap payload，字段可能不是数组。这里若裸
    // for...of，畸形一行就在 bundleToPushEvent 里抛，整条员工同步链停摆——供给面缺一行只是某条腿
    // 不可用，因它整轮拒服务是把小故障放大成大故障。
    const providerRows = Array.isArray(curation?.webSearchProviders) ? curation.webSearchProviders : [];
    for (const row of providerRows) {
        // 每条腿都有 group（REQ-268 起 tavily 也走网关组）；值域判定用 shared 单源谓词。
        if (!isWebSearchProviderId(row?.provider))
            continue;
        const name = typeof row?.group === "string" ? row.group.trim() : "";
        if (name)
            groups.add(name);
    }
    // x_search 的组不看 `enabled`：enabled 是运行时开关，而供给面缺一行的代价是「超管把开关
    // 打开后腿仍说未配置，且要等下一次 catalog 下沉才好」——按「超管已点名该组」收供给，
    // 开关只在 desktop 装配那一步生效（refreshWebSearchCredential）。
    const xGroup = typeof curation?.xSearch?.group === "string" ? curation.xSearch.group.trim() : "";
    if (xGroup)
        groups.add(xGroup);
    if (groups.size === 0)
        return [];
    const imageGenBases = new Set(catalogEntries.filter(isImageGenCatalogEntry).map((e) => stripModelSuffixTag(e.modelId)));
    const out = new Set();
    for (const entry of catalogEntries) {
        if (entry.protocol === ANTHROPIC_MESSAGES_PROTOCOL)
            continue; // 主脑行不进供给面（见头注）
        const base = stripModelSuffixTag(entry.modelId);
        if (imageGenBases.has(base))
            continue;
        for (const group of groups) {
            if (!isCatalogEntryInGroup(entry, group))
                continue;
            out.add(base);
            break;
        }
    }
    return [...out];
}
/**
 * `audio_transcribe` 的 gemini 腿供给面 —— 与 {@link collectHostedSearchSupplyModelIds}
 * **同一个成因、同一套判据**，只是入口换成 `audioTranscribeProviders`。
 *
 * 为什么它需要自己的供给面：desktop 的 `resolveAudioModelEndpoint` 要在客户端 catalog 里
 * 找到「该组 × `protocol === "gemini"` × `capabilities.supportsAudio === true`」的行才解得出
 * 凭证，而那些行同时是模型漏斗的裁剪对象——超管一收窄干活池（`modelCallModels`），被点名
 * 做音频腿的 gemini 行就被剔掉，客户端表现为**「策展配了、永远说未配置」，零报错**。
 * 这与 v0.3.87 RC 门 D 定的 glm_web 失联是同一类，所以修法照抄那一条，不新发明。
 *
 * 与搜索腿逐字同律的三点：
 *  · **跳过 `anthropic_messages` 行**——那一行就是主脑可见性本身，纳入供给面等于用工具腿
 *    把未策展的模型送回主脑 picker；
 *  · **排除 image_gen base**——媒体行的 id 当不了 `generateContent` 的转写模型；
 *  · 组成员判定走 {@link isCatalogEntryInGroup}（与 desktop 侧解析同一个谓词）。
 *
 * REQ-268 起 `volc_asr` 也是网关能力组腿（伪模型行 `openai_audio_transcriptions`），同样收组名，
 * 不再有「本地 key 天然跳过」的例外。curation 未配置 / 无 group → `[]`。
 * 反序列化边界（红线 #7）：`audioTranscribeProviders` 来自存量 bootstrap payload，非数组时
 * 按空处理而不是抛——供给面缺一行只是某条腿不可用，因它整轮拒服务是把小故障放大成大故障。
 */
export function collectAudioTranscribeSupplyModelIds(catalogEntries, curation) {
    const groups = new Set();
    const providerRows = Array.isArray(curation?.audioTranscribeProviders)
        ? curation.audioTranscribeProviders
        : [];
    for (const row of providerRows) {
        // 两条腿都需要组凭证（REQ-268 起 volc_asr 也走网关组）；值域判定用 shared 单源谓词。
        if (!isAudioTranscribeProviderId(row?.provider))
            continue;
        const name = typeof row?.group === "string" ? row.group.trim() : "";
        if (name)
            groups.add(name);
    }
    if (groups.size === 0)
        return [];
    const imageGenBases = new Set(catalogEntries.filter(isImageGenCatalogEntry).map((e) => stripModelSuffixTag(e.modelId)));
    const out = new Set();
    for (const entry of catalogEntries) {
        if (entry.protocol === ANTHROPIC_MESSAGES_PROTOCOL)
            continue; // 主脑行不进供给面（见头注）
        const base = stripModelSuffixTag(entry.modelId);
        if (imageGenBases.has(base))
            continue;
        for (const group of groups) {
            if (!isCatalogEntryInGroup(entry, group))
                continue;
            out.add(base);
            break;
        }
    }
    return [...out];
}
// ───────────────────────────── L1.5 平台策展-工具模型（PR-T） ────────────────────
//
// tenant 级（DOSIA 超管配置，全平台单份）视觉理解 / 生图 / 视频工具模型清单，替代
// image-tools.ts 的四个硬编码常量（换工具模型要发版）。语义：每字段 undefined = 未配置 =
// 回落代码里的常量（零回归）；配置了即整字段替换
// （不 merge，红线 #8 单一活路径）。消费在 core image-tools（desktop 读 tool_curation_v1
// 传入），工具模型同受 L2 disabledModels 约束（消费点 resolveAvailableModels 预过滤 catalog）。
// ─────────────────────────────────────────────────────────────────────────
/** trim + 非空 string，否则 undefined。工具模型 id 是 ByteSpike verbatim id（无 [1m] suffix），
 *  故只 trim 不 strip suffix（避免误伤含特殊字符的 id）。 */
function cleanId(v) {
    if (typeof v !== "string")
        return undefined;
    const t = v.trim();
    return t || undefined;
}
/** 清洗一个 string[]（生图条目 unsupportedParams 覆写位，F1）：逐项 trim 去空，全空 →
 *  undefined（缺省=回落 catalog）。不去重（`aspect_ratio:1:4` 含冒号的值语义原样保留）。 */
function sanitizeStringList(v) {
    if (!Array.isArray(v))
        return undefined;
    const out = [];
    for (const it of v) {
        const s = cleanId(it);
        if (s)
            out.push(s);
    }
    return out.length > 0 ? out : undefined;
}
/** 清洗 paramSchema（param → allowed values 的正向清单覆写位，F1）：逐 key 校验其值为
 *  string[]，坏行丢弃；全空 → undefined。反序列化边界防御——绝不裸索引，绝不裸抛。 */
function sanitizeParamSchema(v) {
    if (!v || typeof v !== "object" || Array.isArray(v))
        return undefined;
    const out = {};
    for (const [k, raw] of Object.entries(v)) {
        const key = cleanId(k);
        const values = sanitizeStringList(raw);
        if (key && values)
            out[key] = values;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
/** 清洗 paramDefaults（param → 单个默认值 string）：逐 key/value trim，空值丢弃。
 * 值域收窄由下方 sanitizeParamDefaultsForSchema 统一执行；runtime 仍保留最终防御。 */
function sanitizeParamDefaults(v) {
    if (!v || typeof v !== "object" || Array.isArray(v))
        return undefined;
    const out = {};
    for (const [k, raw] of Object.entries(v)) {
        const key = cleanId(k);
        const value = cleanId(raw);
        if (key && value)
            out[key] = value;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
/** Grok media schemas are pinned, verified capability facts. Admin input may
 * choose defaults but may not widen or truncate those facts. Other providers
 * keep the existing per-row override behavior. */
function resolveCuratedMediaParamSchema(modelId, raw) {
    const contract = getGrokMediaModelContract(modelId);
    if (!contract)
        return sanitizeParamSchema(raw);
    return Object.fromEntries(Object.entries(contract.paramSchema).map(([param, values]) => [param, [...values]]));
}
function sanitizeParamDefaultsForSchema(raw, schema) {
    const defaults = sanitizeParamDefaults(raw);
    if (!defaults || !schema)
        return defaults;
    const narrowed = Object.fromEntries(Object.entries(defaults).filter(([param, value]) => schema[param]?.includes(value)));
    return Object.keys(narrowed).length > 0 ? narrowed : undefined;
}
/** vision / document / video 理解清单共用：modelId trim 去空去重、scenario 保留字符串。 */
function sanitizeScenarioModelRows(raw) {
    const out = [];
    const seen = new Set();
    if (!Array.isArray(raw))
        return out;
    for (const it of raw) {
        const modelId = cleanId(it?.modelId);
        if (!modelId || seen.has(modelId))
            continue;
        seen.add(modelId);
        const scenario = cleanId(it?.scenario) ?? "";
        out.push({ modelId, scenario });
    }
    return out;
}
/**
 * **唯一构建器**（红线 #9）—— 从原始输入构造规范 `PlatformToolCuration`：
 *  - 各清单逐项 sanitize（丢无效项、trim id/scenario/group）；
 *  - **某字段清洗后为空 → 省略（undefined）= 未配置 = 该能力不可用**（与 brainCuration 空即
 *    省略同姿态；不再回落代码常量）；
 *  - 盖上 version=1 + editedAt(now) + editedBy。
 * admin-portal PUT 路由与任何服务端产出点都必须接此构建器，不复制字段。
 */
export function buildPlatformToolCuration(raw, editedBy) {
    const input = (raw && typeof raw === "object" ? raw : {});
    // vision/document/video 理解清单按 modelId 去重（保留首见项，非 400——超管误粘贴重复行
    // 不该拦提交，静默清洗即可；返回的 curation 里项数变少即是清洗结果，供前端/复审自行比对）。
    const visionModels = sanitizeScenarioModelRows(input.visionModels);
    const documentModels = sanitizeScenarioModelRows(input.documentModels);
    const videoModels = sanitizeScenarioModelRows(input.videoModels);
    const imageGenModels = [];
    if (Array.isArray(input.imageGenModels)) {
        for (const it of input.imageGenModels) {
            const row = it;
            const rawModelId = cleanId(row.modelId);
            const modelId = rawModelId ? (canonicalizeGrokMediaModelId(rawModelId) ?? rawModelId) : undefined;
            const mediaContract = modelId ? getGrokMediaModelContract(modelId) : undefined;
            const group = cleanId(row.group);
            if (!modelId || !group || mediaContract?.kind === "video")
                continue; // group 是凭证组，缺则该项不可用 → 丢
            const scenario = cleanId(row.scenario) ?? "";
            // 同 modelId 多行 = 该模型的分组优先级链（行序=优先级，各行不同 group）；unsupportedParams /
            // paramSchema 是 per-行参数覆写位（网关 catalog 无 per-组表达力，红线 #9 覆写位唯一通道）；
            // paramDefaults 是 per-行参数默认值（core handler 真注入位）——逐项 sanitize，禁裸抛
            // （反序列化边界防御）。
            const entry = { modelId, group, scenario };
            const unsupportedParams = sanitizeStringList(row.unsupportedParams);
            if (unsupportedParams)
                entry.unsupportedParams = unsupportedParams;
            const paramSchema = resolveCuratedMediaParamSchema(modelId, row.paramSchema);
            if (paramSchema)
                entry.paramSchema = paramSchema;
            const paramDefaults = sanitizeParamDefaultsForSchema(row.paramDefaults, paramSchema);
            if (paramDefaults)
                entry.paramDefaults = paramDefaults;
            imageGenModels.push(entry);
        }
    }
    const videoGenModels = [];
    if (Array.isArray(input.videoGenModels)) {
        for (const it of input.videoGenModels) {
            const row = it;
            const rawModelId = cleanId(row.modelId);
            const modelId = rawModelId ? (canonicalizeGrokMediaModelId(rawModelId) ?? rawModelId) : undefined;
            const mediaContract = modelId ? getGrokMediaModelContract(modelId) : undefined;
            const group = cleanId(row.group);
            // generate_video 当前只有已验证的两个 Grok canonical 视频模型；未知模型与图片模型
            // 都不能落成一张“已配置但 runtime 必然跳过”的假卡片。
            if (!modelId || !group || mediaContract?.kind !== "video")
                continue;
            const entry = {
                modelId,
                group,
                scenario: cleanId(row.scenario) ?? "",
            };
            const unsupportedParams = sanitizeStringList(row.unsupportedParams);
            if (unsupportedParams)
                entry.unsupportedParams = unsupportedParams;
            const paramSchema = resolveCuratedMediaParamSchema(modelId, row.paramSchema);
            if (paramSchema)
                entry.paramSchema = paramSchema;
            const paramDefaults = sanitizeParamDefaultsForSchema(row.paramDefaults, paramSchema);
            if (paramDefaults)
                entry.paramDefaults = paramDefaults;
            videoGenModels.push(entry);
        }
    }
    // imageFallbackChain 同样按 id 去重（保留首见项，非 400——降级链重复项是清洗对象，不是
    // 拒绝理由）。
    const imageFallbackChain = [];
    const seenFallbackIds = new Set();
    if (Array.isArray(input.imageFallbackChain)) {
        for (const v of input.imageFallbackChain) {
            const id = cleanId(v);
            if (!id || seenFallbackIds.has(id))
                continue;
            seenFallbackIds.add(id);
            imageFallbackChain.push(id);
        }
    }
    const curation = {
        version: 1,
        editedAt: new Date().toISOString(),
        editedBy,
    };
    if (visionModels.length > 0)
        curation.visionModels = visionModels;
    if (documentModels.length > 0)
        curation.documentModels = documentModels;
    if (videoModels.length > 0)
        curation.videoModels = videoModels;
    if (imageGenModels.length > 0)
        curation.imageGenModels = imageGenModels;
    if (videoGenModels.length > 0)
        curation.videoGenModels = videoGenModels;
    if (imageFallbackChain.length > 0)
        curation.imageFallbackChain = imageFallbackChain;
    // 干活模型池：base id 归一（stripModelSuffixTag）+ 去重（复用 sanitizeModelIdList 单一
    // 归一实现，红线 #9——与 applyBrainCuration 豁免比对用同一 base id 语义；空 → 省略）。
    const modelCallModels = sanitizeModelIdList(input.modelCallModels);
    if (modelCallModels) {
        curation.modelCallModels = modelCallModels;
        // 干活模型池默认参数（P3）：key 必须在 modelCallModels 内才生效（同 sanitizer 单一实现，
        // 红线 #9——与 buildPlatformBrainCuration 共用 sanitizePlatformModelDefaultParams）。
        const modelParams = sanitizePlatformModelDefaultParams(input.modelParams, modelCallModels);
        if (modelParams)
            curation.modelParams = modelParams;
    }
    // web_search provider 链：只保留已知 provider id；**每一条腿都必须有非空 group**
    // （REQ-268 起 tavily 也从 gateway 组解凭证，不再有「本地厂商密钥」这个例外），否则丢行
    // ——没有 group 就解不出 baseUrl/apiKey，落库只会变成一条永远「跳过（未配置）」的死行；
    // 保序；清洗后空 → 省略（未配置 = desktop 按默认序从 bundle 行 chosenGroup 兜底）。
    const webSearchProviders = [];
    if (Array.isArray(input.webSearchProviders)) {
        for (const it of input.webSearchProviders) {
            if (!it || typeof it !== "object")
                continue;
            const provider = it.provider;
            const group = cleanId(it.group);
            if (!isWebSearchProviderId(provider) || !group)
                continue;
            webSearchProviders.push({ provider, group });
        }
    }
    if (webSearchProviders.length > 0)
        curation.webSearchProviders = webSearchProviders;
    // audio_transcribe provider 链：与上面 web_search 那段**逐字同构**（REQ-268 起两条腿的
    // 凭证拓扑完全相同，都从 gateway 组解出 → 每条腿都必须带非空 group，没有例外）。
    // 同一个 provider 只保留**第一条**：链是「按顺序换腿重试」，同一条腿排两次的第二次
    // 必然与第一次同因失败（同一把凭证、同一个上游），只会在 attempts 里刷出一条重复的
    // 失败原因；去重放在落库这一处，运行时与 admin 页各自去重才是第二份判据。
    const audioTranscribeProviders = [];
    const seenAudioProviders = new Set();
    if (Array.isArray(input.audioTranscribeProviders)) {
        for (const it of input.audioTranscribeProviders) {
            if (!it || typeof it !== "object")
                continue;
            const provider = it.provider;
            const group = cleanId(it.group);
            if (!isAudioTranscribeProviderId(provider) || !group)
                continue;
            if (seenAudioProviders.has(provider))
                continue;
            seenAudioProviders.add(provider);
            audioTranscribeProviders.push({ provider, group });
        }
    }
    if (audioTranscribeProviders.length > 0) {
        curation.audioTranscribeProviders = audioTranscribeProviders;
    }
    // x_search：enabled===true 且 group 非空才落；disabled / 缺 group → 省略（未启用）。
    if (input.xSearch && typeof input.xSearch === "object") {
        const xs = input.xSearch;
        const group = cleanId(xs.group);
        if (xs.enabled === true && group) {
            curation.xSearch = { enabled: true, group };
        }
    }
    return curation;
}
/**
 * 公开快照的生图/生视频行没有 group。个人腿用自己 bundle 的 `chosenGroup.name` 补上；
 * **找不到就丢弃该行**（不补空串、不保留无 group 条目）。调用方必须处理 `dropped`
 * （去重 warn，见 {@link warnDroppedPersonalMediaRows}）。
 */
export function attachChosenGroupToMediaRows(rows, catalog) {
    if (!rows?.length)
        return { attached: [], dropped: [] };
    const groupByBase = new Map();
    for (const entry of catalog) {
        // 零账号行（llm-gateway#354）的组不能代表这个模型：同一模型另一条协议行在别的组有供给时，
        // 拿它的组去取 key 会打到一个不服务该模型的组。当它不存在（与改动前逐字节等价）。
        if (isNoCredentialRow(entry))
            continue;
        const base = stripModelSuffixTag(entry.modelId);
        const group = cleanId(entry.chosenGroup?.name);
        if (!base || !group || groupByBase.has(base))
            continue;
        groupByBase.set(base, group);
    }
    const attached = [];
    const dropped = [];
    for (const row of rows) {
        const group = groupByBase.get(stripModelSuffixTag(row.modelId));
        if (!group) {
            dropped.push(row);
            continue;
        }
        attached.push({ ...row, group });
    }
    return { attached, dropped };
}
const droppedPersonalMediaSeen = new Set();
/** 个人腿补不上 group 的策展行：去重 warn，姿态对齐 `noteDroppedCuration`。 */
export function warnDroppedPersonalMediaRows(bucket, dropped) {
    for (const row of dropped) {
        const key = `${bucket}|${row.modelId}`;
        if (droppedPersonalMediaSeen.has(key))
            continue;
        droppedPersonalMediaSeen.add(key);
        console.warn(`[bytespike-personal] 公开策展 ${bucket} 模型 "${row.modelId}" 在当前 bundle 没有 chosenGroup，已从个人版工具清单丢弃。`);
    }
}
/** 测试用：清空「已报过」记忆。 */
export function __resetDroppedPersonalMediaNotices() {
    droppedPersonalMediaSeen.clear();
}
/**
 * 公开快照子集 → 企业同款 `PlatformToolCuration`（唯一构建器出口）。
 * 缺公开子集 → undefined（调用方不写 `tool_curation_v1`，不覆盖已有）。
 * 补不上 group 的行放进 dropped*，由调用点去重 warn。
 */
export function hydratePlatformToolCurationFromBundle(publicCuration, catalog) {
    if (!publicCuration)
        return undefined;
    const image = attachChosenGroupToMediaRows(publicCuration.imageGenModels, catalog);
    const video = attachChosenGroupToMediaRows(publicCuration.videoGenModels, catalog);
    return {
        curation: buildPlatformToolCuration({
            modelCallModels: publicCuration.modelCallModels,
            visionModels: publicCuration.visionModels,
            documentModels: publicCuration.documentModels,
            videoModels: publicCuration.videoModels,
            imageFallbackChain: publicCuration.imageFallbackChain,
            imageGenModels: image.attached,
            videoGenModels: video.attached,
        }, "personal-governance"),
        droppedImageGen: image.dropped,
        droppedVideoGen: video.dropped,
    };
}
/**
 * **写入校验**（PR-2 生图分组优先级链机制，2026-07-22）—— admin-portal PUT 路由在
 * `buildPlatformToolCuration` 落库**之前**先过此门，违规则整体拒绝（400 { violations }），
 * 不落半成品。契约（PR-3 前端保存前校验/行内展示同款 shape，禁擅改）：
 * `{ rowIndex, field, rule, message }[]`，`rowIndex` = 提交时 `imageGenModels` 原始下标。
 *
 * 唯一规则（生图段 simplify 2026-07-23：exposedId 假名与 multiRowChains 开关已退役，键=modelId
 * 后原规则②③④恒真/无意义，删；产品语义见 worker-spec-v4.md「PR-2」）：
 *  ① `(modelId, group)` 全局判重——同一「模型+凭证组」组合在整份清单里只能出现一次
 *     （凭证组是执行时的路由目标，两行指向同一目标即冲突）。同一 `modelId` 挂不同 `group`
 *     的多行 = 合法的分组优先级链，不判重。
 *
 * vision/imageFallbackChain 的重复 id 不在此校验范围——那是 `buildPlatformToolCuration`
 * 内的静默去重清洗（非 400，见该函数注释），不是需要拦截提交的违规。
 *
 * TODO(生图段重做 2026-07-23)：`paramDefaults`（param→默认值）本期仅 `buildPlatformToolCuration`
 * sanitize（trim/丢空），**不在此做 400 范围校验**——权威 `paramSchema`（各参数允许值域）由网关
 * catalog 下发，本函数只拿到提交的 `raw`（无 catalog），无从判越界。最终防呆落在 core
 * generate_image handler：注入默认值前查该模型 live `paramSchema`，越界跳过 + warn（单点权威）。
 * 若日后要在写入门也拦，需把 catalog paramSchema 一并传入本函数。
 */
export function validatePlatformToolCuration(raw) {
    const violations = [];
    const input = (raw && typeof raw === "object" ? raw : {});
    const validateRows = (rawRows, field) => {
        if (!Array.isArray(rawRows))
            return;
        const rows = [];
        rawRows.forEach((it, rowIndex) => {
            const row = it;
            const rawModelId = cleanId(row.modelId);
            const modelId = rawModelId ? (canonicalizeGrokMediaModelId(rawModelId) ?? rawModelId) : undefined;
            const group = cleanId(row.group);
            if (!modelId || !group)
                return;
            rows.push({ rowIndex, modelId, group });
        });
        // 规则①：(modelId, group) 全局判重——记第一次出现的行号，之后每次重现都判违规。
        const firstSeenPair = new Map();
        for (const r of rows) {
            // 复合 key 分隔符用 US（ASCII Unit Separator，专为"字段分隔"设计的控制字符）而非
            // 裸 NUL 字节：modelId/group 均不可能含它，语义上永不碰撞；但和 NUL 不同，US 不会被
            // git 的 binary 探测误判（git 只把 NUL 当 binary 信号）。这里此前用的正是裸 NUL——
            // 因为落在文件第 140081 字节（超出 git ~8000 字节采样窗口）才侥幸没触发整文件被判
            // binary，换个位置（如 agent-bridge.ts 2026-07-30 事故）就会让 git diff/grep 对整个
            // 文件失效，是位置依赖、不可预测的隐患，不是这里恰好没事就说明写法本身安全
            // （ratchet nul-byte-in-source 规则据此抓出并要求清零）。
            const pairKey = `${r.modelId}\u001F${r.group}`;
            const firstRowIndex = firstSeenPair.get(pairKey);
            if (firstRowIndex === undefined) {
                firstSeenPair.set(pairKey, r.rowIndex);
                continue;
            }
            violations.push({
                rowIndex: r.rowIndex,
                field,
                rule: "duplicate-model-group",
                message: `模型「${r.modelId}」+ 凭证组「${r.group}」与第 ${firstRowIndex + 1} 行重复，同一(模型,凭证组)组合只能出现一次`,
            });
        }
    };
    validateRows(input.imageGenModels, "group");
    validateRows(input.videoGenModels, "videoGenModels.group");
    return violations.sort((a, b) => a.rowIndex - b.rowIndex);
}
/**
 * **反序列化边界 guard**（红线 #9）—— `JSON.parse` 读盘/网络/bundle 后索引前先过此单点。
 * 非对象 / version≠1 → `undefined`（消费端回落到「未配置=该能力不可用」，绝不裸崩）。
 * 各字段经 buildPlatformToolCuration 逐项 sanitize；保留原始 editedAt/editedBy。
 */
export function parsePlatformToolCuration(raw) {
    if (!raw || typeof raw !== "object")
        return undefined;
    const obj = raw;
    if (obj.version !== 1)
        return undefined;
    // 一次性迁移（架构不变量 #12）：旧 `videoDefault` 字符串 → `videoModels` 单行清单。
    // 已有 `videoModels` 时不覆盖。下个策展大版本即可删除本分支（存量读盘走完一轮即可）。
    const migrated = { ...obj };
    const hasVideoModels = Array.isArray(obj.videoModels) && obj.videoModels.length > 0;
    const oldDefault = typeof obj.videoDefault === "string" ? obj.videoDefault.trim() : "";
    if (!hasVideoModels && oldDefault) {
        migrated.videoModels = [{ modelId: oldDefault, scenario: "" }];
    }
    delete migrated.videoDefault;
    const built = buildPlatformToolCuration(migrated, typeof obj.editedBy === "string" ? obj.editedBy : "");
    return { ...built, editedAt: typeof obj.editedAt === "string" ? obj.editedAt : built.editedAt };
}
// ── L1.6 三层模型权限白名单漏斗（org / role 同用）— 唯一构建器 + 反序列化 guard ──
//
// 超管圈平台池 → 企业内圈本企业可用集（org.modelSelection）→ 角色再圈（role.dosia.modelSelection）。
// 两线（主脑 brainModels / 工具 toolModels）同构、字段独立可选，缺省 = 该线继承上层全量。
// 本期只建数据面（构建器 + guard + 存储 + 下发 + 管理 UI），**不动 enforcement**（旧
// disabledModels 继续生效）。与 buildPlatformBrainCuration 同姿态：base id 归一、空即省略。
// ─────────────────────────────────────────────────────────────────────────
/** 逐项 string 校验 + stripModelSuffixTag 归一 + trim + 去空去重 → base id 集；空 → undefined。 */
function sanitizeModelIdList(raw) {
    if (!Array.isArray(raw))
        return undefined;
    const set = new Set();
    for (const v of raw) {
        if (typeof v !== "string")
            continue;
        const base = stripModelSuffixTag(v).trim();
        if (base)
            set.add(base);
    }
    return set.size > 0 ? [...set] : undefined;
}
/** 清洗 effort 允许子集（PiReasoningEffort[]）：逐项 ∈ THINKING_LEVELS，去重，**空 →
 *  undefined**（禁 `[]` 哨兵；[] 与 allowedSubset absent 语义冲突，一律折叠为
 *  「未收窄=跟随」）。反序列化边界防御：非数组 → undefined，绝不裸抛。 */
function sanitizeEffortSubset(v) {
    if (!Array.isArray(v))
        return undefined;
    const set = new Set();
    for (const x of v)
        if (typeof x === "string" && PI_REASONING_EFFORTS.has(x))
            set.add(x);
    return set.size > 0 ? [...set] : undefined;
}
/** SDK 链 effort 允许子集清洗 5 档（EffortLevel：low/medium/high/xhigh/max，SDK 0.3.212）。
 *  **minimal 非 EffortLevel、不放行**。禁空数组哨兵、反序列化边界防御（非数组 → undefined，不裸抛）。 */
function sanitizeEffortSubsetSdk(v) {
    if (!Array.isArray(v))
        return undefined;
    const set = new Set();
    for (const x of v)
        if (typeof x === "string" && SDK_EFFORT_LEVELS.has(x))
            set.add(x);
    return set.size > 0 ? [...set] : undefined;
}
/** 清洗 thinking 模式允许子集（ThinkingMode[]）：逐项 ∈ PLATFORM_THINKING_MODES，去重，空 →
 *  undefined（同 sanitizeEffortSubset 的禁空数组姿态）。 */
function sanitizeThinkingSubset(v) {
    if (!Array.isArray(v))
        return undefined;
    const set = new Set();
    for (const x of v)
        if (typeof x === "string" && PLATFORM_THINKING_MODES.has(x))
            set.add(x);
    return set.size > 0 ? [...set] : undefined;
}
function sanitizeIntInPiRange(v) {
    return typeof v === "number" && Number.isInteger(v) && v >= PI_MAX_COMPLETION_TOKENS_MIN && v <= PI_MAX_COMPLETION_TOKENS_MAX ? v : undefined;
}
/**
 * 清洗一条 `SdkChainParams`（Model Z）—— paramDefaults(effort/thinking) + allowedSubset(effort/thinking)
 * 各字段独立校验；**服务端边界 #1（Codex 修 #4）：paramDefaults 越出本条 allowedSubset → 丢该默认**
 * （sanitize 层结构自守，网关能力门在 resolve 层）。全空 → undefined。
 */
function sanitizeSdkChainParams(v) {
    if (!v || typeof v !== "object" || Array.isArray(v))
        return undefined;
    const obj = v;
    const asRaw = (obj.allowedSubset && typeof obj.allowedSubset === "object" && !Array.isArray(obj.allowedSubset) ? obj.allowedSubset : {});
    const allowedEffort = sanitizeEffortSubsetSdk(asRaw.effort);
    const allowedThinking = sanitizeThinkingSubset(asRaw.thinking);
    const pdRaw = (obj.paramDefaults && typeof obj.paramDefaults === "object" && !Array.isArray(obj.paramDefaults) ? obj.paramDefaults : {});
    const paramDefaults = {};
    if (typeof pdRaw.effort === "string" && SDK_EFFORT_LEVELS.has(pdRaw.effort) && (!allowedEffort || allowedEffort.includes(pdRaw.effort))) {
        paramDefaults.effort = pdRaw.effort;
    }
    if (typeof pdRaw.thinking === "string" && PLATFORM_THINKING_MODES.has(pdRaw.thinking) && (!allowedThinking || allowedThinking.includes(pdRaw.thinking))) {
        paramDefaults.thinking = pdRaw.thinking;
    }
    const out = {};
    if (Object.keys(paramDefaults).length > 0)
        out.paramDefaults = paramDefaults;
    const allowedSubset = {};
    if (allowedEffort)
        allowedSubset.effort = allowedEffort;
    if (allowedThinking)
        allowedSubset.thinking = allowedThinking;
    if (Object.keys(allowedSubset).length > 0)
        out.allowedSubset = allowedSubset;
    return Object.keys(out).length > 0 ? out : undefined;
}
/**
 * 清洗一条 `PiChainParams`（Model Z）—— paramDefaults(effort/outputTokens) + allowedSubset(effort/
 * outputTokens.max)；**服务端边界 #1**：paramDefaults.effort 越出 allowedSubset.effort → 丢；
 * paramDefaults.outputTokens 夹到 allowedSubset.outputTokens.max（Codex 修 #1）。全空 → undefined。
 */
function sanitizePiChainParams(v) {
    if (!v || typeof v !== "object" || Array.isArray(v))
        return undefined;
    const obj = v;
    const asRaw = (obj.allowedSubset && typeof obj.allowedSubset === "object" && !Array.isArray(obj.allowedSubset) ? obj.allowedSubset : {});
    const allowedEffort = sanitizeEffortSubset(asRaw.effort);
    const otRaw = (asRaw.outputTokens && typeof asRaw.outputTokens === "object" && !Array.isArray(asRaw.outputTokens) ? asRaw.outputTokens : undefined);
    const allowedOutputMax = otRaw ? sanitizeIntInPiRange(otRaw.max) : undefined;
    const pdRaw = (obj.paramDefaults && typeof obj.paramDefaults === "object" && !Array.isArray(obj.paramDefaults) ? obj.paramDefaults : {});
    const paramDefaults = {};
    if (typeof pdRaw.effort === "string" && PI_REASONING_EFFORTS.has(pdRaw.effort) && (!allowedEffort || allowedEffort.includes(pdRaw.effort))) {
        paramDefaults.effort = pdRaw.effort;
    }
    const pdOut = sanitizeIntInPiRange(pdRaw.outputTokens);
    if (pdOut !== undefined)
        paramDefaults.outputTokens = allowedOutputMax !== undefined ? Math.min(pdOut, allowedOutputMax) : pdOut;
    const out = {};
    if (Object.keys(paramDefaults).length > 0)
        out.paramDefaults = paramDefaults;
    const allowedSubset = {};
    if (allowedEffort)
        allowedSubset.effort = allowedEffort;
    if (allowedOutputMax !== undefined)
        allowedSubset.outputTokens = { max: allowedOutputMax };
    if (Object.keys(allowedSubset).length > 0)
        out.allowedSubset = allowedSubset;
    return Object.keys(out).length > 0 ? out : undefined;
}
/**
 * **唯一 per-chain sanitizer**（红线 #9，Model Z 2026-07-23）—— `ModelSelection.modelParams`
 * (`Record<baseId, ModelParamsByChain>`) 反序列化边界。**Option C 无版本化迁移**：按 value 字段名
 * 判别新旧（字段集不相交，安全）——
 *  - **新形**（含 `sdk`/`pi` 键）：分别 sanitizeSdkChainParams / sanitizePiChainParams。
 *  - **旧扁平形**（含顶层 `reasoningEffort`/`thinking`/`maxTokens`/`temperature`，P0 单值）：**迁移进
 *    `sdk`**（`{ paramDefaults: { effort: reasoningEffort, thinking } }`）—— **行为保持**：旧 org/角色
 *    单值此前只经 resolveModelParams→materialize 喂 SDK 链（maxTokens/temperature 无 SDK 落点、被
 *    丢弃；从不进 Pi 链），故只迁 sdk、Pi 链保持不变，零 Pi 回归。key 归一 stripModelSuffixTag。
 * 全空/畸形项丢弃；输出全空 → undefined（未配置 = 继承上层）。绝不裸索引、绝不裸抛。
 */
function sanitizeModelParamsByChain(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return undefined;
    const out = {};
    for (const [rawId, value] of Object.entries(raw)) {
        const modelId = stripModelSuffixTag(rawId.trim());
        if (!modelId || !value || typeof value !== "object" || Array.isArray(value))
            continue;
        const v = value;
        const isNewShape = "sdk" in v || "pi" in v;
        const entry = {};
        if (isNewShape) {
            const sdk = sanitizeSdkChainParams(v.sdk);
            const pi = sanitizePiChainParams(v.pi);
            if (sdk)
                entry.sdk = sdk;
            if (pi)
                entry.pi = pi;
        }
        else {
            // 旧扁平 P0 单值 → 迁移进 sdk（行为保持：只喂 SDK 链，maxTokens/temperature 丢弃，不进 Pi）。
            const paramDefaults = {};
            if (typeof v.reasoningEffort === "string" && SDK_EFFORT_LEVELS.has(v.reasoningEffort))
                paramDefaults.effort = v.reasoningEffort;
            if (typeof v.thinking === "string" && PLATFORM_THINKING_MODES.has(v.thinking))
                paramDefaults.thinking = v.thinking;
            if (Object.keys(paramDefaults).length > 0)
                entry.sdk = { paramDefaults };
        }
        if (entry.sdk || entry.pi)
            out[modelId] = entry;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
/**
 * **唯一构建器**（红线 #9）—— 从原始主脑/工具白名单输入构造规范 `ModelSelection`：
 *  - 逐项 string 校验、stripModelSuffixTag 归一、trim、去空、去重 → base id 集；
 *  - **某线空数组 → 该字段省略（undefined）= 继承上层全量**（footgun 防护，与
 *    buildPlatformBrainCuration 空即省略同姿态，避免误存空清单锁死该线）；
 *  - 盖上 version=1 + editedAt(now) + editedBy。
 * org PUT 路由与 role.dosia 解析都必须接此构建器，不复制字段。
 */
export function buildModelSelection(rawBrain, rawTool, editedBy, rawModelParams, rawReviewedBrain) {
    const selection = {
        version: 1,
        editedAt: new Date().toISOString(),
        editedBy,
    };
    const brainModels = sanitizeModelIdList(rawBrain);
    const toolModels = sanitizeModelIdList(rawTool);
    if (brainModels) {
        selection.brainModels = brainModels;
        // 见过的候选集（N-87）只随显式主脑圈定存在：恢复继承后留着它没有意义，下次再圈定时重新记。
        const reviewedBrainModels = sanitizeModelIdList(rawReviewedBrain);
        if (reviewedBrainModels)
            selection.reviewedBrainModels = reviewedBrainModels;
    }
    if (toolModels)
        selection.toolModels = toolModels;
    // 参数覆盖与可见性 whitelist 解耦（P0）：不按 brain/tool 白名单过滤 key，「可见性继承（未收窄）
    // 但参数已覆盖」是合法态；全空/畸形 → undefined（继承上层，不误存空 map）。Model Z：per-chain
    // sanitizer（新 sdk/pi 形 + 旧扁平迁移，红线 #9 唯一 guard）。
    const modelParams = sanitizeModelParamsByChain(rawModelParams);
    if (modelParams)
        selection.modelParams = modelParams;
    return selection;
}
/**
 * **反序列化边界 guard**（红线 #9）—— `JSON.parse` 读盘/网络/bundle 后索引前先过此单点。
 * 非对象 / version≠1 → `undefined`（消费端回落到「未配置=继承上层」，绝不裸崩）。
 * 各线逐项 sanitize（同 buildModelSelection 的 base id 语义）；缺失或清洗后为空 → 该线省略
 * （继承上层）。`modelParams`（P0，2026-07-22）经同一 sanitizer 显式接住（不接则新字段
 * 静默丢弃）——非对象/畸形 → 该项/整字段丢弃不抛。保留原始 editedAt/editedBy。
 */
export function parseModelSelection(raw) {
    if (!raw || typeof raw !== "object")
        return undefined;
    const obj = raw;
    if (obj.version !== 1)
        return undefined;
    const built = buildModelSelection(obj.brainModels, obj.toolModels, typeof obj.editedBy === "string" ? obj.editedBy : "", obj.modelParams, obj.reviewedBrainModels);
    return { ...built, editedAt: typeof obj.editedAt === "string" ? obj.editedAt : built.editedAt };
}
/** Pi 的 ThinkingLevel 子集收成 EffortLevel。minimal 不是 SDK 词，不进 ResolvedChainParams。 */
function toEffortLevels(levels) {
    if (!levels)
        return undefined;
    const out = [];
    for (const level of levels) {
        if (level === "low" || level === "medium" || level === "high" || level === "xhigh" || level === "max")
            out.push(level);
    }
    return out;
}
/** allow-set 交集：`undefined` = universal（不收窄）；两 array → 集合交。用于逐层 allowedSubset 收窄。 */
function intersectAllow(a, b) {
    if (!a)
        return b ? [...b] : undefined;
    if (!b)
        return [...a];
    const bs = new Set(b);
    return a.filter((x) => bs.has(x));
}
/**
 * 模型参数范围治理**per-chain 参数生效纯函数**（Model Z，2026-07-23，红线 #9 唯一 resolve）——
 * 对某 catalog 行的某条链（sdk/pi），把 平台/org/role 三层的 per-chain 覆写做**allowedSubset 逐层
 * 交集收窄** + **paramDefaults 字段级 `??` cascade(role??org??platform) + 按有效范围/网关能力 clamp**，
 * 产出有效默认 + 有效允许集。参考 claude-code `availableModels`(允许集)⟂`model`(默认) 分离 + policy
 * 收窄 + 用时校验（方案 §1）。
 *
 * **能力门（Codex 修 #6，协议专属）**：`caps` 为该链协议专属行能力（SDK=anthropic 行、Pi=openai 行）。
 *  - effort：网关全集 = `supportedEffortLevels`（有则用）| `supportsEffort===true`→不收窄 |
 *    否则 —— **SDK 链**收窄为空（缺 effort 能力 → 拒，同旧 resolveModelParams 严格姿态）、**Pi 链**
 *    不收窄（缺 descriptor → 放行，保旧 Pi「不 gate」行为，零回归）。
 *  - thinking（SDK）：默认仅当 `thinkingParam.supported===true`（同旧 resolveModelParams，不按
 *    canDisable gate 默认）；返回的 `allowed.thinking` 按 canDisable 收窄（供 PR-2 picker）。
 *  - outputTokens（Pi）：默认夹到 `min(outputTokenParam.max, 各层 allowedSubset.outputTokens.max)`。
 *
 * 三层全空 → 返回 undefined（落网关默认）。纯函数、不改入参。
 */
export function resolvePerChainParams(chain, platform, org, role, caps) {
    if (!platform && !org && !role)
        return undefined;
    const layers = [platform, org, role];
    // ── effort（两链共有，链感知全集） ──
    const gatewayLevels = caps?.supportedEffortLevels;
    // SDK 链：保留声明的全部 EffortLevel 值（含 medium/xhigh）；无具体档位但 supportsEffort → 保守兜底
    // ALL_EFFORT_LEVELS（4 档，**无 xhigh** —— xhigh 永不从兜底推断）；缺 effort 能力 → [] 拒。
    // Pi 链：保留声明里属于 THINKING_LEVELS 的档（不再按 low/high/max 三个字面量丢掉 medium/xhigh/minimal）；
    // 无 levels → undefined 不收窄。
    const gatewayEffort = chain === "sdk"
        ? gatewayLevels && gatewayLevels.length > 0
            ? [...gatewayLevels]
            : caps?.supportsEffort === true
                ? [...ALL_EFFORT_LEVELS] // 声明支持但无具体档位 → 保守 4 档兜底（xhigh 不进兜底）
                : [] // SDK 严格：缺 effort 能力 → 拒
        : gatewayLevels && gatewayLevels.length > 0
            ? gatewayLevels.filter((l) => THINKING_LEVELS.includes(l))
            : undefined; // Pi 宽松：缺 descriptor → 放行
    let effAllowedEffort = gatewayEffort;
    for (const l of layers) {
        // Pi 子集可以含 minimal。这条共享槽仍是 EffortLevel：minimal 留下不了，medium/xhigh 留得下。
        // 订阅腿发送不读这里，它走 resolveSubscriptionEffortLevels。
        effAllowedEffort = intersectAllow(effAllowedEffort, toEffortLevels(l?.allowedSubset?.effort));
    }
    const rawDefault = role?.paramDefaults?.effort ?? org?.paramDefaults?.effort ?? platform?.paramDefaults?.effort;
    const defEffort = rawDefault !== undefined && rawDefault !== "minimal" ? rawDefault : undefined;
    const outDefaults = {};
    if (defEffort !== undefined && (effAllowedEffort === undefined || effAllowedEffort.includes(defEffort))) {
        outDefaults.effort = defEffort;
    }
    const outAllowed = {};
    // 仅 surface 非空收窄集（undefined=不收窄→省略；[]=网关不支持/收窄成空→省略，PR-1 不消费 allowed，
    // 内部仍用 effAllowedEffort 做默认门；PR-2 picker 再细化空集语义）。
    if (effAllowedEffort !== undefined && effAllowedEffort.length > 0)
        outAllowed.effort = effAllowedEffort;
    if (chain === "sdk") {
        // ── thinking（仅 SDK） ──
        const sdkLayers = layers;
        const supported = caps?.thinkingParam?.supported === true;
        // 默认 gate：仅 supported（同旧 resolveModelParams，不按 canDisable gate 默认）+ 逐层子集。
        let layerThinking;
        for (const l of sdkLayers)
            layerThinking = intersectAllow(layerThinking, l?.allowedSubset?.thinking);
        const defThinking = sdkLayers[2]?.paramDefaults?.thinking ?? sdkLayers[1]?.paramDefaults?.thinking ?? sdkLayers[0]?.paramDefaults?.thinking;
        if (defThinking !== undefined && supported && (layerThinking === undefined || layerThinking.includes(defThinking))) {
            outDefaults.thinking = defThinking;
        }
        // allowed.thinking（PR-2 picker）：网关全集按 canDisable 收窄，再交层子集。
        const gatewayThinking = !supported
            ? []
            : caps?.thinkingParam?.canDisable === false
                ? ["auto", "adaptive", "enabled"]
                : undefined;
        const effThinking = intersectAllow(gatewayThinking, layerThinking);
        if (effThinking !== undefined && effThinking.length > 0)
            outAllowed.thinking = effThinking;
    }
    else {
        // ── outputTokens（仅 Pi） ──
        const piLayers = layers;
        const caxMax = caps?.outputTokenParam?.max;
        let effMax = typeof caxMax === "number" ? caxMax : undefined;
        for (const l of piLayers) {
            const m = l?.allowedSubset?.outputTokens?.max;
            if (typeof m === "number")
                effMax = effMax === undefined ? m : Math.min(effMax, m);
        }
        const defOut = piLayers[2]?.paramDefaults?.outputTokens ?? piLayers[1]?.paramDefaults?.outputTokens ?? piLayers[0]?.paramDefaults?.outputTokens;
        if (defOut !== undefined)
            outDefaults.outputTokens = effMax !== undefined ? Math.min(defOut, effMax) : defOut;
        if (effMax !== undefined)
            outAllowed.outputTokensMax = effMax;
    }
    if (Object.keys(outDefaults).length === 0 && Object.keys(outAllowed).length === 0)
        return undefined;
    return { defaults: outDefaults, allowed: outAllowed };
}
/** SDK 链 adapter（红线 #9）：平台扁平 `PlatformBrainCuration.modelParams[base]`（Option C 不 flip）
 *  → `SdkChainParams` 只读视图喂 resolve。丢 temperature/maxTokens（无 SDK materialize 落点）。 */
export function platformDefaultParamsToSdkChain(p) {
    if (!p)
        return undefined;
    const paramDefaults = {};
    if (p.reasoningEffort !== undefined)
        paramDefaults.effort = p.reasoningEffort;
    if (p.thinking !== undefined)
        paramDefaults.thinking = p.thinking;
    return Object.keys(paramDefaults).length > 0 ? { paramDefaults } : undefined;
}
/** SDK resolve 产出 → `PlatformModelDefaultParams`（喂现有 materializeModelParamsIntoDefaults，
 *  只用 reasoningEffort/thinking）。全空 → undefined。 */
export function sdkResolvedToDefaultParams(r) {
    if (!r)
        return undefined;
    const out = {};
    if (r.defaults.effort !== undefined)
        out.reasoningEffort = r.defaults.effort;
    if (r.defaults.thinking !== undefined)
        out.thinking = r.defaults.thinking;
    return Object.keys(out).length > 0 ? out : undefined;
}
/** Pi resolve 产出 → flatten `PlatformPiModelParams`（effort→reasoningEffort、outputTokens→
 *  maxCompletionTokens）喂现有扁平 `orgPiModelParams` wire（方案 §4，Option C）。全空 → undefined。 */
export function piResolvedToModelParams(r) {
    if (!r)
        return undefined;
    const out = {};
    const eff = r.defaults.effort;
    if (eff !== undefined)
        out.reasoningEffort = eff;
    if (r.defaults.outputTokens !== undefined)
        out.maxCompletionTokens = r.defaults.outputTokens;
    return Object.keys(out).length > 0 ? out : undefined;
}
// ── 模型参数范围治理 PR-4：员工侧约束下发面（EffectiveModelParamPolicy）单源 ────────────
//
// `resolvePerChainParams` 已算出 `ResolvedChainParams.allowed`（PR-1 计算但未消费）。PR-4 把它
// 收成 per-chain 约束条目下发到 desktop；双门 enforcement（picker + 消费端 clamp）共用同一份，
// 单一 clamp 纯函数源（红线 #9）。
/** SDK resolve 产出 → SDK 链约束条目（`allowed.effort`/`allowed.thinking`，非空才带）。全空 →
 *  undefined（不收窄）。 */
export function sdkResolvedToConstraint(r) {
    if (!r)
        return undefined;
    const out = {};
    if (r.allowed.effort && r.allowed.effort.length > 0)
        out.effort = [...r.allowed.effort];
    if (r.allowed.thinking && r.allowed.thinking.length > 0)
        out.thinking = [...r.allowed.thinking];
    return Object.keys(out).length > 0 ? out : undefined;
}
/** Pi resolve 产出 → Pi 链约束条目（`allowed.effort` + `allowed.outputTokensMax`）。全空 →
 *  undefined。 */
export function piResolvedToConstraint(r) {
    if (!r)
        return undefined;
    const out = {};
    const eff = r.allowed.effort;
    if (eff && eff.length > 0)
        out.effort = [...eff];
    if (r.allowed.outputTokensMax !== undefined)
        out.outputMax = r.allowed.outputTokensMax;
    return Object.keys(out).length > 0 ? out : undefined;
}
/**
 * **唯一反序列化 guard**（红线 #9）—— `effective_model_param_policy_v1` 读盘 / push 事件消费边界。
 * `Record<baseId, { sdk?, pi? }>`。逐条 key 归一 stripModelSuffixTag；effort/thinking 走既有
 * `sanitizeEffortSubset`/`sanitizeThinkingSubset`（禁空数组）、outputMax 走 `sanitizeIntInPiRange`。
 * 空条目丢弃；全空 → 空对象（= 不收窄）。非对象/畸形 → `{}`，绝不裸抛。
 */
export function parseEffectiveModelParamPolicy(raw) {
    const out = {};
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return out;
    for (const [rawId, value] of Object.entries(raw)) {
        const base = stripModelSuffixTag((typeof rawId === "string" ? rawId : "").trim());
        if (!base || !value || typeof value !== "object" || Array.isArray(value))
            continue;
        const v = value;
        const entry = {};
        const sdkRaw = (v.sdk && typeof v.sdk === "object" && !Array.isArray(v.sdk) ? v.sdk : {});
        const sdkEffort = sanitizeEffortSubsetSdk(sdkRaw.effort); // SDK 链 5 档（含 xhigh/medium），别用 3 档 sanitizer 静默丢 xhigh
        const sdkThinking = sanitizeThinkingSubset(sdkRaw.thinking);
        if (sdkEffort || sdkThinking) {
            const sdk = {};
            if (sdkEffort)
                sdk.effort = sdkEffort;
            if (sdkThinking)
                sdk.thinking = sdkThinking;
            entry.sdk = sdk;
        }
        const piRaw = (v.pi && typeof v.pi === "object" && !Array.isArray(v.pi) ? v.pi : {});
        const piEffort = sanitizeEffortSubset(piRaw.effort);
        const piOutputMax = sanitizeIntInPiRange(piRaw.outputMax);
        if (piEffort || piOutputMax !== undefined) {
            const pi = {};
            if (piEffort)
                pi.effort = piEffort;
            if (piOutputMax !== undefined)
                pi.outputMax = piOutputMax;
            entry.pi = pi;
        }
        if (entry.sdk || entry.pi)
            out[base] = entry;
    }
    return out;
}
/**
 * **门2 消费端 backstop clamp**（单一 clamp 源，红线 #9）—— 把一个 effort 值钳进允许集。
 *  - `allowed` absent/空 → 不约束，原样返回 `value`（**零回归**：无 allowedSubset 时逐字节保持现状）。
 *  - `value` ∈ allowed → 原样返回。
 *  - `value` 越界 → 回落 `fallback`（若 fallback ∈ allowed）；fallback 也越界/缺 → `undefined`
 *    （不注入，落网关/模型默认——不硬塞一个越界值）。
 *  - `value` undefined → `undefined`（无请求，不越权注入 fallback）。
 */
export function clampEffortToAllowed(value, allowed, fallback) {
    if (value === undefined)
        return undefined;
    if (!allowed || allowed.length === 0)
        return value;
    if (allowed.includes(value))
        return value;
    if (fallback !== undefined && allowed.includes(fallback))
        return fallback;
    return undefined;
}
/**
 * 把选中的档收敛到模型支持集里：不高于 selected 的最高档；selected 低于集合
 * 最低档时取最低档。selected 已在集合里则原样返回。空集合 → undefined。
 * 不改写字面量（不做 max→ultra 这类平移）；调用方再用 clampEffortToAllowed。
 */
export function nearestSupportedEffort(selected, levels) {
    const rank = (level) => THINKING_LEVELS.indexOf(level);
    const ordered = levels.filter((level) => rank(level) !== -1);
    if (ordered.length === 0)
        return undefined;
    const selectedRank = rank(selected);
    const notAbove = ordered.filter((level) => rank(level) <= selectedRank);
    const pool = notAbove.length > 0 ? notAbove : ordered;
    return pool.reduce((best, level) => {
        if (notAbove.length === 0)
            return rank(level) < rank(best) ? level : best;
        return rank(level) > rank(best) ? level : best;
    });
}
/** 门2 thinking clamp（同 `clampEffortToAllowed` 语义，ThinkingMode 域）。泛型保留调用方的窄类型
 *  （如 UI 的 "adaptive"|"enabled"|"disabled"，不被 allowed 的宽 ThinkingMode 冲淡出 "auto"）。 */
export function clampThinkingToAllowed(value, allowed, fallback) {
    if (value === undefined)
        return undefined;
    if (!allowed || allowed.length === 0)
        return value;
    if (allowed.includes(value))
        return value;
    if (fallback !== undefined && allowed.includes(fallback))
        return fallback;
    return undefined;
}
/** 门2 数值上界 clamp（Pi outputTokens）：`max` absent → 不约束（原样）；否则 `min(value, max)`。
 *  `value` undefined → undefined。 */
export function clampToMax(value, max) {
    if (value === undefined)
        return undefined;
    if (max === undefined)
        return value;
    return Math.min(value, max);
}
/**
 * 企业模型治理统一面 **P1 materialize 单点映射纯函数**（红线 #9，2026-07-22）—— 把
 * `resolveModelParams` 产出的有效参数（`PlatformModelDefaultParams`）**融进员工 bundle model
 * 的 `capabilities.defaults`**（`WireModelDefaults` raw 网关形），字段级保留既有值。
 *
 * **写点是 raw wire 形（`WireModelDefaults`：effort/thinking 皆 string 串），非 `ModelParamDefaults`
 * 对象形**（消费链取证结论，方案 §2.5 已改正）：bundle model 的 `capabilities.defaults` 故意保
 * raw、单一 parse 点在 desktop `parseModelDefaults`（@dosia/core，enterprise-bundle-runtime.ts:187
 * 对每员工 catalog 归一）。`PlatformModelDefaultParams.thinking`（"auto"|"adaptive"|"enabled"|
 * "disabled"）与 `reasoningEffort`（"low"|"high"|"max"）本就是 `parseModelDefaults` 期望的 wire
 * 词表，故此映射近乎恒等——无需 `normalizeThinkingDisplay`（adaptive 的 display:"summarized" 归一
 * 在下游 SDK sink `buildQueryOptions` 做，见 types normalizeThinkingDisplay 注释）。
 *
 * **字段级**：仅 `effort`/`thinking` 两字段被 eff 覆盖（其余 wire 字段——`enable_tool_search` /
 * `source` / 网关既有 effort·thinking——原样保留）。`maxTokens`/`temperature` 在 `WireModelDefaults`
 * **无槽位**、且不在 P1 范围（Claude 主脑 SDK 路径无注入落点，方案 §2.5），直接丢弃。
 *
 * `eff` 为 undefined 或只带 maxTokens/temperature（无 brain 可映射字段）→ **原样返回 existing**
 * （不注入空 defaults，保留网关默认作继承链底座）。纯函数、不改入参。
 */
export function materializeModelParamsIntoDefaults(existing, eff) {
    if (!eff)
        return existing;
    const effort = eff.reasoningEffort;
    const thinking = eff.thinking;
    // 只带 maxTokens/temperature（无 brain 可映射字段）→ 不动 defaults（保留网关默认）。
    if (effort === undefined && thinking === undefined)
        return existing;
    const out = { ...(existing ?? {}) };
    if (effort !== undefined)
        out.effort = effort;
    if (thinking !== undefined)
        out.thinking = thinking;
    return out;
}
export function applyModelSelection(entries, selection, opts) {
    const brainModels = selection?.brainModels;
    const toolModels = selection?.toolModels;
    const hasBrain = !!brainModels && brainModels.length > 0;
    const hasTool = !!toolModels && toolModels.length > 0;
    // 两线均缺省 → 原样（未配置 = 继承上层全量，footgun 防护同 applyBrainCuration）。
    if (!hasBrain && !hasTool)
        return entries.slice();
    const brainSet = hasBrain ? new Set(brainModels.map(stripModelSuffixTag)) : undefined;
    const toolSet = hasTool ? new Set(toolModels.map(stripModelSuffixTag)) : undefined;
    // 主脑候选基集 = 原生 Anthropic 腿（非工具组、非生图）的 base id，与 applyBrainCuration 同一判据。
    const candidateBases = new Set([
        ...entries
            .filter(isBrainPoolEntry)
            .map((e) => stripModelSuffixTag(e.modelId)),
        ...(opts?.brainLineBases ?? []),
    ]);
    return entries.filter((e) => {
        // 网关托管能力腿的伪模型行（tavily_search / openai_audio_transcriptions，REQ-268）
        // **恒放行**：白名单圈的是「模型」，而这两行是能力通道，它们的 id 不是任何超管会去圈
        // 的模型。被裁掉的表现是最难查的那种——超管一配 toolModels，员工端 web_search /
        // audio_transcribe 就永远不注册，零报错（接缝 R）。
        if (isGatewayCapabilityProtocol(e.protocol))
            return true;
        const base = stripModelSuffixTag(e.modelId);
        if (candidateBases.has(base)) {
            // 主脑候选：brainModels 缺省 → 放行；否则整模型跨协议按 base 判定。
            return brainSet ? brainSet.has(base) : true;
        }
        // 其余行（纯工具组 + 纯 openai）：toolModels 缺省 → 放行；否则按 base 判定。
        return toolSet ? toolSet.has(base) : true;
    });
}
/**
 * 把 catalog 按「完整组成员」展开成 Map<groupName, { id, models }>：一个模型出现在它
 * `availableGroups` 里的每一个组下（按 modelId 去重）。
 *
 * 为何必须按 availableGroups 而非 chosenGroup 分组：catalog 把同一模型去重到单条
 * （chosenGroup = lowest group_id），同一模型常被多个组提供（如 claude-opus-4-7 同时在
 * claude-default[id 2] 和 claude-lite[id 21]）。若按 chosenGroup 分组，高 id 组只会
 * 拿到「自己独有」的模型 → claude-lite 只剩 claude-opus-4-8 一个。按 availableGroups
 * 展开后每个组都显示自己在 ByteSpike 后台的完整模型清单。
 *
 * 泛型保留入参元素类型，上层（管理面 / 员工面）拿回原始 entry 继续渲染。
 */
export function expandCatalogToGroups(entries) {
    const byGroup = new Map();
    for (const e of entries) {
        const memberOf = e.availableGroups?.length ? e.availableGroups : (e.chosenGroup ? [e.chosenGroup] : []);
        for (const grp of memberOf) {
            let g = byGroup.get(grp.name);
            if (!g) {
                g = { id: grp.id, models: [] };
                byGroup.set(grp.name, g);
            }
            if (!g.models.some((m) => m.modelId === e.modelId))
                g.models.push(e);
        }
    }
    return byGroup;
}
/**
 * Decide whether a PERSONAL-mode client should (re)hydrate `model_catalog_v1`
 * from the LIVE ByteSpike public manifest at startup.
 *
 * Why personal-only: enterprise clients get their catalog from the org bundle
 * push (the curated, *entitled* subset). The public manifest is the full
 * platform superset and would re-introduce groups the org isn't entitled to, so
 * we never auto-overwrite an enterprise catalog from here.
 *
 * Returns true when, in personal mode:
 *   - the local catalog is empty (never synced — first launch), OR
 *   - it was last hydrated longer than `ttlMs` ago (keeps the personal menu
 *     current without a manual "同步" click; the cache is live data, not a
 *     release-frozen constant). A null `hydratedAtMs` with a non-empty cache is
 *     treated as stale (legacy install whose cache predates this timestamp).
 *
 * When the fetch fails the cache is left as-is (N-95: there is no packaged
 * fallback list any more — an empty cache stays empty, the picker shows the
 * fetch state, and a later launch retries the live fetch).
 */
export function shouldHydratePersonalCatalog(input) {
    if (input.deployMode !== "personal")
        return false;
    if (input.storedCount === 0)
        return true;
    if (input.hydratedAtMs == null)
        return true;
    return input.nowMs - input.hydratedAtMs >= input.ttlMs;
}
/** Personal-mode catalog refresh cadence. The public manifest is a single
 *  small GET, so an hour-long TTL lets a same-day gateway release (new model
 *  added to the manifest) reach personal users without a manual "同步" click
 *  or an app restart — a long-running app never re-hydrates otherwise.
 *  Single source of truth: consumed by both the boot-time hydration call and
 *  the long-running-app periodic leg (apps/desktop/src/main/ipc-bridge.ts). */
export const CATALOG_HYDRATE_TTL_MS = 60 * 60 * 1000;
/**
 * Provider points at a real Anthropic Claude gateway, so it's safe to send
 * [1m] suffix when paired with a Claude-family model id.
 *
 * Enumeration is the Anthropic SDK-boundary subset of `ModelProvider`
 * (`claude-local` / `claude-custom`). Personal pi wallets (`chatgpt-oauth` /
 * `xai-oauth`) never reach the SDK boundary. The `claude-custom` gateway is
 * dual-use (ByteSpike Claude + DeepSeek/Kimi native through the same endpoint)
 * — the model-id check via isClaudeFamilyModel handles that case
 * (claude-custom + deepseek-v4-pro → no [1m]).
 */
export function isAnthropicClaudeProvider(provider) {
    return provider === "claude-local"
        || provider === "claude-custom";
}
/**
 * Resolve the 1M-capability FACT for a model id. `model` may carry a `[1m]`
 * suffix or be a bare alias — it is normalized (suffix stripped) before the
 * registry lookup. Returns true iff the model supports the 1M window.
 */
export function supports1m(model, sources) {
    if (sources?.adminOverride !== undefined)
        return sources.adminOverride;
    if (sources?.manifest1m !== undefined)
        return sources.manifest1m;
    if (sources?.sdkHas1m)
        return true;
    const bare = (model ?? "").replace(/\[[^\]]*\]/g, "");
    const reg = DOSIA_MODEL_REGISTRY[bare]?.supports1m;
    return reg === true;
}
// ──────────────────────────────────────────────────────────────────────
// Routing decision: should the send path use resolved GATEWAY creds?
// ──────────────────────────────────────────────────────────────────────
/**
 * Decide whether the send path should use the resolved GATEWAY credentials
 * (claude-custom) vs fall back to claude-local OAuth. Single source of truth for
 * agent-bridge (interactive) + resolveBackgroundSessionCredentials (cron/loop).
 *
 * resolveModelCredentials only ever returns non-oauth GATEWAY creds, and
 * claude-local can only serve Claude-family models. So an OAuth-brain user whose
 * explicitly-selected CONCRETE model is NOT Claude-family (gpt/deepseek/gemini/…)
 * must use the gateway creds — forcing claude-local guarantees "Not logged in".
 * Claude-family models on an OAuth brain still prefer claude-local (free personal
 * subscription) even if they also live in a gateway catalog — preserves billing intent.
 *
 * **走 pi 腿的个人订阅脑（`chatgpt-oauth` / `xai-oauth`）恒 false，且没有 model-override 例外**
 * （2026-07-31 ChatGPT；2026-08-22 xAI 同理）：该脑的模型在企业网关 catalog 里**很可能也有
 * 同名行**（gpt-5.4 … / grok-4.6）—— 员工点的是「我自己的会员」，若这里放行网关凭证，
 * 公司就被按 token 计费，而员工与管理员都看不到任何异常。这正是枚举替换布尔的直接原因：
 * 布尔版本里非 Claude 个人订阅落 `!isOAuthBrain → true`。
 */
export function shouldUseResolvedGatewayCreds(args) {
    if (!args.hasResolvedCreds)
        return false;
    // 走 pi 腿的个人订阅钱包**永不**借道网关凭证：它自带凭证（pi-ai 腿），且同名模型常在网关
    // catalog 里有行，放行 = 静默改计费渠道。xai-oauth 同理。model-override 例外也不适用
    // （覆盖的是「这个模型走哪个网关端点」，不是「这次该花谁的钱」）。
    if (isPersonalPiWallet(args.brainWallet))
        return false;
    if (args.brainWallet === "gateway")
        return true;
    if (args.sourceLabel === "model-override")
        return true;
    return !isClaudeFamilyModel(args.effectiveModel);
}
//# sourceMappingURL=model-registry.js.map